/*!
 * EventCalendar v4.4.1
 * https://github.com/vkurko/calendar
 */
import { tick, getContext, untrack, setContext, onMount, mount, unmount } from "svelte";
import * as $ from "svelte/internal/client";
import "svelte/internal/disclose-version";
import { derived, writable, readable, get } from "svelte/store";
import "svelte/internal/flags/legacy";
import { SvelteSet } from "svelte/reactivity";
function keyEnter(fn) {
  return function(e) {
    return e.key === "Enter" || e.key === " " && !e.preventDefault() ? fn.call(this, e) : void 0;
  };
}
function setContent(node, content) {
  let actions = {
    update(content2) {
      if (typeof content2 == "string") {
        node.innerText = content2;
      } else if (content2 == null ? void 0 : content2.domNodes) {
        node.replaceChildren(...content2.domNodes);
      } else if (content2 == null ? void 0 : content2.html) {
        node.innerHTML = content2.html;
      }
    }
  };
  actions.update(content);
  return actions;
}
function outsideEvent(node, type) {
  const handlePointerDown = (jsEvent) => {
    if (node && !node.contains(jsEvent.target)) {
      node.dispatchEvent(
        new CustomEvent(type + "outside", { detail: { jsEvent } })
      );
    }
  };
  document.addEventListener(type, handlePointerDown, true);
  return {
    destroy() {
      document.removeEventListener(type, handlePointerDown, true);
    }
  };
}
function observeResize(node, callback) {
  let resizeObserver = new ResizeObserver((entries2) => {
    for (let entry of entries2) {
      callback(entry);
    }
  });
  resizeObserver.observe(node);
  return {
    destroy() {
      resizeObserver.unobserve(node);
    }
  };
}
const DAY_IN_SECONDS = 86400;
function createDate(input = void 0) {
  if (input !== void 0) {
    return input instanceof Date ? _fromLocalDate(input) : _fromISOString(input);
  }
  return _fromLocalDate(/* @__PURE__ */ new Date());
}
function createDuration(input) {
  if (typeof input === "number") {
    input = { seconds: input };
  } else if (typeof input === "string") {
    let seconds = 0, exp = 2;
    for (let part of input.split(":", 3)) {
      seconds += parseInt(part, 10) * Math.pow(60, exp--);
    }
    input = { seconds };
  } else if (input instanceof Date) {
    input = { hours: input.getUTCHours(), minutes: input.getUTCMinutes(), seconds: input.getUTCSeconds() };
  }
  let weeks = input.weeks || input.week || 0;
  return {
    years: input.years || input.year || 0,
    months: input.months || input.month || 0,
    days: weeks * 7 + (input.days || input.day || 0),
    seconds: (input.hours || input.hour || 0) * 60 * 60 + (input.minutes || input.minute || 0) * 60 + (input.seconds || input.second || 0),
    inWeeks: !!weeks
  };
}
function cloneDate(date) {
  return new Date(date.getTime());
}
function addDuration(date, duration, x = 1) {
  date.setUTCFullYear(date.getUTCFullYear() + x * duration.years);
  let month = date.getUTCMonth() + x * duration.months;
  date.setUTCMonth(month);
  month %= 12;
  if (month < 0) {
    month += 12;
  }
  while (date.getUTCMonth() !== month) {
    subtractDay(date);
  }
  date.setUTCDate(date.getUTCDate() + x * duration.days);
  date.setUTCSeconds(date.getUTCSeconds() + x * duration.seconds);
  return date;
}
function subtractDuration(date, duration, x = 1) {
  return addDuration(date, duration, -x);
}
function addDay(date, x = 1) {
  date.setUTCDate(date.getUTCDate() + x);
  return date;
}
function subtractDay(date, x = 1) {
  return addDay(date, -x);
}
function setMidnight(date) {
  date.setUTCHours(0, 0, 0, 0);
  return date;
}
function toLocalDate(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );
}
function toISOString(date, len = 19) {
  return date.toISOString().substring(0, len);
}
function datesEqual(date1, ...dates2) {
  return dates2.every((date2) => date1.getTime() === date2.getTime());
}
function nextClosestDay(date, day) {
  let diff2 = day - date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + (diff2 >= 0 ? diff2 : diff2 + 7));
  return date;
}
function prevClosestDay(date, day) {
  let diff2 = day - date.getUTCDay();
  date.setUTCDate(date.getUTCDate() + (diff2 <= 0 ? diff2 : diff2 - 7));
  return date;
}
function noTimePart(date) {
  return typeof date === "string" && date.length <= 10;
}
function copyTime(toDate, fromDate) {
  toDate.setUTCHours(fromDate.getUTCHours(), fromDate.getUTCMinutes(), fromDate.getUTCSeconds(), 0);
  return toDate;
}
function toSeconds(duration) {
  return duration.seconds;
}
function nextDate(date, duration) {
  addDuration(date, duration);
  return date;
}
function prevDate(date, duration, hiddenDays) {
  subtractDuration(date, duration);
  if (hiddenDays.length && hiddenDays.length < 7) {
    while (hiddenDays.includes(date.getUTCDay())) {
      subtractDay(date);
    }
  }
  return date;
}
function getWeekNumber(date, firstDay) {
  date = cloneDate(date);
  if (firstDay == 0) {
    date.setUTCDate(date.getUTCDate() + 6 - date.getUTCDay());
  } else {
    date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  }
  let yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date - yearStart) / 1e3 / DAY_IN_SECONDS + 1) / 7);
}
function _fromLocalDate(date) {
  return new Date(Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  ));
}
function _fromISOString(str) {
  const parts = str.match(/\d+/g);
  return new Date(Date.UTC(
    Number(parts[0]),
    Number(parts[1]) - 1,
    Number(parts[2]),
    Number(parts[3] || 0),
    Number(parts[4] || 0),
    Number(parts[5] || 0)
  ));
}
function assign(...args) {
  return Object.assign(...args);
}
function keys(object) {
  return Object.keys(object);
}
function entries(object) {
  return Object.entries(object);
}
function floor(value) {
  return Math.floor(value);
}
function ceil(value) {
  return Math.ceil(value);
}
function min(...args) {
  return Math.min(...args);
}
function max(...args) {
  return Math.max(...args);
}
function symbol() {
  return Symbol("ec");
}
function isArray(value) {
  return Array.isArray(value);
}
function isFunction(value) {
  return typeof value === "function";
}
function run(fn) {
  return fn();
}
function runAll(fns) {
  fns.forEach(run);
}
function noop() {
}
const identity = (x) => x;
function stopPropagation(fn) {
  return function(event) {
    event.stopPropagation();
    if (fn) {
      fn.call(this, event);
    }
  };
}
function debounce(fn, handle, queueStore) {
  queueStore.update((queue) => queue.set(handle, fn));
}
function flushDebounce(queue) {
  runAll(queue);
  queue.clear();
}
function task(fn, handle, tasks) {
  handle ?? (handle = fn);
  if (!tasks.has(handle)) {
    tasks.set(handle, setTimeout(() => {
      tasks.delete(handle);
      fn();
    }));
  }
}
let payloadProp = symbol();
function setPayload(obj, payload) {
  obj[payloadProp] = payload;
}
function hasPayload(obj) {
  return !!(obj == null ? void 0 : obj[payloadProp]);
}
function getPayload(obj) {
  return obj[payloadProp];
}
function createElement(tag, className, content, attrs = []) {
  let el = document.createElement(tag);
  el.className = className;
  if (typeof content == "string") {
    el.innerText = content;
  } else if (content.domNodes) {
    el.replaceChildren(...content.domNodes);
  } else if (content.html) {
    el.innerHTML = content.html;
  }
  for (let attr of attrs) {
    el.setAttribute(...attr);
  }
  return el;
}
function hasYScroll(el) {
  return el.scrollHeight > el.clientHeight;
}
function rect(el) {
  return el.getBoundingClientRect();
}
function ancestor(el, up) {
  while (up--) {
    el = el.parentElement;
  }
  return el;
}
function height(el) {
  return rect(el).height;
}
function getElementWithPayload(x, y, root2 = document, processed = []) {
  processed.push(root2);
  for (let el of root2.elementsFromPoint(x, y)) {
    if (hasPayload(el)) {
      return el;
    }
    if (el.shadowRoot && !processed.includes(el.shadowRoot)) {
      let shadowEl = getElementWithPayload(x, y, el.shadowRoot, processed);
      if (shadowEl) {
        return shadowEl;
      }
    }
  }
  return null;
}
function listen(node, event, handler, options) {
  node.addEventListener(event, handler, options);
  return () => node.removeEventListener(event, handler, options);
}
function createView(view2, _viewTitle, _currentRange, _activeRange) {
  return {
    type: view2,
    title: _viewTitle,
    currentStart: _currentRange.start,
    currentEnd: _currentRange.end,
    activeStart: _activeRange.start,
    activeEnd: _activeRange.end,
    calendar: void 0
  };
}
function toViewWithLocalDates(view2) {
  view2 = assign({}, view2);
  view2.currentStart = toLocalDate(view2.currentStart);
  view2.currentEnd = toLocalDate(view2.currentEnd);
  view2.activeStart = toLocalDate(view2.activeStart);
  view2.activeEnd = toLocalDate(view2.activeEnd);
  return view2;
}
function listView(view2) {
  return view2.startsWith("list");
}
function timelineView(view2) {
  return view2.includes("Timeline");
}
let eventId = 1;
function createEvents(input) {
  return input.map((event) => {
    let result = {
      id: "id" in event ? String(event.id) : `{generated-${eventId++}}`,
      resourceIds: toArrayProp(event, "resourceId").map(String),
      allDay: event.allDay ?? (noTimePart(event.start) && noTimePart(event.end)),
      start: createDate(event.start),
      end: createDate(event.end),
      title: event.title ?? "",
      editable: event.editable,
      startEditable: event.startEditable,
      durationEditable: event.durationEditable,
      display: event.display ?? "auto",
      extendedProps: event.extendedProps ?? {},
      backgroundColor: event.backgroundColor ?? event.color,
      textColor: event.textColor,
      classNames: toArrayProp(event, "className"),
      styles: toArrayProp(event, "style")
    };
    if (result.allDay) {
      setMidnight(result.start);
      let end = cloneDate(result.end);
      setMidnight(result.end);
      if (!datesEqual(result.end, end) || datesEqual(result.end, result.start)) {
        addDay(result.end);
      }
    }
    return result;
  });
}
function toArrayProp(input, propName) {
  let result = input[propName + "s"] ?? input[propName] ?? [];
  return isArray(result) ? result : [result];
}
function createEventSources(input) {
  return input.map((source) => ({
    events: source.events,
    url: source.url && source.url.trimEnd("&") || "",
    method: source.method && source.method.toUpperCase() || "GET",
    extraParams: source.extraParams || {}
  }));
}
function createEventChunk(event, start, end) {
  let chunk = {
    start: event.start > start ? event.start : start,
    end: event.end < end ? event.end : end,
    event
  };
  chunk.zeroDuration = datesEqual(chunk.start, chunk.end);
  return chunk;
}
function sortEventChunks(chunks) {
  chunks.sort((a, b) => a.start - b.start || b.event.allDay - a.event.allDay);
}
function createEventContent(chunk, displayEventEnd, eventContent, theme, _intlEventTime, _view) {
  let timeText = _intlEventTime.formatRange(
    chunk.start,
    displayEventEnd && chunk.event.display !== "pointer" && !chunk.zeroDuration ? copyTime(cloneDate(chunk.start), chunk.end) : chunk.start
  );
  let content;
  if (eventContent) {
    content = isFunction(eventContent) ? eventContent({
      event: toEventWithLocalDates(chunk.event),
      timeText,
      view: toViewWithLocalDates(_view)
    }) : eventContent;
  }
  if (content === void 0) {
    let domNodes;
    switch (chunk.event.display) {
      case "background":
        domNodes = [];
        break;
      case "pointer":
        domNodes = [createTimeElement(timeText, chunk, theme)];
        break;
      default:
        domNodes = [
          ...chunk.event.allDay ? [] : [createTimeElement(timeText, chunk, theme)],
          createElement("h4", theme.eventTitle, chunk.event.title)
        ];
    }
    content = { domNodes };
  }
  return [timeText, content];
}
function createTimeElement(timeText, chunk, theme) {
  return createElement(
    "time",
    theme.eventTime,
    timeText,
    [["datetime", toISOString(chunk.start)]]
  );
}
function createEventClasses(eventClassNames, event, _view) {
  let result = event.classNames;
  if (eventClassNames) {
    if (isFunction(eventClassNames)) {
      eventClassNames = eventClassNames({
        event: toEventWithLocalDates(event),
        view: toViewWithLocalDates(_view)
      });
    }
    result = [
      ...isArray(eventClassNames) ? eventClassNames : [eventClassNames],
      ...result
    ];
  }
  return result;
}
function toEventWithLocalDates(event) {
  return _cloneEvent(event, toLocalDate);
}
function cloneEvent(event) {
  return _cloneEvent(event, cloneDate);
}
function _cloneEvent(event, dateFn) {
  event = assign({}, event);
  event.start = dateFn(event.start);
  event.end = dateFn(event.end);
  return event;
}
function prepareEventChunks$1(chunks, hiddenDays) {
  let longChunks = {};
  if (chunks.length) {
    sortEventChunks(chunks);
    let prevChunk;
    for (let chunk of chunks) {
      let dates = [];
      let date = setMidnight(cloneDate(chunk.start));
      while (chunk.end > date) {
        if (!hiddenDays.includes(date.getUTCDay())) {
          dates.push(cloneDate(date));
          if (dates.length > 1) {
            let key = date.getTime();
            if (longChunks[key]) {
              longChunks[key].chunks.push(chunk);
            } else {
              longChunks[key] = {
                sorted: false,
                chunks: [chunk]
              };
            }
          }
        }
        addDay(date);
      }
      if (dates.length) {
        chunk.date = dates[0];
        chunk.days = dates.length;
        chunk.dates = dates;
        if (chunk.start < dates[0]) {
          chunk.start = dates[0];
        }
        let maxEnd = addDay(cloneDate(dates.at(-1)));
        if (chunk.end > maxEnd) {
          chunk.end = maxEnd;
        }
      } else {
        chunk.date = setMidnight(cloneDate(chunk.start));
        chunk.days = 1;
        chunk.dates = [chunk.date];
      }
      if (prevChunk && datesEqual(prevChunk.date, chunk.date)) {
        chunk.prev = prevChunk;
      }
      prevChunk = chunk;
    }
  }
  return longChunks;
}
function repositionEvent$1(chunk, longChunks, height2) {
  chunk.top = 0;
  if (chunk.prev) {
    chunk.top = chunk.prev.bottom + 1;
  }
  chunk.bottom = chunk.top + height2;
  let margin = 1;
  let key = chunk.date.getTime();
  if (longChunks[key]) {
    if (!longChunks[key].sorted) {
      longChunks[key].chunks.sort((a, b) => a.top - b.top);
      longChunks[key].sorted = true;
    }
    for (let longChunk of longChunks[key].chunks) {
      if (chunk.top < longChunk.bottom && chunk.bottom > longChunk.top) {
        let offset = longChunk.bottom - chunk.top + 1;
        margin += offset;
        chunk.top += offset;
        chunk.bottom += offset;
      }
    }
  }
  return margin;
}
function runReposition(refs, data) {
  var _a;
  refs.length = data.length;
  let result = [];
  for (let ref of refs) {
    result.push((_a = ref == null ? void 0 : ref.reposition) == null ? void 0 : _a.call(ref));
  }
  return result;
}
function eventIntersects(event, start, end, resources) {
  if (event.start < end && event.end > start) {
    if (resources) {
      if (!isArray(resources)) {
        resources = [resources];
      }
      return resources.some((resource) => event.resourceIds.includes(resource.id));
    }
    return true;
  }
  return false;
}
function helperEvent(display) {
  return previewEvent(display) || ghostEvent(display) || pointerEvent(display);
}
function bgEvent(display) {
  return display === "background";
}
function previewEvent(display) {
  return display === "preview";
}
function ghostEvent(display) {
  return display === "ghost";
}
function pointerEvent(display) {
  return display === "pointer";
}
function btnTextDay(text) {
  return btnText(text, "day");
}
function btnTextWeek(text) {
  return btnText(text, "week");
}
function btnTextMonth(text) {
  return btnText(text, "month");
}
function btnTextYear(text) {
  return btnText(text, "year");
}
function btnText(text, period) {
  return {
    ...text,
    next: "Next " + period,
    prev: "Previous " + period
  };
}
function themeView(view2) {
  return (theme) => ({ ...theme, view: view2 });
}
function createDateRange(input) {
  let start, end;
  if (input) {
    ({ start, end } = input);
    if (start) {
      start = setMidnight(createDate(start));
    }
    if (end) {
      end = setMidnight(createDate(end));
    }
  }
  return { start, end };
}
function outsideRange(date, range) {
  return range.start && date < range.start || range.end && date > range.end;
}
function limitToRange(date, range) {
  if (range.start && date < range.start) {
    date = range.start;
  }
  if (range.end && date > range.end) {
    date = range.end;
  }
  return date;
}
function createResources(input) {
  let result = [];
  _createResources(input, 0, result);
  return result;
}
function _createResources(input, level, flat) {
  let result = [];
  for (let item of input) {
    let resource = createResource(item);
    result.push(resource);
    flat.push(resource);
    let payload = {
      level,
      children: [],
      expanded: true,
      hidden: false
    };
    setPayload(resource, payload);
    if (item.children) {
      payload.children = _createResources(item.children, level + 1, flat);
    }
  }
  return result;
}
function createResource(input) {
  return {
    id: String(input.id),
    title: input.title || "",
    eventBackgroundColor: input.eventBackgroundColor,
    eventTextColor: input.eventTextColor,
    extendedProps: input.extendedProps ?? {}
  };
}
function resourceBackgroundColor(event, resources) {
  var _a;
  return (_a = findResource(event, resources)) == null ? void 0 : _a.eventBackgroundColor;
}
function resourceTextColor(event, resources) {
  var _a;
  return (_a = findResource(event, resources)) == null ? void 0 : _a.eventTextColor;
}
function findResource(event, resources) {
  return resources.find((resource) => event.resourceIds.includes(resource.id));
}
function intl(locale, format) {
  return derived([locale, format], ([$locale, $format]) => {
    let intl2 = isFunction($format) ? { format: $format } : new Intl.DateTimeFormat($locale, $format);
    return {
      format: (date) => intl2.format(toLocalDate(date))
    };
  });
}
function intlRange(locale, format) {
  return derived([locale, format], ([$locale, $format]) => {
    let formatRange;
    if (isFunction($format)) {
      formatRange = $format;
    } else {
      let intl2 = new Intl.DateTimeFormat($locale, $format);
      formatRange = (start, end) => {
        if (start <= end) {
          return intl2.formatRange(start, end);
        } else {
          let parts = intl2.formatRangeToParts(end, start);
          let result = "";
          let sources = ["startRange", "endRange"];
          let processed = [false, false];
          for (let part of parts) {
            let i = sources.indexOf(part.source);
            if (i >= 0) {
              if (!processed[i]) {
                result += _getParts(sources[1 - i], parts);
                processed[i] = true;
              }
            } else {
              result += part.value;
            }
          }
          return result;
        }
      };
    }
    return {
      formatRange: (start, end) => formatRange(toLocalDate(start), toLocalDate(end))
    };
  });
}
function _getParts(source, parts) {
  let result = "";
  for (let part of parts) {
    if (part.source == source) {
      result += part.value;
    }
  }
  return result;
}
function viewResources(state) {
  return derived(
    [state.resources, state.filterResourcesWithEvents, state._filteredEvents, state._activeRange],
    ([$resources, $filterResourcesWithEvents, $_filteredEvents, $_activeRange]) => {
      let result = $resources.filter((resource) => !getPayload(resource).hidden);
      if ($filterResourcesWithEvents) {
        result = $resources.filter((resource) => {
          for (let event of $_filteredEvents) {
            if (event.display !== "background" && event.resourceIds.includes(resource.id) && event.start < $_activeRange.end && event.end > $_activeRange.start) {
              return true;
            }
          }
          return false;
        });
      }
      if (!result.length) {
        result = createResources([{}]);
      }
      return result;
    }
  );
}
function createTimes(date, $slotDuration, $slotLabelInterval, $_slotTimeLimits, $_intlSlotLabel) {
  date = cloneDate(date);
  let times2 = [];
  let end = cloneDate(date);
  addDuration(date, $_slotTimeLimits.min);
  addDuration(end, $_slotTimeLimits.max);
  if ($slotLabelInterval === void 0) {
    $slotLabelInterval = $slotDuration.seconds < 3600 ? createDuration($slotDuration.seconds * 2) : $slotDuration;
  }
  let label = cloneDate(date);
  while (date < end) {
    times2.push([
      toISOString(date),
      $_intlSlotLabel.format(date),
      date >= label
    ]);
    while ($slotLabelInterval.seconds && date >= label) {
      addDuration(label, $slotLabelInterval);
    }
    addDuration(date, $slotDuration);
  }
  return times2;
}
function createSlotTimeLimits($slotMinTime, $slotMaxTime, $flexibleSlotTimeLimits, $_viewDates, $_filteredEvents) {
  let min$1 = createDuration($slotMinTime);
  let max$1 = createDuration($slotMaxTime);
  if ($flexibleSlotTimeLimits) {
    let minMin = createDuration(min(toSeconds(min$1), max(0, toSeconds(max$1) - DAY_IN_SECONDS)));
    let maxMax = createDuration(max(toSeconds(max$1), toSeconds(minMin) + DAY_IN_SECONDS));
    let filter = isFunction($flexibleSlotTimeLimits == null ? void 0 : $flexibleSlotTimeLimits.eventFilter) ? $flexibleSlotTimeLimits.eventFilter : (event) => !bgEvent(event.display);
    loop: for (let date of $_viewDates) {
      let start = addDuration(cloneDate(date), min$1);
      let end = addDuration(cloneDate(date), max$1);
      let minStart = addDuration(cloneDate(date), minMin);
      let maxEnd = addDuration(cloneDate(date), maxMax);
      for (let event of $_filteredEvents) {
        if (!event.allDay && filter(event) && event.start < maxEnd && event.end > minStart) {
          if (event.start < start) {
            let seconds = max((event.start - date) / 1e3, toSeconds(minMin));
            if (seconds < toSeconds(min$1)) {
              min$1.seconds = seconds;
            }
          }
          if (event.end > end) {
            let seconds = min((event.end - date) / 1e3, toSeconds(maxMax));
            if (seconds > toSeconds(max$1)) {
              max$1.seconds = seconds;
            }
          }
          if (toSeconds(min$1) === toSeconds(minMin) && toSeconds(max$1) === toSeconds(maxMax)) {
            break loop;
          }
        }
      }
    }
  }
  return { min: min$1, max: max$1 };
}
function createOptions(plugins) {
  var _a;
  let options = {
    allDayContent: void 0,
    allDaySlot: true,
    buttonText: {
      today: "today"
    },
    customButtons: {},
    date: /* @__PURE__ */ new Date(),
    datesSet: void 0,
    dayHeaderFormat: {
      weekday: "short",
      month: "numeric",
      day: "numeric"
    },
    dayHeaderAriaLabelFormat: {
      dateStyle: "full"
    },
    displayEventEnd: true,
    duration: { weeks: 1 },
    events: [],
    eventAllUpdated: void 0,
    eventBackgroundColor: void 0,
    eventClassNames: void 0,
    eventClick: void 0,
    eventColor: void 0,
    eventContent: void 0,
    eventDidMount: void 0,
    eventFilter: void 0,
    // ec option
    eventMouseEnter: void 0,
    eventMouseLeave: void 0,
    eventSources: [],
    eventTextColor: void 0,
    eventTimeFormat: {
      hour: "numeric",
      minute: "2-digit"
    },
    filterEventsWithResources: false,
    filterResourcesWithEvents: false,
    firstDay: 0,
    flexibleSlotTimeLimits: false,
    // ec option
    headerToolbar: {
      start: "title",
      center: "",
      end: "today prev,next"
    },
    height: void 0,
    hiddenDays: [],
    highlightedDates: [],
    // ec option
    lazyFetching: true,
    loading: void 0,
    locale: void 0,
    nowIndicator: false,
    resourceLabelContent: void 0,
    resourceLabelDidMount: void 0,
    resources: [],
    selectable: false,
    scrollTime: "06:00:00",
    slotDuration: "00:30:00",
    slotEventOverlap: true,
    slotHeight: 24,
    // ec option
    slotLabelInterval: void 0,
    slotLabelFormat: {
      hour: "numeric",
      minute: "2-digit"
    },
    slotMaxTime: "24:00:00",
    slotMinTime: "00:00:00",
    slotWidth: 72,
    theme: {
      allDay: "ec-all-day",
      active: "ec-active",
      bgEvent: "ec-bg-event",
      bgEvents: "ec-bg-events",
      body: "ec-body",
      button: "ec-button",
      buttonGroup: "ec-button-group",
      calendar: "ec",
      content: "ec-content",
      day: "ec-day",
      dayHead: "ec-day-head",
      days: "ec-days",
      disabled: "ec-disabled",
      event: "ec-event",
      eventBody: "ec-event-body",
      eventTime: "ec-event-time",
      eventTitle: "ec-event-title",
      events: "ec-events",
      extra: "ec-extra",
      handle: "ec-handle",
      header: "ec-header",
      hiddenScroll: "ec-hidden-scroll",
      highlight: "ec-highlight",
      icon: "ec-icon",
      line: "ec-line",
      lines: "ec-lines",
      minor: "ec-minor",
      nowIndicator: "ec-now-indicator",
      otherMonth: "ec-other-month",
      resource: "ec-resource",
      sidebar: "ec-sidebar",
      sidebarTitle: "ec-sidebar-title",
      today: "ec-today",
      time: "ec-time",
      title: "ec-title",
      toolbar: "ec-toolbar",
      view: "",
      weekdays: ["ec-sun", "ec-mon", "ec-tue", "ec-wed", "ec-thu", "ec-fri", "ec-sat"],
      withScroll: "ec-with-scroll"
    },
    titleFormat: {
      year: "numeric",
      month: "short",
      day: "numeric"
    },
    validRange: void 0,
    view: void 0,
    viewDidMount: void 0,
    views: {}
  };
  for (let plugin of plugins) {
    (_a = plugin.createOptions) == null ? void 0 : _a.call(plugin, options);
  }
  return options;
}
function createParsers(plugins) {
  var _a;
  let parsers = {
    date: (date) => setMidnight(createDate(date)),
    duration: createDuration,
    events: createEvents,
    eventSources: createEventSources,
    hiddenDays: (days2) => [...new Set(days2)],
    highlightedDates: (dates) => dates.map((date) => setMidnight(createDate(date))),
    resources: createResources,
    scrollTime: createDuration,
    slotDuration: createDuration,
    slotLabelInterval: (input) => input !== void 0 ? createDuration(input) : void 0,
    slotMaxTime: createDuration,
    slotMinTime: createDuration,
    validRange: createDateRange
  };
  for (let plugin of plugins) {
    (_a = plugin.createParsers) == null ? void 0 : _a.call(plugin, parsers);
  }
  return parsers;
}
function diff(options, prevOptions) {
  let diff2 = [];
  for (let key of keys(options)) {
    if (options[key] !== prevOptions[key]) {
      diff2.push([key, options[key]]);
    }
  }
  return diff2;
}
function dayGrid(state) {
  return derived(state.view, ($view) => $view == null ? void 0 : $view.startsWith("dayGrid"));
}
function activeRange(state) {
  return derived(
    [state._currentRange, state.firstDay, state.slotMaxTime, state._dayGrid],
    ([$_currentRange, $firstDay, $slotMaxTime, $_dayGrid]) => {
      let start = cloneDate($_currentRange.start);
      let end = cloneDate($_currentRange.end);
      if ($_dayGrid) {
        prevClosestDay(start, $firstDay);
        nextClosestDay(end, $firstDay);
      } else if ($slotMaxTime.days || $slotMaxTime.seconds > DAY_IN_SECONDS) {
        addDuration(subtractDay(end), $slotMaxTime);
        let start2 = subtractDay(cloneDate(end));
        if (start2 < start) {
          start = start2;
        }
      }
      return { start, end };
    }
  );
}
function currentRange(state) {
  return derived(
    [state.date, state.duration, state.firstDay],
    ([$date, $duration, $firstDay]) => {
      let start = cloneDate($date), end;
      if ($duration.months) {
        start.setUTCDate(1);
      } else if ($duration.inWeeks) {
        prevClosestDay(start, $firstDay);
      }
      end = addDuration(cloneDate(start), $duration);
      return { start, end };
    }
  );
}
function viewDates(state) {
  return derived([state._activeRange, state.hiddenDays], ([$_activeRange, $hiddenDays]) => {
    let dates = [];
    let date = setMidnight(cloneDate($_activeRange.start));
    let end = setMidnight(cloneDate($_activeRange.end));
    while (date < end) {
      if (!$hiddenDays.includes(date.getUTCDay())) {
        dates.push(cloneDate(date));
      }
      addDay(date);
    }
    if (!dates.length && $hiddenDays.length && $hiddenDays.length < 7) {
      state.date.update((date2) => {
        while ($hiddenDays.includes(date2.getUTCDay())) {
          addDay(date2);
        }
        return date2;
      });
      dates = get(state._viewDates);
    }
    return dates;
  });
}
function viewTitle(state) {
  return derived(
    [state.date, state._activeRange, state._intlTitle, state._dayGrid],
    ([$date, $_activeRange, $_intlTitle, $_dayGrid]) => {
      return $_dayGrid ? $_intlTitle.formatRange($date, $date) : $_intlTitle.formatRange($_activeRange.start, subtractDay(cloneDate($_activeRange.end)));
    }
  );
}
function view(state) {
  return derived([state.view, state._viewTitle, state._currentRange, state._activeRange], (args) => createView(...args));
}
function events(state) {
  let _events = writable([]);
  let abortController;
  let fetching = 0;
  let debounceHandle = {};
  derived(
    [state.events, state.eventSources, state._activeRange, state._fetchedRange, state.lazyFetching, state.loading],
    (values, set) => debounce(() => {
      let [$events, $eventSources, $_activeRange, $_fetchedRange, $lazyFetching, $loading] = values;
      if (!$eventSources.length) {
        set($events);
        return;
      }
      if (!$_fetchedRange.start || $_fetchedRange.start > $_activeRange.start || $_fetchedRange.end < $_activeRange.end || !$lazyFetching) {
        if (abortController) {
          abortController.abort();
        }
        abortController = new AbortController();
        if (isFunction($loading) && !fetching) {
          $loading(true);
        }
        let stopLoading = () => {
          if (--fetching === 0 && isFunction($loading)) {
            $loading(false);
          }
        };
        let events2 = [];
        let failure = (e) => stopLoading();
        let success = (data) => {
          events2 = events2.concat(createEvents(data));
          set(events2);
          stopLoading();
        };
        let startStr = toISOString($_activeRange.start);
        let endStr = toISOString($_activeRange.end);
        for (let source of $eventSources) {
          if (isFunction(source.events)) {
            let result = source.events({
              start: toLocalDate($_activeRange.start),
              end: toLocalDate($_activeRange.end),
              startStr,
              endStr
            }, success, failure);
            if (result !== void 0) {
              Promise.resolve(result).then(success, failure);
            }
          } else {
            let params = isFunction(source.extraParams) ? source.extraParams() : assign({}, source.extraParams);
            params.start = startStr;
            params.end = endStr;
            params = new URLSearchParams(params);
            let url = source.url, headers = {}, body;
            if (["GET", "HEAD"].includes(source.method)) {
              url += (url.includes("?") ? "&" : "?") + params;
            } else {
              headers["content-type"] = "application/x-www-form-urlencoded;charset=UTF-8";
              body = String(params);
            }
            fetch(url, { method: source.method, headers, body, signal: abortController.signal, credentials: "same-origin" }).then((response) => response.json()).then(success).catch(failure);
          }
          ++fetching;
        }
        $_fetchedRange.start = $_activeRange.start;
        $_fetchedRange.end = $_activeRange.end;
      }
    }, debounceHandle, state._queue),
    []
  ).subscribe(_events.set);
  return _events;
}
function filteredEvents(state) {
  let view2;
  state._view.subscribe(($_view) => view2 = $_view);
  let debounceHandle = {};
  return derived(
    [state._events, state.eventFilter],
    (values, set) => debounce(() => {
      let [$_events, $eventFilter] = values;
      set(
        isFunction($eventFilter) ? $_events.filter((event, index2, events2) => $eventFilter({
          event,
          index: index2,
          events: events2,
          view: view2
        })) : $_events
      );
    }, debounceHandle, state._queue),
    []
  );
}
function now() {
  return readable(createDate(), (set) => {
    let interval = setInterval(() => {
      set(createDate());
    }, 1e3);
    return () => clearInterval(interval);
  });
}
function today(state) {
  return derived(state._now, ($_now) => setMidnight(cloneDate($_now)));
}
class State {
  constructor(plugins, input) {
    var _a, _b;
    plugins = plugins || [];
    let options = createOptions(plugins);
    let parsers = createParsers(plugins);
    options = parseOpts(options, parsers);
    input = parseOpts(input, parsers);
    for (let [option, value] of Object.entries(options)) {
      this[option] = writable(value);
    }
    this._queue = writable(/* @__PURE__ */ new Map());
    this._tasks = /* @__PURE__ */ new Map();
    this._auxiliary = writable([]);
    this._dayGrid = dayGrid(this);
    this._currentRange = currentRange(this);
    this._activeRange = activeRange(this);
    this._fetchedRange = writable({ start: void 0, end: void 0 });
    this._events = events(this);
    this._now = now();
    this._today = today(this);
    this._intlEventTime = intlRange(this.locale, this.eventTimeFormat);
    this._intlSlotLabel = intl(this.locale, this.slotLabelFormat);
    this._intlDayHeader = intl(this.locale, this.dayHeaderFormat);
    this._intlDayHeaderAL = intl(this.locale, this.dayHeaderAriaLabelFormat);
    this._intlTitle = intlRange(this.locale, this.titleFormat);
    this._bodyEl = writable(void 0);
    this._scrollable = writable(false);
    this._recheckScrollable = writable(false);
    this._viewTitle = viewTitle(this);
    this._viewDates = viewDates(this);
    this._view = view(this);
    this._viewComponent = writable(void 0);
    this._filteredEvents = filteredEvents(this);
    this._interaction = writable({});
    this._iEvents = writable([null, null]);
    this._iClasses = writable(identity);
    this._iClass = writable(void 0);
    this._set = (key, value) => {
      if (validKey(key, this)) {
        if (parsers[key]) {
          value = parsers[key](value);
        }
        this[key].set(value);
      }
    };
    this._get = (key) => validKey(key, this) ? get(this[key]) : void 0;
    for (let plugin of plugins) {
      (_a = plugin.createStores) == null ? void 0 : _a.call(plugin, this);
    }
    if (input.view) {
      this.view.set(input.view);
    }
    let views = /* @__PURE__ */ new Set([...keys(options.views), ...keys(input.views ?? {})]);
    for (let view2 of views) {
      let defOpts = mergeOpts(options, options.views[view2] ?? {});
      let opts = mergeOpts(defOpts, input, ((_b = input.views) == null ? void 0 : _b[view2]) ?? {});
      let component = opts.component;
      filterOpts(opts, this);
      for (let key of keys(opts)) {
        let { set, _set = set, ...rest } = this[key];
        this[key] = {
          // Set value in all views
          set: ["buttonText", "theme"].includes(key) ? (value) => {
            if (isFunction(value)) {
              let result = value(defOpts[key]);
              opts[key] = result;
              set(set === _set ? result : value);
            } else {
              opts[key] = value;
              set(value);
            }
          } : (value) => {
            opts[key] = value;
            set(value);
          },
          _set,
          ...rest
        };
      }
      this.view.subscribe((newView) => {
        if (newView === view2) {
          this._viewComponent.set(component);
          if (isFunction(opts.viewDidMount)) {
            tick().then(() => opts.viewDidMount(get(this._view)));
          }
          for (let key of keys(opts)) {
            this[key]._set(opts[key]);
          }
        }
      });
    }
  }
}
function parseOpts(opts, parsers) {
  let result = { ...opts };
  for (let key of keys(parsers)) {
    if (key in result) {
      result[key] = parsers[key](result[key]);
    }
  }
  if (opts.views) {
    result.views = {};
    for (let view2 of keys(opts.views)) {
      result.views[view2] = parseOpts(opts.views[view2], parsers);
    }
  }
  return result;
}
function mergeOpts(...args) {
  let result = {};
  for (let opts of args) {
    let override = {};
    for (let key of ["buttonText", "theme"]) {
      if (isFunction(opts[key])) {
        override[key] = opts[key](result[key]);
      }
    }
    result = {
      ...result,
      ...opts,
      ...override
    };
  }
  return result;
}
function filterOpts(opts, state) {
  keys(opts).filter((key) => !validKey(key, state) || key === "view").forEach((key) => delete opts[key]);
}
function validKey(key, state) {
  return state.hasOwnProperty(key) && key[0] !== "_";
}
var root_2$7 = $.from_html(`<h2></h2>`);
var root_4$3 = $.from_html(`<button><i></i></button>`);
var root_6$1 = $.from_html(`<button><i></i></button>`);
var root_8$1 = $.from_html(`<button> </button>`);
var root_10$1 = $.from_html(`<button></button>`);
var root_12$1 = $.from_html(`<button> </button>`);
function Buttons($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $date = () => $.store_get(date, "$date", $$stores);
  const $duration = () => $.store_get(duration, "$duration", $$stores);
  const $hiddenDays = () => $.store_get(hiddenDays, "$hiddenDays", $$stores);
  const $_currentRange = () => $.store_get(_currentRange, "$_currentRange", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_viewTitle = () => $.store_get(_viewTitle, "$_viewTitle", $$stores);
  const $buttonText = () => $.store_get(buttonText, "$buttonText", $$stores);
  const $customButtons = () => $.store_get(customButtons, "$customButtons", $$stores);
  const $view = () => $.store_get(view2, "$view", $$stores);
  let buttons = $.prop($$props, "buttons", 8);
  let {
    _currentRange,
    _viewTitle,
    _viewDates,
    buttonText,
    customButtons,
    date,
    duration,
    hiddenDays,
    theme,
    validRange,
    view: view2
  } = getContext("state");
  let today2 = setMidnight(createDate());
  let prevDisabled = $.mutable_source(), nextDisabled = $.mutable_source(), todayDisabled = $.mutable_source();
  let running = $.mutable_source(false);
  function isRunning() {
    return $.get(running);
  }
  function test() {
    return $_viewDates().every((date2) => outsideRange(date2, $validRange()));
  }
  function prev() {
    $.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
  }
  function next() {
    $.store_set(date, nextDate($date(), $duration()));
  }
  $.legacy_pre_effect(
    () => ($validRange(), $date(), $duration(), $hiddenDays(), $.get(todayDisabled), $_currentRange(), tick),
    () => {
      if (!isRunning()) {
        $.set(running, true);
        $.set(prevDisabled, false);
        $.set(nextDisabled, false);
        if ($validRange().start) {
          let currentDate = cloneDate($date());
          $.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
          $.set(prevDisabled, test());
          $.store_set(date, currentDate);
        }
        if ($validRange().end) {
          let currentDate = cloneDate($date());
          $.store_set(date, nextDate($date(), $duration()));
          $.set(nextDisabled, test());
          $.store_set(date, currentDate);
        }
        $.set(todayDisabled, today2 >= $_currentRange().start && today2 < $_currentRange().end);
        if (!$.get(todayDisabled) && ($validRange().start || $validRange().end)) {
          let currentDate = cloneDate($date());
          $.store_set(date, cloneDate(today2));
          $.set(todayDisabled, test());
          $.store_set(date, currentDate);
        }
        tick().then(() => $.set(running, false));
      }
    }
  );
  $.legacy_pre_effect_reset();
  $.init();
  var fragment = $.comment();
  var node = $.first_child(fragment);
  $.each(node, 1, buttons, $.index, ($$anchor2, button) => {
    var fragment_1 = $.comment();
    var node_1 = $.first_child(fragment_1);
    {
      var consequent = ($$anchor3) => {
        var h2 = root_2$7();
        $.action(h2, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), $_viewTitle);
        $.template_effect(() => $.set_class(h2, 1, $theme().title));
        $.append($$anchor3, h2);
      };
      var alternate = ($$anchor3, $$elseif) => {
        {
          var consequent_1 = ($$anchor4) => {
            var button_1 = root_4$3();
            var i = $.child(button_1);
            $.reset(button_1);
            $.template_effect(() => {
              $.set_class(button_1, 1, `${$theme().button ?? ""} ec-${$.get(button) ?? ""}`);
              $.set_attribute(button_1, "aria-label", $buttonText().prev);
              $.set_attribute(button_1, "title", $buttonText().prev);
              button_1.disabled = $.get(prevDisabled);
              $.set_class(i, 1, `${$theme().icon ?? ""} ec-${$.get(button) ?? ""}`);
            });
            $.event("click", button_1, prev);
            $.append($$anchor4, button_1);
          };
          var alternate_1 = ($$anchor4, $$elseif2) => {
            {
              var consequent_2 = ($$anchor5) => {
                var button_2 = root_6$1();
                var i_1 = $.child(button_2);
                $.reset(button_2);
                $.template_effect(() => {
                  $.set_class(button_2, 1, `${$theme().button ?? ""} ec-${$.get(button) ?? ""}`);
                  $.set_attribute(button_2, "aria-label", $buttonText().next);
                  $.set_attribute(button_2, "title", $buttonText().next);
                  button_2.disabled = $.get(nextDisabled);
                  $.set_class(i_1, 1, `${$theme().icon ?? ""} ec-${$.get(button) ?? ""}`);
                });
                $.event("click", button_2, next);
                $.append($$anchor5, button_2);
              };
              var alternate_2 = ($$anchor5, $$elseif3) => {
                {
                  var consequent_3 = ($$anchor6) => {
                    var button_3 = root_8$1();
                    var text = $.child(button_3, true);
                    $.reset(button_3);
                    $.template_effect(() => {
                      $.set_class(button_3, 1, `${$theme().button ?? ""} ec-${$.get(button) ?? ""}`);
                      button_3.disabled = $.get(todayDisabled);
                      $.set_text(text, $buttonText()[$.get(button)]);
                    });
                    $.event("click", button_3, () => $.store_set(date, cloneDate(today2)));
                    $.append($$anchor6, button_3);
                  };
                  var alternate_3 = ($$anchor6, $$elseif4) => {
                    {
                      var consequent_4 = ($$anchor7) => {
                        var button_4 = root_10$1();
                        $.effect(() => $.event("click", button_4, function(...$$args) {
                          var _a;
                          (_a = $customButtons()[$.get(button)].click) == null ? void 0 : _a.apply(this, $$args);
                        }));
                        $.action(button_4, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $customButtons()[$.get(button)].text);
                        $.template_effect(() => $.set_class(button_4, 1, `${$theme().button ?? ""} ec-${$.get(button) ?? ""}${$customButtons()[$.get(button)].active ? " " + $theme().active : ""}`));
                        $.append($$anchor7, button_4);
                      };
                      var alternate_4 = ($$anchor7, $$elseif5) => {
                        {
                          var consequent_5 = ($$anchor8) => {
                            var button_5 = root_12$1();
                            var text_1 = $.child(button_5, true);
                            $.reset(button_5);
                            $.template_effect(() => {
                              $.set_class(button_5, 1, `${$theme().button ?? ""}${$view() === $.get(button) ? " " + $theme().active : ""} ec-${$.get(button) ?? ""}`);
                              $.set_text(text_1, $buttonText()[$.get(button)]);
                            });
                            $.event("click", button_5, () => $.store_set(view2, $.get(button)));
                            $.append($$anchor8, button_5);
                          };
                          $.if(
                            $$anchor7,
                            ($$render) => {
                              if ($.get(button) != "") $$render(consequent_5);
                            },
                            $$elseif5
                          );
                        }
                      };
                      $.if(
                        $$anchor6,
                        ($$render) => {
                          if ($customButtons()[$.get(button)]) $$render(consequent_4);
                          else $$render(alternate_4, false);
                        },
                        $$elseif4
                      );
                    }
                  };
                  $.if(
                    $$anchor5,
                    ($$render) => {
                      if ($.get(button) == "today") $$render(consequent_3);
                      else $$render(alternate_3, false);
                    },
                    $$elseif3
                  );
                }
              };
              $.if(
                $$anchor4,
                ($$render) => {
                  if ($.get(button) == "next") $$render(consequent_2);
                  else $$render(alternate_2, false);
                },
                $$elseif2
              );
            }
          };
          $.if(
            $$anchor3,
            ($$render) => {
              if ($.get(button) == "prev") $$render(consequent_1);
              else $$render(alternate_1, false);
            },
            $$elseif
          );
        }
      };
      $.if(node_1, ($$render) => {
        if ($.get(button) == "title") $$render(consequent);
        else $$render(alternate, false);
      });
    }
    $.append($$anchor2, fragment_1);
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
var root_3$5 = $.from_html(`<div><!></div>`);
var root_1$d = $.from_html(`<div></div>`);
var root$r = $.from_html(`<nav></nav>`);
function Toolbar($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $headerToolbar = () => $.store_get(headerToolbar, "$headerToolbar", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let { headerToolbar, theme } = getContext("state");
  let sections = $.derived(() => {
    var _a;
    let sections2 = {};
    for (let key of ["start", "center", "end"]) {
      sections2[key] = ((_a = $headerToolbar()[key]) == null ? void 0 : _a.split(" ").map((group) => group.split(","))) ?? [];
    }
    return sections2;
  });
  var nav = root$r();
  $.each(nav, 21, () => keys($.get(sections)), $.index, ($$anchor2, key) => {
    var div = root_1$d();
    $.each(div, 21, () => $.get(sections)[$.get(key)], $.index, ($$anchor3, buttons) => {
      var fragment = $.comment();
      var node = $.first_child(fragment);
      {
        var consequent = ($$anchor4) => {
          var div_1 = root_3$5();
          var node_1 = $.child(div_1);
          Buttons(node_1, {
            get buttons() {
              return $.get(buttons);
            }
          });
          $.reset(div_1);
          $.template_effect(() => $.set_class(div_1, 1, $theme().buttonGroup));
          $.append($$anchor4, div_1);
        };
        var alternate = ($$anchor4) => {
          Buttons($$anchor4, {
            get buttons() {
              return $.get(buttons);
            }
          });
        };
        $.if(node, ($$render) => {
          if ($.get(buttons).length > 1) $$render(consequent);
          else $$render(alternate, false);
        });
      }
      $.append($$anchor3, fragment);
    });
    $.reset(div);
    $.template_effect(() => $.set_class(div, 1, `ec-${$.get(key) ?? ""}`));
    $.append($$anchor2, div);
  });
  $.reset(nav);
  $.template_effect(() => $.set_class(nav, 1, $theme().toolbar));
  $.append($$anchor, nav);
  $.pop();
  $$cleanup();
}
function Auxiliary$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_activeRange = () => $.store_get(_activeRange, "$_activeRange", $$stores);
  const $datesSet = () => $.store_get(datesSet, "$datesSet", $$stores);
  const $_view = () => $.store_get(_view, "$_view", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $eventAllUpdated = () => $.store_get(eventAllUpdated, "$eventAllUpdated", $$stores);
  const $_queue = () => $.store_get(_queue, "$_queue", $$stores);
  const $_recheckScrollable = () => $.store_get(_recheckScrollable, "$_recheckScrollable", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $_auxiliary = () => $.store_get(_auxiliary, "$_auxiliary", $$stores);
  let {
    datesSet,
    eventAllUpdated,
    _auxiliary,
    _activeRange,
    _filteredEvents,
    _scrollable,
    _bodyEl,
    _tasks,
    _recheckScrollable,
    _queue,
    _view
  } = getContext("state");
  $.user_effect(() => {
    $_activeRange();
    untrack(() => {
      if (isFunction($datesSet())) {
        $datesSet()({
          start: toLocalDate($_activeRange().start),
          end: toLocalDate($_activeRange().end),
          startStr: toISOString($_activeRange().start),
          endStr: toISOString($_activeRange().end),
          view: toViewWithLocalDates($_view())
        });
      }
    });
  });
  $.user_effect(() => {
    $_filteredEvents();
    untrack(() => {
      if (isFunction($eventAllUpdated())) {
        task(() => $eventAllUpdated()({ view: toViewWithLocalDates($_view()) }), "eau", _tasks);
      }
    });
  });
  $.user_effect(() => {
    $_queue();
    untrack(() => {
      flushDebounce($_queue());
    });
  });
  $.user_effect(() => {
    $_recheckScrollable();
    untrack(() => {
      if ($_bodyEl()) {
        $.store_set(_scrollable, hasYScroll($_bodyEl()));
      }
      $.store_set(_recheckScrollable, false);
    });
  });
  var fragment = $.comment();
  var node = $.first_child(fragment);
  $.each(node, 1, $_auxiliary, $.index, ($$anchor2, Component) => {
    var fragment_1 = $.comment();
    var node_1 = $.first_child(fragment_1);
    $.component(node_1, () => $.get(Component), ($$anchor3, $$component) => {
      $$component($$anchor3, {});
    });
    $.append($$anchor2, fragment_1);
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
var root$q = $.from_html(`<div><!> <!></div> <!>`, 1);
function Calendar($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_events = () => $.store_get(_events, "$_events", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $date = () => $.store_get(date, "$date", $$stores);
  const $duration = () => $.store_get(duration, "$duration", $$stores);
  const $hiddenDays = () => $.store_get(hiddenDays, "$hiddenDays", $$stores);
  const $_viewComponent = () => $.store_get(_viewComponent, "$_viewComponent", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_scrollable = () => $.store_get(_scrollable, "$_scrollable", $$stores);
  const $_iClass = () => $.store_get(_iClass, "$_iClass", $$stores);
  const $height = () => $.store_get(height2, "$height", $$stores);
  const $view = () => $.store_get(view2, "$view", $$stores);
  let plugins = $.prop($$props, "plugins", 19, () => []), options = $.prop($$props, "options", 19, () => ({}));
  let state = new State(plugins(), options());
  setContext("state", state);
  let {
    _viewComponent,
    _interaction,
    _iClass,
    _events,
    _scrollable,
    date,
    duration,
    hiddenDays,
    height: height2,
    theme,
    view: view2
  } = state;
  let prevOptions = { ...options() };
  $.user_effect(() => {
    for (let [name, value] of diff(options(), prevOptions)) {
      untrack(() => {
        setOption(name, value);
      });
    }
    assign(prevOptions, options());
  });
  function setOption(name, value) {
    state._set(name, value);
    return this;
  }
  function getOption(name) {
    let value = state._get(name);
    return value instanceof Date ? toLocalDate(value) : value;
  }
  function refetchEvents() {
    state._fetchedRange.set({ start: void 0, end: void 0 });
    return this;
  }
  function getEvents() {
    return $_events().map(toEventWithLocalDates);
  }
  function getEventById(id) {
    for (let event of $_events()) {
      if (event.id == id) {
        return toEventWithLocalDates(event);
      }
    }
    return null;
  }
  function addEvent(event) {
    event = createEvents([event])[0];
    $_events().push(event);
    $.store_set(_events, $_events());
    return toEventWithLocalDates(event);
  }
  function updateEvent(event) {
    let id = String(event.id);
    let idx = $_events().findIndex((event2) => event2.id === id);
    if (idx >= 0) {
      $.store_mutate(_events, $.untrack($_events)[idx] = createEvents([event])[0], $.untrack($_events));
      return toEventWithLocalDates(event);
    }
    return null;
  }
  function removeEventById(id) {
    id = String(id);
    let idx = $_events().findIndex((event) => event.id === id);
    if (idx >= 0) {
      $_events().splice(idx, 1);
      $.store_set(_events, $_events());
    }
    return this;
  }
  function getView() {
    return toViewWithLocalDates(get(state._view));
  }
  function unselect() {
    var _a;
    (_a = $_interaction().action) == null ? void 0 : _a.unselect();
    return this;
  }
  function dateFromPoint(x, y) {
    let dayEl = getElementWithPayload(x, y);
    if (dayEl) {
      let info = getPayload(dayEl)(x, y);
      info.date = toLocalDate(info.date);
      return info;
    }
    return null;
  }
  function next() {
    $.store_set(date, nextDate($date(), $duration()));
    return this;
  }
  function prev() {
    $.store_set(date, prevDate($date(), $duration(), $hiddenDays()));
    return this;
  }
  let View2 = $.derived($_viewComponent);
  var fragment = root$q();
  var div = $.first_child(fragment);
  let styles;
  var node = $.child(div);
  Toolbar(node, {});
  var node_1 = $.sibling(node, 2);
  $.component(node_1, () => $.get(View2), ($$anchor2, $$component) => {
    $$component($$anchor2, {});
  });
  $.reset(div);
  var node_2 = $.sibling(div, 2);
  Auxiliary$1(node_2, {});
  $.template_effect(
    ($0) => {
      $.set_class(div, 1, `${$theme().calendar ?? ""} ${$theme().view ?? ""}${$_scrollable() ? " " + $theme().withScroll : ""}${$_iClass() ? " " + $theme()[$_iClass()] : ""}`);
      $.set_attribute(div, "role", $0);
      styles = $.set_style(div, "", styles, { height: $height() });
    },
    [
      () => listView($view()) ? "list" : "table"
    ]
  );
  $.append($$anchor, fragment);
  var $$pop = $.pop({
    setOption,
    getOption,
    refetchEvents,
    getEvents,
    getEventById,
    addEvent,
    updateEvent,
    removeEventById,
    getView,
    unselect,
    dateFromPoint,
    next,
    prev
  });
  $$cleanup();
  return $$pop;
}
function days(state) {
  return derived([state.date, state.firstDay, state.hiddenDays], ([$date, $firstDay, $hiddenDays]) => {
    let days2 = [];
    let day = cloneDate($date);
    let max2 = 7;
    while (day.getUTCDay() !== $firstDay && max2) {
      subtractDay(day);
      --max2;
    }
    for (let i = 0; i < 7; ++i) {
      if (!$hiddenDays.includes(day.getUTCDay())) {
        days2.push(cloneDate(day));
      }
      addDay(day);
    }
    return days2;
  });
}
var root_1$c = $.from_html(`<div role="columnheader"><span></span></div>`);
var root$p = $.from_html(`<div><div role="row"></div> <div></div></div>`);
function Header$1($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_days = () => $.store_get(_days, "$_days", $$stores);
  const $_intlDayHeaderAL = () => $.store_get(_intlDayHeaderAL, "$_intlDayHeaderAL", $$stores);
  const $_intlDayHeader = () => $.store_get(_intlDayHeader, "$_intlDayHeader", $$stores);
  let {
    theme,
    _intlDayHeader,
    _intlDayHeaderAL,
    _days
  } = getContext("state");
  $.init();
  var div = root$p();
  var div_1 = $.child(div);
  $.each(div_1, 5, $_days, $.index, ($$anchor2, day) => {
    var div_2 = root_1$c();
    var span = $.child(div_2);
    $.action(span, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(day)));
    $.reset(div_2);
    $.template_effect(
      ($0, $1) => {
        $.set_class(div_2, 1, `${$theme().day ?? ""} ${$0 ?? ""}`);
        $.set_attribute(span, "aria-label", $1);
      },
      [
        () => {
          var _a;
          return (_a = $theme().weekdays) == null ? void 0 : _a[$.get(day).getUTCDay()];
        },
        () => $_intlDayHeaderAL().format($.get(day))
      ],
      $.derived_safe_equal
    );
    $.append($$anchor2, div_2);
  });
  $.reset(div_1);
  var div_3 = $.sibling(div_1, 2);
  $.reset(div);
  $.template_effect(() => {
    $.set_class(div, 1, $theme().header);
    $.set_class(div_1, 1, $theme().days);
    $.set_class(div_3, 1, $theme().hiddenScroll);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
var root_1$b = $.from_html(`<div></div>`);
var root$o = $.from_html(`<article><!></article>`);
function BaseEvent($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $resources = () => $.store_get(resources, "$resources", $$stores);
  const $eventBackgroundColor = () => $.store_get(eventBackgroundColor, "$eventBackgroundColor", $$stores);
  const $eventColor = () => $.store_get(eventColor, "$eventColor", $$stores);
  const $eventTextColor = () => $.store_get(eventTextColor, "$eventTextColor", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $eventClassNames = () => $.store_get(eventClassNames, "$eventClassNames", $$stores);
  const $_view = () => $.store_get(_view, "$_view", $$stores);
  const $displayEventEnd = () => $.store_get(displayEventEnd, "$displayEventEnd", $$stores);
  const $eventContent = () => $.store_get(eventContent, "$eventContent", $$stores);
  const $_intlEventTime = () => $.store_get(_intlEventTime, "$_intlEventTime", $$stores);
  const $eventDidMount = () => $.store_get(eventDidMount, "$eventDidMount", $$stores);
  const $eventClick = () => $.store_get(eventClick, "$eventClick", $$stores);
  const $eventMouseEnter = () => $.store_get(eventMouseEnter, "$eventMouseEnter", $$stores);
  const $eventMouseLeave = () => $.store_get(eventMouseLeave, "$eventMouseLeave", $$stores);
  let el = $.prop($$props, "el", 15), classes = $.prop($$props, "classes", 3, identity), styles = $.prop($$props, "styles", 3, identity);
  let {
    displayEventEnd,
    eventBackgroundColor,
    eventColor,
    eventContent,
    eventClick,
    eventDidMount,
    eventClassNames,
    eventMouseEnter,
    eventMouseLeave,
    eventTextColor,
    resources,
    theme,
    _view,
    _intlEventTime
  } = getContext("state");
  let event = $.derived(() => $$props.chunk.event);
  let display = $.derived(() => $$props.chunk.event.display);
  let bgColor = $.derived(() => $.get(event).backgroundColor ?? resourceBackgroundColor($.get(event), $resources()) ?? $eventBackgroundColor() ?? $eventColor());
  let txtColor = $.derived(() => $.get(event).textColor ?? resourceTextColor($.get(event), $resources()) ?? $eventTextColor());
  let style = $.derived(() => entries(styles()({
    "background-color": $.get(bgColor),
    "color": $.get(txtColor)
  })).map((entry) => `${entry[0]}:${entry[1]}`).join(";"));
  let classNames = $.derived(() => classes()([
    bgEvent($.get(display)) ? $theme().bgEvent : $theme().event,
    ...createEventClasses($eventClassNames(), $.get(event), $_view())
  ]).join(" "));
  let $$d = $.derived(() => createEventContent($$props.chunk, $displayEventEnd(), $eventContent(), $theme(), $_intlEventTime(), $_view())), $$array = $.derived(() => $.to_array($.get($$d), 2)), timeText = $.derived(() => $.get($$array)[0]), content = $.derived(() => $.get($$array)[1]);
  onMount(() => {
    if (isFunction($eventDidMount())) {
      $eventDidMount()({
        event: toEventWithLocalDates($.get(event)),
        timeText: $.get(timeText),
        el: el(),
        view: toViewWithLocalDates($_view())
      });
    }
  });
  function createHandler(fn, display2) {
    return !helperEvent(display2) && isFunction(fn) ? (jsEvent) => fn({
      event: toEventWithLocalDates($.get(event)),
      el: el(),
      jsEvent,
      view: toViewWithLocalDates($_view())
    }) : void 0;
  }
  let onclick2 = $.derived(() => !bgEvent($.get(display)) && createHandler($eventClick(), $.get(display)) || void 0);
  let onkeydown = $.derived(() => $.get(onclick2) && keyEnter($.get(onclick2)));
  let onmouseenter = $.derived(() => createHandler($eventMouseEnter(), $.get(display)));
  let onmouseleave = $.derived(() => createHandler($eventMouseLeave(), $.get(display)));
  var article = root$o();
  article.__click = function(...$$args) {
    var _a;
    (_a = $.get(onclick2)) == null ? void 0 : _a.apply(this, $$args);
  };
  article.__keydown = function(...$$args) {
    var _a;
    (_a = $.get(onkeydown)) == null ? void 0 : _a.apply(this, $$args);
  };
  article.__pointerdown = function(...$$args) {
    var _a;
    (_a = $$props.onpointerdown) == null ? void 0 : _a.apply(this, $$args);
  };
  {
    const defaultBody = ($$anchor2) => {
      var div = root_1$b();
      $.action(div, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(content));
      $.template_effect(() => $.set_class(div, 1, $.clsx($theme().eventBody)));
      $.append($$anchor2, div);
    };
    var node = $.child(article);
    {
      var consequent = ($$anchor2) => {
        var fragment = $.comment();
        var node_1 = $.first_child(fragment);
        $.snippet(node_1, () => $$props.body, () => defaultBody, () => $.get(bgColor), () => $.get(txtColor));
        $.append($$anchor2, fragment);
      };
      var alternate = ($$anchor2) => {
        defaultBody($$anchor2);
      };
      $.if(node, ($$render) => {
        if ($$props.body) $$render(consequent);
        else $$render(alternate, false);
      });
    }
    $.reset(article);
    $.bind_this(article, ($$value) => el($$value), () => el());
  }
  $.template_effect(() => {
    $.set_class(article, 1, $.clsx($.get(classNames)));
    $.set_style(article, $.get(style));
    $.set_attribute(article, "role", $.get(onclick2) ? "button" : void 0);
    $.set_attribute(article, "tabindex", $.get(onclick2) ? 0 : void 0);
  });
  $.event("mouseenter", article, function(...$$args) {
    var _a;
    (_a = $.get(onmouseenter)) == null ? void 0 : _a.apply(this, $$args);
  });
  $.event("mouseleave", article, function(...$$args) {
    var _a;
    (_a = $.get(onmouseleave)) == null ? void 0 : _a.apply(this, $$args);
  });
  $.append($$anchor, article);
  $.pop();
  $$cleanup();
}
$.delegate(["click", "keydown", "pointerdown"]);
function InteractableEvent($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_iClasses = () => $.store_get(_iClasses, "$_iClasses", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  let el = $.prop($$props, "el", 15);
  let { _interaction, _iClasses } = getContext("state");
  let event = $.derived(() => $$props.chunk.event);
  let display = $.derived(() => $$props.chunk.event.display);
  let classes = $.derived(() => (classNames) => $_iClasses()(classNames, $.get(event)));
  function createDragHandler(event2) {
    var _a, _b;
    return ((_a = $_interaction().action) == null ? void 0 : _a.draggable(event2)) ? (jsEvent) => {
      var _a2, _b2;
      return $_interaction().action.drag(event2, jsEvent, (_a2 = $$props.forceDate) == null ? void 0 : _a2.call($$props), (_b2 = $$props.forceMargin) == null ? void 0 : _b2.call($$props));
    } : (_b = $_interaction().action) == null ? void 0 : _b.noAction;
  }
  let onpointerdown = $.derived(() => !bgEvent($.get(display)) && !helperEvent($.get(display)) ? createDragHandler($.get(event)) : void 0);
  let Resizer2 = $.derived(() => $_interaction().resizer);
  {
    const body = ($$anchor2, defaultBody = $.noop) => {
      var fragment_1 = $.comment();
      var node = $.first_child(fragment_1);
      {
        var consequent = ($$anchor3) => {
          var fragment_2 = $.comment();
          var node_1 = $.first_child(fragment_2);
          $.component(node_1, () => $.get(Resizer2), ($$anchor4, $$component) => {
            $$component($$anchor4, {
              get chunk() {
                return $$props.chunk;
              },
              get axis() {
                return $$props.axis;
              },
              get forceDate() {
                return $$props.forceDate;
              },
              get forceMargin() {
                return $$props.forceMargin;
              },
              children: ($$anchor5, $$slotProps) => {
                var fragment_3 = $.comment();
                var node_2 = $.first_child(fragment_3);
                $.snippet(node_2, defaultBody);
                $.append($$anchor5, fragment_3);
              },
              $$slots: { default: true }
            });
          });
          $.append($$anchor3, fragment_2);
        };
        var alternate = ($$anchor3) => {
          var fragment_4 = $.comment();
          var node_3 = $.first_child(fragment_4);
          $.snippet(node_3, defaultBody);
          $.append($$anchor3, fragment_4);
        };
        $.if(node, ($$render) => {
          if ($.get(Resizer2)) $$render(consequent);
          else $$render(alternate, false);
        });
      }
      $.append($$anchor2, fragment_1);
    };
    BaseEvent($$anchor, {
      get chunk() {
        return $$props.chunk;
      },
      get classes() {
        return $.get(classes);
      },
      get styles() {
        return $$props.styles;
      },
      get onpointerdown() {
        return $.get(onpointerdown);
      },
      get el() {
        return el();
      },
      set el($$value) {
        el($$value);
      },
      body,
      $$slots: { body: true }
    });
  }
  $.pop();
  $$cleanup();
}
function Event$4($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $dayMaxEvents = () => $.store_get(dayMaxEvents, "$dayMaxEvents", $$stores);
  const $_hiddenEvents = () => $.store_get(_hiddenEvents, "$_hiddenEvents", $$stores);
  const $_popupDate = () => $.store_get(_popupDate, "$_popupDate", $$stores);
  let longChunks = $.prop($$props, "longChunks", 19, () => ({})), inPopup = $.prop($$props, "inPopup", 3, false), dates = $.prop($$props, "dates", 19, () => []);
  let { dayMaxEvents, _hiddenEvents, _popupDate } = getContext("state");
  let el = $.state(void 0);
  let margin = $.state(1);
  let hidden = $.state(false);
  let event = $.derived(() => $$props.chunk.event);
  let display = $.derived(() => $$props.chunk.event.display);
  let styles = $.derived(() => (style) => {
    if (bgEvent($.get(display))) {
      style["width"] = `calc(${$$props.chunk.days * 100}% + ${$$props.chunk.days - 1}px)`;
    } else {
      let marginTop = $.get(margin);
      if ($.get(event)._margin) {
        let [_margin, _dates] = $.get(event)._margin;
        if ($$props.chunk.date >= _dates[0] && $$props.chunk.date <= _dates.at(-1)) {
          marginTop = _margin;
        }
      }
      style["width"] = `calc(${$$props.chunk.days * 100}% + ${($$props.chunk.days - 1) * 7}px)`;
      style["margin-top"] = `${marginTop}px`;
    }
    if ($.get(hidden)) {
      style["visibility"] = "hidden";
    }
    return style;
  });
  function reposition() {
    $.set(margin, repositionEvent$1($$props.chunk, longChunks(), height($.get(el))), true);
    if ($dayMaxEvents() === true) {
      hide();
    } else {
      $.set(hidden, false);
    }
  }
  function hide() {
    let dayEl = ancestor($.get(el), 2);
    let h = height(dayEl) - height(dayEl.firstElementChild) - footHeight(dayEl);
    $.set(hidden, $$props.chunk.bottom > h);
    let update = false;
    for (let date of $$props.chunk.dates) {
      let hiddenEvents = $_hiddenEvents()[date.getTime()];
      if (hiddenEvents) {
        let size = hiddenEvents.size;
        if ($.get(hidden)) {
          hiddenEvents.add($$props.chunk.event);
        } else {
          hiddenEvents.delete($$props.chunk.event);
        }
        if (size !== hiddenEvents.size) {
          update = true;
        }
      }
    }
    if (update) {
      $.store_set(_hiddenEvents, $_hiddenEvents());
    }
  }
  function footHeight(dayEl) {
    let h = 0;
    for (let i = 0; i < $$props.chunk.days; ++i) {
      h = max(h, height(dayEl.lastElementChild));
      dayEl = dayEl.nextElementSibling;
      if (!dayEl) {
        break;
      }
    }
    return h;
  }
  InteractableEvent($$anchor, {
    get chunk() {
      return $$props.chunk;
    },
    get styles() {
      return $.get(styles);
    },
    axis: "x",
    forceDate: () => inPopup() ? $_popupDate() : void 0,
    forceMargin: () => [
      rect($.get(el)).top - rect(ancestor($.get(el), 1)).top,
      dates()
    ],
    get el() {
      return $.get(el);
    },
    set el($$value) {
      $.set(el, $$value, true);
    }
  });
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
var root$n = $.from_html(`<div><div><time></time> <a role="button" tabindex="0">&times;</a></div> <div></div></div>`);
function Popup($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_popupChunks = () => $.store_get(_popupChunks, "$_popupChunks", $$stores);
  const $_popupDate = () => $.store_get(_popupDate, "$_popupDate", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_intlDayPopover = () => $.store_get(_intlDayPopover, "$_intlDayPopover", $$stores);
  const $buttonText = () => $.store_get(buttonText, "$buttonText", $$stores);
  let {
    buttonText,
    theme,
    _interaction,
    _intlDayPopover,
    _popupDate,
    _popupChunks
  } = getContext("state");
  let el = $.state(void 0);
  let style = $.state("");
  function position() {
    let dayEl = ancestor($.get(el), 1);
    let bodyEl = ancestor(dayEl, 3);
    let popupRect = rect($.get(el));
    let dayRect = rect(dayEl);
    let bodyRect = rect(bodyEl);
    $.set(style, "");
    let left;
    if (popupRect.width >= bodyRect.width) {
      left = bodyRect.left - dayRect.left;
      let right = dayRect.right - bodyRect.right;
      $.set(style, $.get(style) + `right:${right}px;`);
    } else {
      left = (dayRect.width - popupRect.width) / 2;
      if (dayRect.left + left < bodyRect.left) {
        left = bodyRect.left - dayRect.left;
      } else if (dayRect.left + left + popupRect.width > bodyRect.right) {
        left = bodyRect.right - dayRect.left - popupRect.width;
      }
    }
    $.set(style, $.get(style) + `left:${left}px;`);
    let top;
    if (popupRect.height >= bodyRect.height) {
      top = bodyRect.top - dayRect.top;
      let bottom = dayRect.bottom - bodyRect.bottom;
      $.set(style, $.get(style) + `bottom:${bottom}px;`);
    } else {
      top = (dayRect.height - popupRect.height) / 2;
      if (dayRect.top + top < bodyRect.top) {
        top = bodyRect.top - dayRect.top;
      } else if (dayRect.top + top + popupRect.height > bodyRect.bottom) {
        top = bodyRect.bottom - dayRect.top - popupRect.height;
      }
    }
    $.set(style, $.get(style) + `top:${top}px;`);
  }
  $.user_effect(() => {
    $_popupChunks();
    tick().then(reposition);
  });
  function reposition() {
    if ($_popupChunks().length) {
      position();
    } else {
      close();
    }
  }
  function close() {
    $.store_set(_popupDate, null);
    $.store_set(_popupChunks, []);
  }
  function handlePointerDownOutside() {
    var _a;
    close();
    (_a = $_interaction().action) == null ? void 0 : _a.noClick();
  }
  var div = root$n();
  var event_handler = $.derived(stopPropagation);
  div.__pointerdown = function(...$$args) {
    var _a;
    (_a = $.get(event_handler)) == null ? void 0 : _a.apply(this, $$args);
  };
  var div_1 = $.child(div);
  var time = $.child(div_1);
  $.action(time, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayPopover().format($_popupDate()));
  var a = $.sibling(time, 2);
  var event_handler_1 = $.derived(() => stopPropagation(close));
  a.__click = function(...$$args) {
    var _a;
    (_a = $.get(event_handler_1)) == null ? void 0 : _a.apply(this, $$args);
  };
  var event_handler_2 = $.derived(() => keyEnter(close));
  a.__keydown = function(...$$args) {
    var _a;
    (_a = $.get(event_handler_2)) == null ? void 0 : _a.apply(this, $$args);
  };
  $.reset(div_1);
  var div_2 = $.sibling(div_1, 2);
  $.each(div_2, 5, $_popupChunks, (chunk) => chunk.event, ($$anchor2, chunk) => {
    Event$4($$anchor2, {
      get chunk() {
        return $.get(chunk);
      },
      inPopup: true
    });
  });
  $.reset(div_2);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.action(div, ($$node, $$action_arg) => outsideEvent == null ? void 0 : outsideEvent($$node, $$action_arg), () => "pointerdown");
  $.template_effect(
    ($0) => {
      $.set_class(div, 1, $theme().popup);
      $.set_style(div, $.get(style));
      $.set_class(div_1, 1, $theme().dayHead);
      $.set_attribute(time, "datetime", $0);
      $.set_attribute(a, "aria-label", $buttonText().close);
      $.set_class(div_2, 1, $theme().events);
    },
    [() => toISOString($_popupDate(), 10)]
  );
  $.event("pointerdownoutside", div, handlePointerDownOutside);
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
$.delegate(["pointerdown", "click", "keydown"]);
var root_1$a = $.from_html(`<span></span>`);
var root_5$1 = $.from_html(`<div><!></div>`);
var root_6 = $.from_html(`<div><!></div>`);
var root_4$2 = $.from_html(`<!> <!>`, 1);
var root_10 = $.from_html(`<a role="button" tabindex="0" aria-haspopup="true"></a>`);
var root$m = $.from_html(`<div role="cell"><div><time></time> <!></div> <div><!></div> <!> <div><!></div> <!> <div><!></div></div>`);
function Day$4($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_hiddenEvents = () => $.store_get(_hiddenEvents, "$_hiddenEvents", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $currentDate = () => $.store_get(currentDate, "$currentDate", $$stores);
  const $highlightedDates = () => $.store_get(highlightedDates, "$highlightedDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $moreLinkContent = () => $.store_get(moreLinkContent, "$moreLinkContent", $$stores);
  const $_popupDate = () => $.store_get(_popupDate, "$_popupDate", $$stores);
  const $weekNumbers = () => $.store_get(weekNumbers, "$weekNumbers", $$stores);
  const $firstDay = () => $.store_get(firstDay, "$firstDay", $$stores);
  const $weekNumberContent = () => $.store_get(weekNumberContent, "$weekNumberContent", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $_intlDayCell = () => $.store_get(_intlDayCell, "$_intlDayCell", $$stores);
  let iChunks = $.prop($$props, "iChunks", 19, () => []);
  let {
    date: currentDate,
    dayMaxEvents,
    highlightedDates,
    firstDay,
    moreLinkContent,
    theme,
    validRange,
    weekNumbers,
    weekNumberContent,
    _hiddenEvents,
    _intlDayCell,
    _popupDate,
    _popupChunks,
    _today,
    _interaction
  } = getContext("state");
  let el = $.state(void 0);
  let hiddenEvents = new SvelteSet();
  $.user_pre_effect(() => {
    $.store_mutate(_hiddenEvents, $.untrack($_hiddenEvents)[$$props.date.getTime()] = untrack(() => hiddenEvents), $.untrack($_hiddenEvents));
  });
  let refs = [];
  let isToday = $.derived(() => datesEqual($$props.date, $_today()));
  let otherMonth = $.derived(() => $$props.date.getUTCMonth() !== $currentDate().getUTCMonth());
  let highlight = $.derived(() => $highlightedDates().some((d) => datesEqual(d, $$props.date)));
  let disabled = $.derived(() => outsideRange($$props.date, $validRange()));
  let dayBgChunks = $.derived(() => !$.get(disabled) ? $$props.bgChunks.filter((bgChunk) => datesEqual(bgChunk.date, $$props.date)) : []);
  let dayChunks = $.derived(() => {
    let dayChunks2 = [];
    if (!$.get(disabled)) {
      for (let chunk of $$props.chunks) {
        if (datesEqual(chunk.date, $$props.date)) {
          dayChunks2.push(chunk);
        }
      }
    }
    return dayChunks2;
  });
  $.user_pre_effect(() => {
    $.get(dayChunks);
    hiddenEvents.clear();
  });
  let moreLink = $.derived(() => {
    let moreLink2 = "";
    if (!$.get(disabled) && hiddenEvents.size) {
      let text = "+" + hiddenEvents.size + " more";
      if ($moreLinkContent()) {
        moreLink2 = isFunction($moreLinkContent()) ? $moreLinkContent()({ num: hiddenEvents.size, text }) : $moreLinkContent();
      } else {
        moreLink2 = text;
      }
    }
    return moreLink2;
  });
  onMount(() => {
    setPayload($.get(el), () => ({
      allDay: true,
      date: $$props.date,
      resource: void 0,
      dayEl: $.get(el),
      disabled: $.get(disabled)
    }));
  });
  function showMore() {
    $.store_set(_popupDate, $$props.date);
  }
  let showPopup = $.derived(() => $_popupDate() && datesEqual($$props.date, $_popupDate()));
  $.user_pre_effect(() => {
    $.get(dayChunks);
    $$props.longChunks;
    if ($.get(showPopup)) {
      tick().then(setPopupChunks);
    }
  });
  function setPopupChunks() {
    var _a;
    let nextDay = addDay(cloneDate($$props.date));
    let chunks = $.get(dayChunks).concat(((_a = $$props.longChunks[$$props.date.getTime()]) == null ? void 0 : _a.chunks) || []);
    $.store_set(_popupChunks, chunks.map((chunk) => assign({}, chunk, createEventChunk(chunk.event, $$props.date, nextDay), { days: 1, dates: [$$props.date] })).sort((a, b) => a.top - b.top));
  }
  let showWeekNumber = $.derived(() => $weekNumbers() && $$props.date.getUTCDay() == ($firstDay() ? 1 : 0));
  let weekNumber = $.derived(() => {
    let weekNumber2;
    if ($.get(showWeekNumber)) {
      let week = getWeekNumber($$props.date, $firstDay());
      if ($weekNumberContent()) {
        weekNumber2 = isFunction($weekNumberContent()) ? $weekNumberContent()({ date: toLocalDate($$props.date), week }) : $weekNumberContent();
      } else {
        weekNumber2 = "W" + String(week).padStart(2, "0");
      }
    }
    return weekNumber2;
  });
  function reposition() {
    if (!$.get(disabled)) {
      runReposition(refs, $.get(dayChunks));
    }
  }
  var div = root$m();
  div.__pointerdown = function(...$$args) {
    var _a, _b;
    (_b = (_a = $_interaction().action) == null ? void 0 : _a.select) == null ? void 0 : _b.apply(this, $$args);
  };
  var div_1 = $.child(div);
  var time = $.child(div_1);
  $.action(time, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayCell().format($$props.date));
  var node = $.sibling(time, 2);
  {
    var consequent = ($$anchor2) => {
      var span = root_1$a();
      $.action(span, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(weekNumber));
      $.template_effect(() => $.set_class(span, 1, $theme().weekNumber));
      $.append($$anchor2, span);
    };
    $.if(node, ($$render) => {
      if ($.get(showWeekNumber)) $$render(consequent);
    });
  }
  $.reset(div_1);
  var div_2 = $.sibling(div_1, 2);
  var node_1 = $.child(div_2);
  {
    var consequent_1 = ($$anchor2) => {
      var fragment = $.comment();
      var node_2 = $.first_child(fragment);
      $.each(node_2, 17, () => $.get(dayBgChunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event$4($$anchor3, {
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      $.append($$anchor2, fragment);
    };
    $.if(node_1, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_1);
    });
  }
  $.reset(div_2);
  var node_3 = $.sibling(div_2, 2);
  {
    var consequent_4 = ($$anchor2) => {
      var fragment_2 = root_4$2();
      var node_4 = $.first_child(fragment_2);
      {
        var consequent_2 = ($$anchor3) => {
          var div_3 = root_5$1();
          var node_5 = $.child(div_3);
          Event$4(node_5, {
            get chunk() {
              return iChunks()[2];
            }
          });
          $.reset(div_3);
          $.template_effect(() => $.set_class(div_3, 1, $theme().events));
          $.append($$anchor3, div_3);
        };
        $.if(node_4, ($$render) => {
          if (iChunks()[2] && datesEqual(iChunks()[2].date, $$props.date)) $$render(consequent_2);
        });
      }
      var node_6 = $.sibling(node_4, 2);
      {
        var consequent_3 = ($$anchor3) => {
          var div_4 = root_6();
          var node_7 = $.child(div_4);
          Event$4(node_7, {
            get chunk() {
              return iChunks()[0];
            }
          });
          $.reset(div_4);
          $.template_effect(() => $.set_class(div_4, 1, `${$theme().events ?? ""} ${$theme().preview ?? ""}`));
          $.append($$anchor3, div_4);
        };
        $.if(node_6, ($$render) => {
          if (iChunks()[0] && datesEqual(iChunks()[0].date, $$props.date)) $$render(consequent_3);
        });
      }
      $.append($$anchor2, fragment_2);
    };
    $.if(node_3, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_4);
    });
  }
  var div_5 = $.sibling(node_3, 2);
  var node_8 = $.child(div_5);
  {
    var consequent_5 = ($$anchor2) => {
      var fragment_3 = $.comment();
      var node_9 = $.first_child(fragment_3);
      $.each(node_9, 19, () => $.get(dayChunks), (chunk) => chunk.event, ($$anchor3, chunk, i) => {
        $.bind_this(
          Event$4($$anchor3, {
            get chunk() {
              return $.get(chunk);
            },
            get longChunks() {
              return $$props.longChunks;
            },
            get dates() {
              return $$props.dates;
            }
          }),
          ($$value, i2) => refs[i2] = $$value,
          (i2) => refs == null ? void 0 : refs[i2],
          () => [$.get(i)]
        );
      });
      $.append($$anchor2, fragment_3);
    };
    $.if(node_8, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_5);
    });
  }
  $.reset(div_5);
  var node_10 = $.sibling(div_5, 2);
  {
    var consequent_6 = ($$anchor2) => {
      Popup($$anchor2, {});
    };
    $.if(node_10, ($$render) => {
      if ($.get(showPopup)) $$render(consequent_6);
    });
  }
  var div_6 = $.sibling(node_10, 2);
  var node_11 = $.child(div_6);
  {
    var consequent_7 = ($$anchor2) => {
      var a_1 = root_10();
      var event_handler = $.derived(() => stopPropagation(showMore));
      a_1.__click = function(...$$args) {
        var _a;
        (_a = $.get(event_handler)) == null ? void 0 : _a.apply(this, $$args);
      };
      var event_handler_1 = $.derived(() => keyEnter(showMore));
      a_1.__keydown = function(...$$args) {
        var _a;
        (_a = $.get(event_handler_1)) == null ? void 0 : _a.apply(this, $$args);
      };
      var event_handler_2 = $.derived(stopPropagation);
      a_1.__pointerdown = function(...$$args) {
        var _a;
        (_a = $.get(event_handler_2)) == null ? void 0 : _a.apply(this, $$args);
      };
      $.action(a_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(moreLink));
      $.append($$anchor2, a_1);
    };
    $.if(node_11, ($$render) => {
      if (!$.get(disabled) && hiddenEvents.size) $$render(consequent_7);
    });
  }
  $.reset(div_6);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.template_effect(
    ($0, $1) => {
      $.set_class(div, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$.get(isToday) ? " " + $theme().today : ""}${$.get(otherMonth) ? " " + $theme().otherMonth : ""}${$.get(highlight) ? " " + $theme().highlight : ""}${$.get(disabled) ? " " + $theme().disabled : ""}`);
      $.set_class(div_1, 1, $theme().dayHead);
      $.set_attribute(time, "datetime", $1);
      $.set_class(div_2, 1, $theme().bgEvents);
      $.set_class(div_5, 1, $theme().events);
      $.set_class(div_6, 1, $theme().dayFoot);
    },
    [
      () => {
        var _a;
        return (_a = $theme().weekdays) == null ? void 0 : _a[$$props.date.getUTCDay()];
      },
      () => toISOString($$props.date, 10)
    ]
  );
  $.append($$anchor, div);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
$.delegate(["pointerdown", "click", "keydown"]);
var root$l = $.from_html(`<div role="row"></div>`);
function Week$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $filterEventsWithResources = () => $.store_get(filterEventsWithResources, "$filterEventsWithResources", $$stores);
  const $resources = () => $.store_get(resources, "$resources", $$stores);
  const $hiddenDays = () => $.store_get(hiddenDays, "$hiddenDays", $$stores);
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let {
    _filteredEvents,
    _iEvents,
    resources,
    filterEventsWithResources,
    hiddenDays,
    theme,
    validRange
  } = getContext("state");
  let refs = [];
  let start = $.derived(() => limitToRange($$props.dates[0], $validRange()));
  let end = $.derived(() => addDay(cloneDate(limitToRange($$props.dates.at(-1), $validRange()))));
  let $$d = $.derived(() => {
    let chunks2 = [];
    let bgChunks2 = [];
    for (let event of $_filteredEvents()) {
      if (eventIntersects(event, $.get(start), $.get(end), $filterEventsWithResources() ? $resources() : void 0)) {
        let chunk = createEventChunk(event, $.get(start), $.get(end));
        if (bgEvent(event.display)) {
          if (event.allDay) {
            bgChunks2.push(chunk);
          }
        } else {
          chunks2.push(chunk);
        }
      }
    }
    prepareEventChunks$1(bgChunks2, $hiddenDays());
    let longChunks2 = prepareEventChunks$1(chunks2, $hiddenDays());
    return [chunks2, bgChunks2, longChunks2];
  }), $$array = $.derived(() => $.to_array($.get($$d), 3)), chunks = $.derived(() => $.get($$array)[0]), bgChunks = $.derived(() => $.get($$array)[1]), longChunks = $.derived(() => $.get($$array)[2]);
  let iChunks = $.derived(() => $_iEvents().map((event) => {
    let chunk;
    if (event && eventIntersects(event, $.get(start), $.get(end))) {
      chunk = createEventChunk(event, $.get(start), $.get(end));
      prepareEventChunks$1([chunk], $hiddenDays());
    } else {
      chunk = null;
    }
    return chunk;
  }));
  function reposition() {
    runReposition(refs, $$props.dates);
  }
  var div = root$l();
  $.each(div, 21, () => $$props.dates, $.index, ($$anchor2, date, i) => {
    $.bind_this(
      Day$4($$anchor2, {
        get date() {
          return $.get(date);
        },
        get chunks() {
          return $.get(chunks);
        },
        get bgChunks() {
          return $.get(bgChunks);
        },
        get longChunks() {
          return $.get(longChunks);
        },
        get iChunks() {
          return $.get(iChunks);
        },
        get dates() {
          return $$props.dates;
        }
      }),
      ($$value, i2) => refs[i2] = $$value,
      (i2) => refs == null ? void 0 : refs[i2],
      () => [i]
    );
  });
  $.reset(div);
  $.template_effect(() => $.set_class(div, 1, $theme().days));
  $.append($$anchor, div);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
var root$k = $.from_html(`<div><div></div></div>`);
function Body$3($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $hiddenDays = () => $.store_get(hiddenDays, "$hiddenDays", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $dayMaxEvents = () => $.store_get(dayMaxEvents, "$dayMaxEvents", $$stores);
  const $_hiddenEvents = () => $.store_get(_hiddenEvents, "$_hiddenEvents", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let {
    _bodyEl,
    _viewDates,
    _filteredEvents,
    _hiddenEvents,
    _recheckScrollable,
    dayMaxEvents,
    hiddenDays,
    theme
  } = getContext("state");
  let refs = [];
  let days2 = $.derived(() => 7 - $hiddenDays().length);
  let weeks = $.derived(() => {
    let weeks2 = [];
    for (let i = 0; i < $_viewDates().length / $.get(days2); ++i) {
      let dates = [];
      for (let j = 0; j < $.get(days2); ++j) {
        dates.push($_viewDates()[i * $.get(days2) + j]);
      }
      weeks2.push(dates);
    }
    return weeks2;
  });
  $.user_pre_effect(() => {
    $.get(weeks);
    $dayMaxEvents();
    $.store_set(_hiddenEvents, {});
  });
  function reposition() {
    runReposition(refs, $.get(weeks));
  }
  $.user_effect(() => {
    $_filteredEvents();
    $_hiddenEvents();
    $dayMaxEvents();
    untrack(reposition);
  });
  var div = root$k();
  $.event("resize", $.window, reposition);
  var div_1 = $.child(div);
  $.each(div_1, 21, () => $.get(weeks), $.index, ($$anchor2, dates, i) => {
    $.bind_this(
      Week$1($$anchor2, {
        get dates() {
          return $.get(dates);
        }
      }),
      ($$value, i2) => refs[i2] = $$value,
      (i2) => refs == null ? void 0 : refs[i2],
      () => [i]
    );
  });
  $.reset(div_1);
  $.reset(div);
  $.bind_this(div, ($$value) => $.store_set(_bodyEl, $$value), () => $_bodyEl());
  $.action(div, ($$node, $$action_arg) => observeResize == null ? void 0 : observeResize($$node, $$action_arg), () => () => $.store_set(_recheckScrollable, true));
  $.template_effect(() => {
    $.set_class(div, 1, `${$theme().body ?? ""}${$dayMaxEvents() === true ? " " + $theme().uniform : ""}`);
    $.set_class(div_1, 1, $theme().content);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
var root$j = $.from_html(`<!> <!>`, 1);
function View$4($$anchor) {
  var fragment = root$j();
  var node = $.first_child(fragment);
  Header$1(node, {});
  var node_1 = $.sibling(node, 2);
  Body$3(node_1, {});
  $.append($$anchor, fragment);
}
const index$4 = {
  createOptions(options) {
    options.dayMaxEvents = false;
    options.dayCellFormat = { day: "numeric" };
    options.dayPopoverFormat = { month: "long", day: "numeric", year: "numeric" };
    options.moreLinkContent = void 0;
    options.weekNumbers = false;
    options.weekNumberContent = void 0;
    options.buttonText.dayGridMonth = "month";
    options.buttonText.close = "Close";
    options.theme.uniform = "ec-uniform";
    options.theme.dayFoot = "ec-day-foot";
    options.theme.popup = "ec-popup";
    options.theme.weekNumber = "ec-week-number";
    options.view = "dayGridMonth";
    options.views.dayGridMonth = {
      buttonText: btnTextMonth,
      component: View$4,
      dayHeaderFormat: { weekday: "short" },
      dayHeaderAriaLabelFormat: { weekday: "long" },
      displayEventEnd: false,
      duration: { months: 1 },
      theme: themeView("ec-day-grid ec-month-view"),
      titleFormat: { year: "numeric", month: "long" }
    };
  },
  createStores(state) {
    state._days = days(state);
    state._intlDayCell = intl(state.locale, state.dayCellFormat);
    state._intlDayPopover = intl(state.locale, state.dayPopoverFormat);
    state._hiddenEvents = writable({});
    state._popupDate = writable(null);
    state._popupChunks = writable([]);
  }
};
function eventDraggable(event, $eventStartEditable, $editable) {
  return event.startEditable ?? $eventStartEditable ?? event.editable ?? $editable;
}
function eventResizable(event, $eventDurationEditable, $editable) {
  return event.durationEditable ?? $eventDurationEditable ?? event.editable ?? $editable;
}
let busy = false;
function animate(fn) {
  if (!busy) {
    busy = true;
    window.requestAnimationFrame(() => {
      fn();
      busy = false;
    });
  }
}
function limit(value, minLimit, maxLimit) {
  return max(minLimit, min(maxLimit, value));
}
function Action($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $eventStartEditable = () => $.store_get(eventStartEditable, "$eventStartEditable", $$stores);
  const $editable = () => $.store_get(editable, "$editable", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $selectable = () => $.store_get(selectable, "$selectable", $$stores);
  const $view = () => $.store_get(view2, "$view", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $datesAboveResources = () => $.store_get(datesAboveResources, "$datesAboveResources", $$stores);
  const $selectLongPressDelay = () => $.store_get(selectLongPressDelay, "$selectLongPressDelay", $$stores);
  const $eventLongPressDelay = () => $.store_get(eventLongPressDelay, "$eventLongPressDelay", $$stores);
  const $longPressDelay = () => $.store_get(longPressDelay, "$longPressDelay", $$stores);
  const $selectMinDistance = () => $.store_get(selectMinDistance, "$selectMinDistance", $$stores);
  const $eventDragMinDistance = () => $.store_get(eventDragMinDistance, "$eventDragMinDistance", $$stores);
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  const $eventResizeStart = () => $.store_get(eventResizeStart, "$eventResizeStart", $$stores);
  const $eventDragStart = () => $.store_get(eventDragStart, "$eventDragStart", $$stores);
  const $resizeConstraint = () => $.store_get(resizeConstraint, "$resizeConstraint", $$stores);
  const $selectConstraint = () => $.store_get(selectConstraint, "$selectConstraint", $$stores);
  const $dragConstraint = () => $.store_get(dragConstraint, "$dragConstraint", $$stores);
  const $dragScroll = () => $.store_get(dragScroll, "$dragScroll", $$stores);
  const $slotHeight = () => $.store_get(slotHeight, "$slotHeight", $$stores);
  const $slotWidth = () => $.store_get(slotWidth, "$slotWidth", $$stores);
  const $unselectAuto = () => $.store_get(unselectAuto, "$unselectAuto", $$stores);
  const $unselectCancel = () => $.store_get(unselectCancel, "$unselectCancel", $$stores);
  const $selectFn = () => $.store_get(selectFn, "$selectFn", $$stores);
  const $eventResizeStop = () => $.store_get(eventResizeStop, "$eventResizeStop", $$stores);
  const $eventDragStop = () => $.store_get(eventDragStop, "$eventDragStop", $$stores);
  const $_view = () => $.store_get(_view, "$_view", $$stores);
  const $eventResize = () => $.store_get(eventResize, "$eventResize", $$stores);
  const $eventDrop = () => $.store_get(eventDrop, "$eventDrop", $$stores);
  const $dateClick = () => $.store_get(dateClick, "$dateClick", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_dayGrid = () => $.store_get(_dayGrid, "$_dayGrid", $$stores);
  const $_events = () => $.store_get(_events, "$_events", $$stores);
  const $selectBackgroundColor = () => $.store_get(selectBackgroundColor, "$selectBackgroundColor", $$stores);
  const $unselectFn = () => $.store_get(unselectFn, "$unselectFn", $$stores);
  let {
    _iEvents,
    _iClass,
    _events,
    _view,
    _dayGrid,
    _bodyEl,
    datesAboveResources,
    dateClick,
    dragConstraint,
    dragScroll,
    editable,
    eventStartEditable,
    eventDragMinDistance,
    eventDragStart,
    eventDragStop,
    eventDrop,
    eventLongPressDelay,
    eventResizeStart,
    eventResizeStop,
    eventResize,
    longPressDelay,
    resizeConstraint,
    selectable,
    select: selectFn,
    selectBackgroundColor,
    selectConstraint,
    selectLongPressDelay,
    selectMinDistance,
    slotDuration,
    slotHeight,
    slotWidth,
    unselect: unselectFn,
    unselectAuto,
    unselectCancel,
    validRange,
    view: view2
  } = getContext("state");
  const ACTION_DRAG = 1;
  const ACTION_RESIZE_END = 2;
  const ACTION_RESIZE_START = 3;
  const ACTION_SELECT = 4;
  const ACTION_CLICK = 5;
  const ACTION_NO_ACTION = 6;
  let action;
  let interacting;
  let event;
  let display;
  let date, newDate;
  let resource, newResource;
  let fromX, fromY;
  let toX, toY;
  let bodyEl, bodyRect, clipEl, clipRect;
  let delta;
  let allDay;
  let iClass;
  let minResize;
  let selectStep;
  let selected;
  let noDateClick;
  let timer = $.mutable_source();
  let viewport;
  let margin;
  let extraDuration;
  function draggable(event2) {
    return eventDraggable(event2, $eventStartEditable(), $editable());
  }
  function drag(eventToDrag, jsEvent, forceDate, forceMargin) {
    if (!action) {
      action = validJsEvent(jsEvent) ? ACTION_DRAG : ACTION_NO_ACTION;
      if (complexAction()) {
        event = eventToDrag;
        common(jsEvent);
        if (forceDate) {
          date = forceDate;
        }
        if (forceMargin) {
          margin = forceMargin;
        }
        iClass = "dragging";
        move(jsEvent);
      }
    }
  }
  function resize(eventToResize, jsEvent, start, axis, forceDate, forceMargin, zeroDuration) {
    if (!action) {
      action = validJsEvent(jsEvent) ? start ? ACTION_RESIZE_START : ACTION_RESIZE_END : ACTION_NO_ACTION;
      if (complexAction()) {
        event = eventToResize;
        common(jsEvent);
        if (forceDate) {
          date = forceDate;
        }
        if (forceMargin) {
          margin = forceMargin;
        }
        iClass = axis === "x" ? "resizingX" : "resizingY";
        if (resizingStart()) {
          minResize = cloneDate(event.end);
          if (allDay) {
            copyTime(minResize, event.start);
            if (minResize >= event.end) {
              subtractDay(minResize);
            }
          } else {
            subtractDuration(minResize, $slotDuration());
            if (minResize < event.start) {
              minResize = event.start;
            }
            date = event.start;
          }
        } else {
          minResize = cloneDate(event.start);
          if (allDay) {
            copyTime(minResize, event.end);
            if (minResize <= event.start && !zeroDuration) {
              addDay(minResize);
            }
          } else {
            addDuration(minResize, $slotDuration());
            if (minResize > event.end) {
              minResize = event.end;
            }
            date = event.end;
            if (!zeroDuration) {
              date = subtractDuration(cloneDate(date), $slotDuration());
            }
          }
          if (zeroDuration && !allDay) {
            extraDuration = $slotDuration();
          }
        }
        move(jsEvent);
      }
    }
  }
  function select(jsEvent) {
    if (!action) {
      action = validJsEvent(jsEvent) ? $selectable() && !listView($view()) ? ACTION_SELECT : ACTION_CLICK : ACTION_NO_ACTION;
      if (complexAction()) {
        common(jsEvent);
        iClass = "selecting";
        selectStep = allDay ? createDuration({ day: 1 }) : $slotDuration();
        event = {
          allDay,
          start: date,
          end: addDuration(cloneDate(date), selectStep),
          resourceIds: resource ? [resource.id] : []
        };
        move(jsEvent);
      }
    }
  }
  function noAction() {
    if (!action) {
      action = ACTION_NO_ACTION;
    }
  }
  function common(jsEvent) {
    window.getSelection().removeAllRanges();
    fromX = toX = jsEvent.clientX;
    fromY = toY = jsEvent.clientY;
    let dayEl = getElementWithPayload(toX, toY);
    ({ allDay, date, resource } = getPayload(dayEl)(toX, toY));
    if (timelineView($view())) {
      bodyEl = clipEl = $_bodyEl();
    } else {
      bodyEl = ancestor(dayEl, resource ? 4 : 3);
      clipEl = ancestor(dayEl, resource && (dragging() || $datesAboveResources()) ? 2 : 1);
    }
    calcViewport();
    if (jsEvent.pointerType !== "mouse") {
      $.set(timer, setTimeout(
        () => {
          if (action) {
            interacting = true;
            move(jsEvent);
          }
        },
        (selecting() ? $selectLongPressDelay() : $eventLongPressDelay()) ?? $longPressDelay()
      ));
    }
  }
  function move(jsEvent) {
    if (interacting || jsEvent && jsEvent.pointerType === "mouse" && distance() >= (selecting() ? $selectMinDistance() : $eventDragMinDistance())) {
      interacting = true;
      unselect(jsEvent);
      $.store_set(_iClass, iClass);
      if (!$_iEvents()[0]) {
        if (selecting()) {
          createIEventSelect();
        } else {
          createIEvent(jsEvent, resizing() ? $eventResizeStart() : $eventDragStart());
        }
      }
      let payload = findPayload(findDayEl());
      if (payload) {
        let newAllDay;
        ({
          allDay: newAllDay,
          date: newDate,
          resource: newResource
        } = payload);
        if (newAllDay === allDay) {
          let candidate = copyIEventData({}, $_iEvents()[0]);
          let constraintFn = $resizeConstraint();
          delta = createDuration((newDate - date) / 1e3);
          if (resizingStart()) {
            candidate.start = addDuration(cloneDate(event.start), delta);
            if (candidate.start > minResize) {
              candidate.start = minResize;
              delta = createDuration((minResize - event.start) / 1e3);
            }
          } else {
            candidate.end = addDuration(cloneDate(event.end), delta);
            if (extraDuration) {
              addDuration(candidate.end, extraDuration);
            }
            if (resizing()) {
              if (candidate.end < minResize) {
                candidate.end = minResize;
                delta = createDuration((minResize - event.end) / 1e3);
              }
            } else if (selecting()) {
              if (candidate.end < event.end) {
                candidate.start = subtractDuration(candidate.end, selectStep);
                candidate.end = event.end;
              } else {
                candidate.start = event.start;
              }
              constraintFn = $selectConstraint();
            } else {
              candidate.start = addDuration(cloneDate(event.start), delta);
              if (resource) {
                candidate.resourceIds = event.resourceIds.filter((id) => id !== resource.id);
                candidate.resourceIds.push(newResource.id);
              }
              constraintFn = $dragConstraint();
            }
          }
          do {
            if (constraintFn !== void 0) {
              candidate = copyIEventData(cloneEvent(event), candidate);
              let result = constraintFn(selecting() ? createSelectCallbackInfo(candidate, jsEvent) : createCallbackInfo(candidate, event, jsEvent));
              if (result === false) {
                $.store_mutate(_iEvents, $.untrack($_iEvents)[0] = copyIEventData($_iEvents()[0], event), $.untrack($_iEvents));
                break;
              }
            }
            $.store_mutate(_iEvents, $.untrack($_iEvents)[0] = copyIEventData($_iEvents()[0], candidate), $.untrack($_iEvents));
          } while (0);
        }
      }
    }
    if ($dragScroll()) {
      let thresholdY = $slotHeight() * 2;
      let thresholdX = $slotWidth();
      animate(() => {
        if (bodyEl) {
          if (toY < thresholdY) {
            window.scrollBy(0, max(-10, (toY - thresholdY) / 3));
          }
          if (toY < bodyRect.top + thresholdY) {
            bodyEl.scrollTop += max(-10, (toY - bodyRect.top - thresholdY) / 3);
          }
          if (toY > window.innerHeight - thresholdY) {
            window.scrollBy(0, min(10, (toY - window.innerHeight + thresholdY) / 3));
          }
          if (toY > bodyRect.bottom - thresholdY) {
            bodyEl.scrollTop += min(10, (toY - bodyRect.bottom + thresholdY) / 3);
          }
          if (timelineView($view())) {
            if (toX < bodyRect.left + thresholdX) {
              bodyEl.scrollLeft += max(-10, (toX - bodyRect.left - thresholdX) / 3);
            }
            if (toX > bodyRect.right - thresholdX) {
              bodyEl.scrollLeft += min(10, (toX - bodyRect.right + thresholdX) / 3);
            }
          }
        }
      });
    }
  }
  function handleScroll() {
    if (complexAction()) {
      calcViewport();
      move();
    }
  }
  function handlePointerMove(jsEvent) {
    if (complexAction() && jsEvent.isPrimary) {
      toX = jsEvent.clientX;
      toY = jsEvent.clientY;
      move(jsEvent);
    }
  }
  function handlePointerUp(jsEvent) {
    if (selected && $unselectAuto() && !($unselectCancel() && jsEvent.target.closest($unselectCancel()))) {
      unselect(jsEvent);
    }
    if (action && jsEvent.isPrimary) {
      if (interacting) {
        if (selecting()) {
          selected = true;
          if (isFunction($selectFn())) {
            let info = createSelectCallbackInfo($_iEvents()[0], jsEvent);
            $selectFn()(info);
          }
        } else {
          event.display = display;
          let callback = resizing() ? $eventResizeStop() : $eventDragStop();
          if (isFunction(callback)) {
            callback({
              event: toEventWithLocalDates(event),
              jsEvent,
              view: toViewWithLocalDates($_view())
            });
          }
          let oldEvent = cloneEvent(event);
          updateEvent(event, $_iEvents()[0]);
          destroyIEvent();
          callback = resizing() ? $eventResize() : $eventDrop();
          if (isFunction(callback)) {
            let eventRef = event;
            let info = createCallbackInfo(event, oldEvent, jsEvent);
            callback(assign(info, {
              revert() {
                updateEvent(eventRef, oldEvent);
              }
            }));
          }
        }
      } else {
        if (clicking() || selecting()) {
          if (isFunction($dateClick()) && !noDateClick) {
            toX = jsEvent.clientX;
            toY = jsEvent.clientY;
            let dayEl = getElementWithPayload(toX, toY);
            if (dayEl) {
              let { allDay: allDay2, date: date2, resource: resource2 } = getPayload(dayEl)(toX, toY);
              $dateClick()({
                allDay: allDay2,
                date: toLocalDate(date2),
                dateStr: toISOString(date2),
                dayEl,
                jsEvent,
                view: toViewWithLocalDates($_view()),
                resource: resource2
              });
            }
          }
        }
      }
      interacting = false;
      action = fromX = fromY = toX = toY = event = display = date = newDate = resource = newResource = delta = extraDuration = allDay = $.store_set(_iClass, minResize = selectStep = margin = void 0);
      bodyEl = clipEl = bodyRect = clipRect = void 0;
      if ($.get(timer)) {
        clearTimeout($.get(timer));
        $.set(timer, void 0);
      }
    }
    noDateClick = false;
  }
  function findDayEl() {
    return getElementWithPayload(limit(toX, viewport[0], viewport[1]), limit(toY, viewport[2], viewport[3]));
  }
  function findPayload(dayEl) {
    if (dayEl) {
      let payload = getPayload(dayEl)(toX, toY);
      if (payload.disabled) {
        if (!$validRange().end || payload.date < $validRange().end) {
          return findPayload(dayEl.nextElementSibling);
        }
        if (!$validRange().start || payload.date > $validRange().start) {
          return findPayload(dayEl.previousElementSibling);
        }
      } else {
        return payload;
      }
    }
    return null;
  }
  function calcViewport() {
    bodyRect = rect(bodyEl);
    clipRect = rect(clipEl);
    viewport = [
      max(0, clipRect.left + (timelineView($view()) ? 1 : $_dayGrid() ? 0 : 8)),
      // left
      min(document.documentElement.clientWidth, clipRect.left + clipEl.clientWidth) - 2,
      // right
      max(0, bodyRect.top),
      // top
      min(document.documentElement.clientHeight, bodyRect.top + bodyEl.clientHeight) - 2
      // bottom
    ];
  }
  function createIEvent(jsEvent, callback) {
    if (isFunction(callback)) {
      callback({
        event: toEventWithLocalDates(event),
        jsEvent,
        view: toViewWithLocalDates($_view())
      });
    }
    display = event.display;
    event.display = "preview";
    $.store_mutate(_iEvents, $.untrack($_iEvents)[0] = cloneEvent(event), $.untrack($_iEvents));
    if (margin !== void 0) {
      $.store_mutate(_iEvents, $.untrack($_iEvents)[0]._margin = margin, $.untrack($_iEvents));
    }
    if (extraDuration) {
      addDuration($_iEvents()[0].end, extraDuration);
    }
    event.display = "ghost";
    $.store_set(_events, $_events());
  }
  function createIEventSelect() {
    $.store_mutate(
      _iEvents,
      $.untrack($_iEvents)[0] = {
        id: "{select}",
        allDay: event.allDay,
        start: event.start,
        title: "",
        display: "preview",
        extendedProps: {},
        backgroundColor: $selectBackgroundColor(),
        resourceIds: event.resourceIds,
        classNames: [],
        styles: []
      },
      $.untrack($_iEvents)
    );
  }
  function destroyIEvent() {
    $.store_mutate(_iEvents, $.untrack($_iEvents)[0] = null, $.untrack($_iEvents));
  }
  function copyIEventData(target, source) {
    target.start = source.start;
    target.end = source.end;
    target.resourceIds = source.resourceIds;
    return target;
  }
  function updateEvent(target, source) {
    copyIEventData(target, source);
    $.store_set(_events, $_events());
  }
  function createSelectCallbackInfo(event2, jsEvent) {
    let { start, end } = toEventWithLocalDates(event2);
    return {
      start,
      end,
      startStr: toISOString(event2.start),
      endStr: toISOString(event2.end),
      allDay,
      view: toViewWithLocalDates($_view()),
      resource,
      jsEvent
    };
  }
  function createCallbackInfo(event2, oldEvent, jsEvent) {
    let info;
    if (resizing()) {
      info = resizingStart() ? {
        startDelta: delta,
        endDelta: createDuration(0)
      } : {
        startDelta: createDuration(0),
        endDelta: delta
      };
    } else {
      info = {
        delta,
        oldResource: resource !== newResource ? resource : void 0,
        newResource: resource !== newResource ? newResource : void 0
      };
    }
    assign(info, {
      event: toEventWithLocalDates(event2),
      oldEvent: toEventWithLocalDates(oldEvent),
      view: toViewWithLocalDates($_view()),
      jsEvent
    });
    return info;
  }
  function distance() {
    return Math.sqrt(Math.pow(toX - fromX, 2) + Math.pow(toY - fromY, 2));
  }
  function dragging() {
    return action === ACTION_DRAG;
  }
  function resizing() {
    return action === ACTION_RESIZE_END || resizingStart();
  }
  function resizingStart() {
    return action === ACTION_RESIZE_START;
  }
  function clicking() {
    return action === ACTION_CLICK;
  }
  function selecting() {
    return action === ACTION_SELECT;
  }
  function complexAction() {
    return action && action < ACTION_CLICK;
  }
  function validJsEvent(jsEvent) {
    return jsEvent.isPrimary && (jsEvent.pointerType !== "mouse" || jsEvent.buttons & 1);
  }
  function unselect(jsEvent) {
    if (selected) {
      selected = false;
      destroyIEvent();
      if (isFunction($unselectFn())) {
        $unselectFn()({
          jsEvent,
          view: toViewWithLocalDates($_view())
        });
      }
    }
  }
  function noClick() {
    noDateClick = true;
  }
  _view.subscribe(unselect);
  function handleTouchStart(jsEvent) {
    if (complexAction()) {
      let target = jsEvent.target;
      let stops = [];
      let stop = () => runAll(stops);
      stops.push(listen(window, "touchmove", noop, { passive: false }));
      stops.push(listen(target, "touchmove", createPreventDefaultHandler(() => interacting)));
      stops.push(listen(target, "touchend", stop));
      stops.push(listen(target, "touchcancel", stop));
    }
  }
  function createPreventDefaultHandler(condition) {
    return (jsEvent) => {
      if (condition()) {
        jsEvent.preventDefault();
      }
    };
  }
  $.init();
  $.event("pointermove", $.window, handlePointerMove);
  $.event("pointerup", $.window, handlePointerUp);
  $.event("pointercancel", $.window, handlePointerUp);
  $.event("scroll", $.window, handleScroll);
  var event_handler = $.derived(() => createPreventDefaultHandler(complexAction));
  $.event("selectstart", $.window, function(...$$args) {
    var _a;
    (_a = $.get(event_handler)) == null ? void 0 : _a.apply(this, $$args);
  });
  var event_handler_1 = $.derived(() => createPreventDefaultHandler(() => $.get(timer)));
  $.event("contextmenu", $.window, function(...$$args) {
    var _a;
    (_a = $.get(event_handler_1)) == null ? void 0 : _a.apply(this, $$args);
  });
  $.event("touchstart", $.window, handleTouchStart, void 0, true);
  $.bind_prop($$props, "draggable", draggable);
  $.bind_prop($$props, "drag", drag);
  $.bind_prop($$props, "resize", resize);
  $.bind_prop($$props, "select", select);
  $.bind_prop($$props, "noAction", noAction);
  $.bind_prop($$props, "handleScroll", handleScroll);
  $.bind_prop($$props, "unselect", unselect);
  $.bind_prop($$props, "noClick", noClick);
  var $$pop = $.pop({
    draggable,
    drag,
    resize,
    select,
    noAction,
    handleScroll,
    unselect,
    noClick
  });
  $$cleanup();
  return $$pop;
}
function Pointer($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  let { _iEvents, slotDuration } = getContext("state");
  let x = 0, y = 0;
  $.user_effect(() => {
    if ($_iEvents()[0]) {
      removePointerEvent();
    }
  });
  function move() {
    let dayEl = getElementWithPayload(x, y);
    if (dayEl) {
      let { allDay, date, resource, disabled } = getPayload(dayEl)(x, y);
      if (!disabled) {
        let idx = allDay ? 2 : 1;
        if (!$_iEvents()[idx]) {
          createPointerEvent(idx);
        }
        $.store_mutate(_iEvents, $.untrack($_iEvents)[idx].start = date, $.untrack($_iEvents));
        $.store_mutate(_iEvents, $.untrack($_iEvents)[idx].end = addDuration(cloneDate(date), $slotDuration()), $.untrack($_iEvents));
        $.store_mutate(_iEvents, $.untrack($_iEvents)[idx].resourceIds = resource ? [resource.id] : [], $.untrack($_iEvents));
        return;
      }
    }
    removePointerEvent();
  }
  function handleScroll() {
    move();
  }
  function handlePointerMove(jsEvent) {
    if (validEvent(jsEvent)) {
      x = jsEvent.clientX;
      y = jsEvent.clientY;
      move();
    }
  }
  function createPointerEvent(idx) {
    $.store_mutate(
      _iEvents,
      $.untrack($_iEvents)[idx] = {
        id: "{pointer}",
        title: "",
        display: "pointer",
        extendedProps: {},
        backgroundColor: "transparent",
        classNames: [],
        styles: []
      },
      $.untrack($_iEvents)
    );
  }
  function removePointerEvent() {
    if ($_iEvents()[1]) {
      $.store_mutate(_iEvents, $.untrack($_iEvents)[1] = null, $.untrack($_iEvents));
    }
    if ($_iEvents()[2]) {
      $.store_mutate(_iEvents, $.untrack($_iEvents)[2] = null, $.untrack($_iEvents));
    }
  }
  function validEvent(jsEvent) {
    return jsEvent.isPrimary && jsEvent.pointerType === "mouse";
  }
  $.event("pointermove", $.window, handlePointerMove);
  $.event("scroll", $.window, handleScroll);
  var $$pop = $.pop({ handleScroll });
  $$cleanup();
  return $$pop;
}
var root_1$9 = $.from_html(`<div></div>`);
var root_2$6 = $.from_html(`<div></div>`);
var root$i = $.from_html(`<!> <!> <!>`, 1);
function Resizer($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $eventDurationEditable = () => $.store_get(eventDurationEditable, "$eventDurationEditable", $$stores);
  const $editable = () => $.store_get(editable, "$editable", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $eventResizableFromStart = () => $.store_get(eventResizableFromStart, "$eventResizableFromStart", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let forceDate = $.prop($$props, "forceDate", 3, void 0), forceMargin = $.prop($$props, "forceMargin", 3, void 0);
  let {
    theme,
    eventDurationEditable,
    eventResizableFromStart,
    editable,
    _interaction
  } = getContext("state");
  let event = $.derived(() => $$props.chunk.event);
  let display = $.derived(() => $$props.chunk.event.display);
  let resizable = $.derived(() => !bgEvent($.get(display)) && !helperEvent($.get(display)) && eventResizable($.get(event), $eventDurationEditable(), $editable()));
  function createResizeHandler(start) {
    return (jsEvent) => {
      var _a, _b;
      return $_interaction().action.resize($.get(event), jsEvent, start, $$props.axis, (_a = forceDate()) == null ? void 0 : _a(), (_b = forceMargin()) == null ? void 0 : _b(), $$props.chunk.zeroDuration);
    };
  }
  var fragment = root$i();
  var node = $.first_child(fragment);
  {
    var consequent = ($$anchor2) => {
      var div = root_1$9();
      var event_handler = $.derived(() => createResizeHandler(true));
      div.__pointerdown = function(...$$args) {
        var _a;
        (_a = $.get(event_handler)) == null ? void 0 : _a.apply(this, $$args);
      };
      $.template_effect(() => $.set_class(div, 1, `${$theme().resizer ?? ""} ${$theme().start ?? ""}`));
      $.append($$anchor2, div);
    };
    $.if(node, ($$render) => {
      if ($.get(resizable) && $eventResizableFromStart()) $$render(consequent);
    });
  }
  var node_1 = $.sibling(node, 2);
  $.snippet(node_1, () => $$props.children);
  var node_2 = $.sibling(node_1, 2);
  {
    var consequent_1 = ($$anchor2) => {
      var div_1 = root_2$6();
      var event_handler_1 = $.derived(() => createResizeHandler(false));
      div_1.__pointerdown = function(...$$args) {
        var _a;
        (_a = $.get(event_handler_1)) == null ? void 0 : _a.apply(this, $$args);
      };
      $.template_effect(() => $.set_class(div_1, 1, $theme().resizer));
      $.append($$anchor2, div_1);
    };
    $.if(node_2, ($$render) => {
      if ($.get(resizable)) $$render(consequent_1);
    });
  }
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
$.delegate(["pointerdown"]);
var root$h = $.from_html(`<!> <!>`, 1);
function Auxiliary($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $eventStartEditable = () => $.store_get(eventStartEditable, "$eventStartEditable", $$stores);
  const $editable = () => $.store_get(editable, "$editable", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $pointer = () => $.store_get(pointer, "$pointer", $$stores);
  let {
    theme,
    editable,
    eventStartEditable,
    pointer,
    _bodyEl,
    _interaction,
    _iClasses
  } = getContext("state");
  $.store_mutate(_interaction, $.untrack($_interaction).resizer = Resizer, $.untrack($_interaction));
  $.user_effect(() => {
    $theme();
    $eventStartEditable();
    $editable();
    $.store_set(_iClasses, (classNames, event) => {
      let { display } = event;
      return [
        ...classNames,
        helperEvent(display) ? [$theme()[display]] : !bgEvent(display) && eventDraggable(event, $eventStartEditable(), $editable()) ? [$theme().draggable] : []
      ];
    });
  });
  $.user_effect(() => {
    if ($_bodyEl()) {
      return listen($_bodyEl(), "scroll", bodyScrollHandler);
    }
  });
  function bodyScrollHandler() {
    var _a;
    for (let component of Object.values($_interaction())) {
      (_a = component == null ? void 0 : component.handleScroll) == null ? void 0 : _a.call(component);
    }
  }
  var fragment = root$h();
  var node = $.first_child(fragment);
  $.bind_this(Action(node, {}), ($$value) => $.store_mutate(_interaction, $.untrack($_interaction).action = $$value, $.untrack($_interaction)), () => {
    var _a;
    return (_a = $_interaction()) == null ? void 0 : _a.action;
  });
  var node_1 = $.sibling(node, 2);
  {
    var consequent = ($$anchor2) => {
      $.bind_this(Pointer($$anchor2, {}), ($$value) => $.store_mutate(_interaction, $.untrack($_interaction).pointer = $$value, $.untrack($_interaction)), () => {
        var _a;
        return (_a = $_interaction()) == null ? void 0 : _a.pointer;
      });
    };
    $.if(node_1, ($$render) => {
      if ($pointer()) $$render(consequent);
    });
  }
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
const index$3 = {
  createOptions(options) {
    options.dateClick = void 0;
    options.dragConstraint = void 0;
    options.dragScroll = true;
    options.editable = false;
    options.eventDragMinDistance = 5;
    options.eventDragStart = void 0;
    options.eventDragStop = void 0;
    options.eventDrop = void 0;
    options.eventDurationEditable = true;
    options.eventLongPressDelay = void 0;
    options.eventResizableFromStart = false;
    options.eventResizeStart = void 0;
    options.eventResizeStop = void 0;
    options.eventResize = void 0;
    options.eventStartEditable = true;
    options.longPressDelay = 1e3;
    options.pointer = false;
    options.resizeConstraint = void 0;
    options.select = void 0;
    options.selectBackgroundColor = void 0;
    options.selectConstraint = void 0;
    options.selectLongPressDelay = void 0;
    options.selectMinDistance = 5;
    options.unselect = void 0;
    options.unselectAuto = true;
    options.unselectCancel = "";
    options.theme.draggable = "ec-draggable";
    options.theme.ghost = "ec-ghost";
    options.theme.preview = "ec-preview";
    options.theme.pointer = "ec-pointer";
    options.theme.resizer = "ec-resizer";
    options.theme.start = "ec-start";
    options.theme.dragging = "ec-dragging";
    options.theme.resizingY = "ec-resizing-y";
    options.theme.resizingX = "ec-resizing-x";
    options.theme.selecting = "ec-selecting";
  },
  createStores(state) {
    state._auxiliary.update(($_auxiliary) => [...$_auxiliary, Auxiliary]);
  }
};
var root_1$8 = $.from_html(`<div></div> <!>`, 1);
function Event$3($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let { theme, _interaction } = getContext("state");
  let styles = $.derived(() => (style) => {
    delete style["background-color"];
    delete style["color"];
    return style;
  });
  const expression = $.derived(() => {
    var _a;
    return (_a = $_interaction().action) == null ? void 0 : _a.noAction;
  });
  {
    const body = ($$anchor2, defaultBody = $.noop, bgColor = $.noop, txtColor = $.noop) => {
      var fragment_1 = root_1$8();
      var div = $.first_child(fragment_1);
      let styles_1;
      var node = $.sibling(div, 2);
      $.snippet(node, defaultBody);
      $.template_effect(() => {
        $.set_class(div, 1, $theme().eventTag);
        styles_1 = $.set_style(div, "", styles_1, { "background-color": bgColor() });
      });
      $.append($$anchor2, fragment_1);
    };
    BaseEvent($$anchor, {
      get chunk() {
        return $$props.chunk;
      },
      get styles() {
        return $.get(styles);
      },
      get onpointerdown() {
        return $.get(expression);
      },
      body,
      $$slots: { body: true }
    });
  }
  $.pop();
  $$cleanup();
}
var root_1$7 = $.from_html(`<div role="listitem"><h4><time></time> <time></time></h4> <!></div>`);
function Day$3($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $highlightedDates = () => $.store_get(highlightedDates, "$highlightedDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $filterEventsWithResources = () => $.store_get(filterEventsWithResources, "$filterEventsWithResources", $$stores);
  const $resources = () => $.store_get(resources, "$resources", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $_intlListDay = () => $.store_get(_intlListDay, "$_intlListDay", $$stores);
  const $_intlListDaySide = () => $.store_get(_intlListDaySide, "$_intlListDaySide", $$stores);
  let {
    _filteredEvents,
    _interaction,
    _intlListDay,
    _intlListDaySide,
    _today,
    resources,
    filterEventsWithResources,
    highlightedDates,
    theme,
    validRange
  } = getContext("state");
  let el = $.state(void 0);
  let isToday = $.derived(() => datesEqual($$props.date, $_today()));
  let highlight = $.derived(() => $highlightedDates().some((d) => datesEqual(d, $$props.date)));
  let disabled = $.derived(() => outsideRange($$props.date, $validRange()));
  let datetime = $.derived(() => toISOString($$props.date, 10));
  let chunks = $.derived(() => {
    let chunks2 = [];
    if (!$.get(disabled)) {
      let start = $$props.date;
      let end = addDay(cloneDate($$props.date));
      for (let event of $_filteredEvents()) {
        if (!bgEvent(event.display) && eventIntersects(event, start, end, $filterEventsWithResources() ? $resources() : void 0)) {
          let chunk = createEventChunk(event, start, end);
          chunks2.push(chunk);
        }
      }
      sortEventChunks(chunks2);
    }
    return chunks2;
  });
  $.user_effect(() => {
    if ($.get(el)) {
      setPayload($.get(el), () => ({
        allDay: true,
        date: $$props.date,
        resource: void 0,
        dayEl: $.get(el),
        disabled: $.get(disabled)
      }));
    }
  });
  var fragment = $.comment();
  var node = $.first_child(fragment);
  {
    var consequent = ($$anchor2) => {
      var div = root_1$7();
      div.__pointerdown = function(...$$args) {
        var _a, _b;
        (_b = (_a = $_interaction().action) == null ? void 0 : _a.select) == null ? void 0 : _b.apply(this, $$args);
      };
      var h4 = $.child(div);
      var time = $.child(h4);
      $.action(time, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlListDay().format($$props.date));
      var time_1 = $.sibling(time, 2);
      $.action(time_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlListDaySide().format($$props.date));
      $.reset(h4);
      var node_1 = $.sibling(h4, 2);
      $.each(node_1, 17, () => $.get(chunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event$3($$anchor3, {
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      $.reset(div);
      $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
      $.template_effect(
        ($0) => {
          $.set_class(div, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$.get(isToday) ? " " + $theme().today : ""}${$.get(highlight) ? " " + $theme().highlight : ""}`);
          $.set_class(h4, 1, $theme().dayHead);
          $.set_attribute(time, "datetime", $.get(datetime));
          $.set_class(time_1, 1, $theme().daySide);
          $.set_attribute(time_1, "datetime", $.get(datetime));
        },
        [
          () => {
            var _a;
            return (_a = $theme().weekdays) == null ? void 0 : _a[$$props.date.getUTCDay()];
          }
        ]
      );
      $.append($$anchor2, div);
    };
    $.if(node, ($$render) => {
      if ($.get(chunks).length) $$render(consequent);
    });
  }
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
$.delegate(["pointerdown"]);
function onclick$1(jsEvent, $noEventsClick, noEventsClick, $_view, _view) {
  if (isFunction($noEventsClick())) {
    $noEventsClick()({
      jsEvent,
      view: toViewWithLocalDates($_view())
    });
  }
}
var root_1$6 = $.from_html(`<div></div>`);
var root$g = $.from_html(`<div><div><!></div></div>`);
function Body$2($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $noEventsContent = () => $.store_get(noEventsContent, "$noEventsContent", $$stores);
  const $noEventsClick = () => $.store_get(noEventsClick, "$noEventsClick", $$stores);
  const $_view = () => $.store_get(_view, "$_view", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let {
    _bodyEl,
    _filteredEvents,
    _view,
    _viewDates,
    noEventsClick,
    noEventsContent,
    theme
  } = getContext("state");
  let noEvents = $.derived(() => {
    let noEvents2 = true;
    if ($_viewDates().length) {
      let start = $_viewDates()[0];
      let end = addDay(cloneDate($_viewDates().at(-1)));
      for (let event of $_filteredEvents()) {
        if (!bgEvent(event.display) && event.start < end && event.end > start) {
          noEvents2 = false;
          break;
        }
      }
    }
    return noEvents2;
  });
  let content = $.derived(() => isFunction($noEventsContent()) ? $noEventsContent()() : $noEventsContent());
  var div = root$g();
  var div_1 = $.child(div);
  var node = $.child(div_1);
  {
    var consequent = ($$anchor2) => {
      var div_2 = root_1$6();
      div_2.__click = [
        onclick$1,
        $noEventsClick,
        noEventsClick,
        $_view,
        _view
      ];
      $.action(div_2, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(content));
      $.template_effect(() => $.set_class(div_2, 1, $theme().noEvents));
      $.append($$anchor2, div_2);
    };
    var alternate = ($$anchor2) => {
      var fragment = $.comment();
      var node_1 = $.first_child(fragment);
      $.each(node_1, 1, $_viewDates, $.index, ($$anchor3, date) => {
        Day$3($$anchor3, {
          get date() {
            return $.get(date);
          }
        });
      });
      $.append($$anchor2, fragment);
    };
    $.if(node, ($$render) => {
      if ($.get(noEvents)) $$render(consequent);
      else $$render(alternate, false);
    });
  }
  $.reset(div_1);
  $.reset(div);
  $.bind_this(div, ($$value) => $.store_set(_bodyEl, $$value), () => $_bodyEl());
  $.template_effect(() => {
    $.set_class(div, 1, $theme().body);
    $.set_class(div_1, 1, $theme().content);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
$.delegate(["click"]);
function View$3($$anchor) {
  Body$2($$anchor, {});
}
const index$2 = {
  createOptions(options) {
    options.buttonText.listDay = "list";
    options.buttonText.listWeek = "list";
    options.buttonText.listMonth = "list";
    options.buttonText.listYear = "list";
    options.listDayFormat = { weekday: "long" };
    options.listDaySideFormat = { year: "numeric", month: "long", day: "numeric" };
    options.noEventsClick = void 0;
    options.noEventsContent = "No events";
    options.theme.daySide = "ec-day-side";
    options.theme.eventTag = "ec-event-tag";
    options.theme.noEvents = "ec-no-events";
    options.view = "listWeek";
    options.views.listDay = {
      buttonText: btnTextDay,
      component: View$3,
      duration: { days: 1 },
      theme: themeView("ec-list ec-day-view")
    };
    options.views.listWeek = {
      buttonText: btnTextWeek,
      component: View$3,
      duration: { weeks: 1 },
      theme: themeView("ec-list ec-week-view")
    };
    options.views.listMonth = {
      buttonText: btnTextMonth,
      component: View$3,
      duration: { months: 1 },
      theme: themeView("ec-list ec-month-view")
    };
    options.views.listYear = {
      buttonText: btnTextYear,
      component: View$3,
      duration: { years: 1 },
      theme: themeView("ec-list ec-year-view")
    };
  },
  createStores(state) {
    state._intlListDay = intl(state.locale, state.listDayFormat);
    state._intlListDaySide = intl(state.locale, state.listDaySideFormat);
  }
};
function times(state) {
  return derived(
    [state.slotDuration, state.slotLabelInterval, state._slotTimeLimits, state._intlSlotLabel],
    (args) => createTimes(setMidnight(createDate()), ...args)
  );
}
function slotTimeLimits(state) {
  return derived(
    [state.slotMinTime, state.slotMaxTime, state.flexibleSlotTimeLimits, state._viewDates, state._filteredEvents],
    (args) => createSlotTimeLimits(...args)
  );
}
function groupEventChunks(chunks) {
  if (!chunks.length) {
    return;
  }
  sortEventChunks(chunks);
  let group = {
    columns: [],
    end: chunks[0].end
  };
  for (let chunk of chunks) {
    let c = 0;
    if (chunk.start < group.end) {
      for (; c < group.columns.length; ++c) {
        if (group.columns[c].at(-1).end <= chunk.start) {
          break;
        }
      }
      if (chunk.end > group.end) {
        group.end = chunk.end;
      }
    } else {
      group = {
        columns: [],
        end: chunk.end
      };
    }
    if (group.columns.length < c + 1) {
      group.columns.push([]);
    }
    group.columns[c].push(chunk);
    chunk.group = group;
    chunk.column = c;
  }
}
function createAllDayContent(allDayContent) {
  let text = "all-day";
  let content;
  if (allDayContent) {
    content = isFunction(allDayContent) ? allDayContent({ text }) : allDayContent;
    if (typeof content === "string") {
      content = { html: content };
    }
  } else {
    content = {
      html: text
    };
  }
  return content;
}
var root_1$5 = $.from_html(`<time></time>`);
var root$f = $.from_html(`<div><div></div> <!></div> <div role="row"><div><!></div> <!></div>`, 1);
function Section($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $allDayContent = () => $.store_get(allDayContent, "$allDayContent", $$stores);
  const $slotLabelInterval = () => $.store_get(slotLabelInterval, "$slotLabelInterval", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_times = () => $.store_get(_times, "$_times", $$stores);
  let {
    allDayContent,
    slotLabelInterval,
    theme,
    _times
  } = getContext("state");
  let allDayText = $.derived(() => createAllDayContent($allDayContent()));
  let showAllTimes = $.derived(() => $slotLabelInterval() && $slotLabelInterval().seconds <= 0);
  var fragment = root$f();
  var div = $.first_child(fragment);
  var div_1 = $.child(div);
  $.action(div_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(allDayText));
  var node = $.sibling(div_1, 2);
  $.each(node, 1, $_times, $.index, ($$anchor2, time, i) => {
    var time_1 = root_1$5();
    $.action(time_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(time)[1]);
    $.template_effect(() => {
      $.set_class(time_1, 1, `${$theme().time ?? ""}${(i || $.get(showAllTimes)) && $.get(time)[2] ? "" : " " + $theme().minor}`);
      $.set_attribute(time_1, "datetime", $.get(time)[0]);
    });
    $.append($$anchor2, time_1);
  });
  $.reset(div);
  var div_2 = $.sibling(div, 2);
  var div_3 = $.child(div_2);
  var node_1 = $.child(div_3);
  {
    var consequent = ($$anchor2) => {
      var fragment_1 = $.comment();
      var node_2 = $.first_child(fragment_1);
      $.snippet(node_2, () => $$props.lines);
      $.append($$anchor2, fragment_1);
    };
    $.if(node_1, ($$render) => {
      if ($$props.lines) $$render(consequent);
    });
  }
  $.reset(div_3);
  var node_3 = $.sibling(div_3, 2);
  {
    var consequent_1 = ($$anchor2) => {
      var fragment_2 = $.comment();
      var node_4 = $.first_child(fragment_2);
      $.snippet(node_4, () => $$props.children);
      $.append($$anchor2, fragment_2);
    };
    $.if(node_3, ($$render) => {
      if ($$props.children) $$render(consequent_1);
    });
  }
  $.reset(div_2);
  $.template_effect(() => {
    $.set_class(div, 1, $theme().sidebar);
    $.set_class(div_1, 1, $theme().sidebarTitle);
    $.set_class(div_2, 1, $theme().days);
    $.set_class(div_3, 1, $theme().lines);
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
var root_2$5 = $.from_html(`<div></div>`);
var root$e = $.from_html(`<div><div><!></div></div>`);
function Body$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $scrollTime = () => $.store_get(scrollTime, "$scrollTime", $$stores);
  const $_slotTimeLimits = () => $.store_get(_slotTimeLimits, "$_slotTimeLimits", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $slotHeight = () => $.store_get(slotHeight, "$slotHeight", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_times = () => $.store_get(_times, "$_times", $$stores);
  let {
    _bodyEl,
    _viewDates,
    _slotTimeLimits,
    _times,
    _recheckScrollable,
    scrollTime,
    slotDuration,
    slotHeight,
    theme
  } = getContext("state");
  let el = $.state(void 0);
  $.user_effect(() => {
    $.store_set(_bodyEl, $.get(el));
  });
  $.user_effect(() => {
    $_viewDates();
    $scrollTime();
    untrack(scrollToTime);
  });
  function scrollToTime() {
    $.get(el).scrollTop = (($scrollTime().seconds - $_slotTimeLimits().min.seconds) / $slotDuration().seconds - 0.5) * $slotHeight();
  }
  var div = root$e();
  var div_1 = $.child(div);
  var node = $.child(div_1);
  {
    const lines = ($$anchor2) => {
      var fragment = $.comment();
      var node_1 = $.first_child(fragment);
      $.each(node_1, 1, $_times, $.index, ($$anchor3, time) => {
        var div_2 = root_2$5();
        $.template_effect(() => $.set_class(div_2, 1, `${$theme().line ?? ""}${$.get(time)[2] ? "" : " " + $theme().minor}`));
        $.append($$anchor3, div_2);
      });
      $.append($$anchor2, fragment);
    };
    Section(node, {
      get children() {
        return $$props.children;
      },
      lines,
      $$slots: { lines: true }
    });
  }
  $.reset(div_1);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.action(div, ($$node, $$action_arg) => observeResize == null ? void 0 : observeResize($$node, $$action_arg), () => () => $.store_set(_recheckScrollable, true));
  $.template_effect(() => {
    $.set_class(div, 1, $theme().body);
    $.set_class(div_1, 1, $theme().content);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
function Event$2($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $_slotTimeLimits = () => $.store_get(_slotTimeLimits, "$_slotTimeLimits", $$stores);
  const $slotHeight = () => $.store_get(slotHeight, "$slotHeight", $$stores);
  const $slotEventOverlap = () => $.store_get(slotEventOverlap, "$slotEventOverlap", $$stores);
  let {
    slotEventOverlap,
    slotDuration,
    slotHeight,
    _slotTimeLimits
  } = getContext("state");
  let display = $.derived(() => $$props.chunk.event.display);
  let styles = $.derived(() => (style) => {
    let step = $slotDuration().seconds;
    let offset = $_slotTimeLimits().min.seconds;
    let start = ($$props.chunk.start - $$props.date) / 1e3;
    let end = ($$props.chunk.end - $$props.date) / 1e3;
    let top = (start - offset) / step * $slotHeight();
    let height2 = (end - start) / step * $slotHeight() || $slotHeight();
    let maxHeight = ($_slotTimeLimits().max.seconds - start) / step * $slotHeight();
    style["top"] = `${top}px`;
    style["min-height"] = `${height2}px`;
    style["height"] = `${height2}px`;
    style["max-height"] = `${maxHeight}px`;
    if (!bgEvent($.get(display)) && !helperEvent($.get(display)) || ghostEvent($.get(display))) {
      style["z-index"] = `${$$props.chunk.column + 1}`;
      style["left"] = `${100 / $$props.chunk.group.columns.length * $$props.chunk.column}%`;
      style["width"] = `${100 / $$props.chunk.group.columns.length * ($slotEventOverlap() ? 0.5 * (1 + $$props.chunk.group.columns.length - $$props.chunk.column) : 1)}%`;
    }
    return style;
  });
  InteractableEvent($$anchor, {
    get chunk() {
      return $$props.chunk;
    },
    get styles() {
      return $.get(styles);
    },
    axis: "y"
  });
  $.pop();
  $$cleanup();
}
var root$d = $.from_html(`<div></div>`);
function NowIndicator$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_now = () => $.store_get(_now, "$_now", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $_slotTimeLimits = () => $.store_get(_slotTimeLimits, "$_slotTimeLimits", $$stores);
  const $slotHeight = () => $.store_get(slotHeight, "$slotHeight", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let {
    slotDuration,
    slotHeight,
    theme,
    _now,
    _today,
    _slotTimeLimits
  } = getContext("state");
  let start = $.derived(() => ($_now() - $_today()) / 1e3);
  let top = $.derived(() => {
    let step = $slotDuration().seconds;
    let offset = $_slotTimeLimits().min.seconds;
    return ($.get(start) - offset) / step * $slotHeight();
  });
  var div = root$d();
  $.template_effect(() => {
    $.set_class(div, 1, $theme().nowIndicator);
    $.set_style(div, `top:${$.get(top) ?? ""}px`);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
var root_3$4 = $.from_html(`<!> <!> <!>`, 1);
var root$c = $.from_html(`<div role="cell"><div><!></div> <div><!></div> <div><!></div></div>`);
function Day$2($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $highlightedDates = () => $.store_get(highlightedDates, "$highlightedDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_slotTimeLimits = () => $.store_get(_slotTimeLimits, "$_slotTimeLimits", $$stores);
  const $filterEventsWithResources = () => $.store_get(filterEventsWithResources, "$filterEventsWithResources", $$stores);
  const $resources = () => $.store_get(resources, "$resources", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $slotHeight = () => $.store_get(slotHeight, "$slotHeight", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  const $nowIndicator = () => $.store_get(nowIndicator, "$nowIndicator", $$stores);
  let resource = $.prop($$props, "resource", 3, void 0);
  let {
    _filteredEvents,
    _iEvents,
    highlightedDates,
    nowIndicator,
    slotDuration,
    slotHeight,
    filterEventsWithResources,
    theme,
    resources,
    validRange,
    _interaction,
    _today,
    _slotTimeLimits
  } = getContext("state");
  let el = $.state(void 0);
  let isToday = $.derived(() => datesEqual($$props.date, $_today()));
  let highlight = $.derived(() => $highlightedDates().some((d) => datesEqual(d, $$props.date)));
  let disabled = $.derived(() => outsideRange($$props.date, $validRange()));
  let start = $.derived(() => addDuration(cloneDate($$props.date), $_slotTimeLimits().min));
  let end = $.derived(() => addDuration(cloneDate($$props.date), $_slotTimeLimits().max));
  let resourceFilter = $.derived(() => resource() ?? ($filterEventsWithResources() ? $resources() : void 0));
  let $$d = $.derived(() => {
    if ($.get(disabled)) {
      return [[], []];
    }
    let chunks2 = [];
    let bgChunks2 = [];
    for (let event of $_filteredEvents()) {
      if ((!event.allDay || bgEvent(event.display)) && eventIntersects(event, $.get(start), $.get(end), $.get(resourceFilter))) {
        let chunk = createEventChunk(event, $.get(start), $.get(end));
        switch (event.display) {
          case "background":
            bgChunks2.push(chunk);
            break;
          default:
            chunks2.push(chunk);
        }
      }
    }
    groupEventChunks(chunks2);
    return [chunks2, bgChunks2];
  }), $$array = $.derived(() => $.to_array($.get($$d), 2)), chunks = $.derived(() => $.get($$array)[0]), bgChunks = $.derived(() => $.get($$array)[1]);
  let iChunks = $.derived(() => {
    if ($.get(disabled)) {
      return [];
    }
    return $_iEvents().map((event) => event && eventIntersects(event, $.get(start), $.get(end), resource()) ? createEventChunk(event, $.get(start), $.get(end)) : null);
  });
  function dateFromPoint(x, y) {
    y -= rect($.get(el)).top;
    return {
      allDay: false,
      date: addDuration(addDuration(cloneDate($$props.date), $_slotTimeLimits().min), $slotDuration(), floor(y / $slotHeight())),
      resource: resource(),
      dayEl: $.get(el),
      disabled: $.get(disabled)
    };
  }
  onMount(() => {
    setPayload($.get(el), dateFromPoint);
  });
  var div = root$c();
  div.__pointerdown = function(...$$args) {
    var _a, _b;
    (_b = !$.get(disabled) ? (_a = $_interaction().action) == null ? void 0 : _a.select : void 0) == null ? void 0 : _b.apply(this, $$args);
  };
  var div_1 = $.child(div);
  var node = $.child(div_1);
  {
    var consequent = ($$anchor2) => {
      var fragment = $.comment();
      var node_1 = $.first_child(fragment);
      $.each(node_1, 17, () => $.get(bgChunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event$2($$anchor3, {
          get date() {
            return $$props.date;
          },
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      $.append($$anchor2, fragment);
    };
    $.if(node, ($$render) => {
      if (!$.get(disabled)) $$render(consequent);
    });
  }
  $.reset(div_1);
  var div_2 = $.sibling(div_1, 2);
  var node_2 = $.child(div_2);
  {
    var consequent_3 = ($$anchor2) => {
      var fragment_2 = root_3$4();
      var node_3 = $.first_child(fragment_2);
      {
        var consequent_1 = ($$anchor3) => {
          Event$2($$anchor3, {
            get date() {
              return $$props.date;
            },
            get chunk() {
              return $.get(iChunks)[1];
            }
          });
        };
        $.if(node_3, ($$render) => {
          if ($.get(iChunks)[1]) $$render(consequent_1);
        });
      }
      var node_4 = $.sibling(node_3, 2);
      $.each(node_4, 17, () => $.get(chunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event$2($$anchor3, {
          get date() {
            return $$props.date;
          },
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      var node_5 = $.sibling(node_4, 2);
      {
        var consequent_2 = ($$anchor3) => {
          Event$2($$anchor3, {
            get date() {
              return $$props.date;
            },
            get chunk() {
              return $.get(iChunks)[0];
            }
          });
        };
        $.if(node_5, ($$render) => {
          if ($.get(iChunks)[0] && !$.get(iChunks)[0].event.allDay) $$render(consequent_2);
        });
      }
      $.append($$anchor2, fragment_2);
    };
    $.if(node_2, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_3);
    });
  }
  $.reset(div_2);
  var div_3 = $.sibling(div_2, 2);
  var node_6 = $.child(div_3);
  {
    var consequent_4 = ($$anchor2) => {
      NowIndicator$1($$anchor2, {});
    };
    $.if(node_6, ($$render) => {
      if ($nowIndicator() && $.get(isToday) && !$.get(disabled)) $$render(consequent_4);
    });
  }
  $.reset(div_3);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.template_effect(
    ($0) => {
      $.set_class(div, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$.get(isToday) ? " " + $theme().today : ""}${$.get(highlight) ? " " + $theme().highlight : ""}${$.get(disabled) ? " " + $theme().disabled : ""}`);
      $.set_class(div_1, 1, $theme().bgEvents);
      $.set_class(div_2, 1, $theme().events);
      $.set_class(div_3, 1, $theme().extra);
    },
    [
      () => {
        var _a;
        return (_a = $theme().weekdays) == null ? void 0 : _a[$$props.date.getUTCDay()];
      }
    ]
  );
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
$.delegate(["pointerdown"]);
function Event$1($$anchor, $$props) {
  $.push($$props, true);
  let longChunks = $.prop($$props, "longChunks", 19, () => ({}));
  let el = $.state(void 0);
  let margin = $.state(1);
  let event = $.derived(() => $$props.chunk.event);
  let display = $.derived(() => $$props.chunk.event.display);
  let styles = $.derived(() => (style) => {
    if (bgEvent($.get(display))) {
      style["width"] = `calc(${$$props.chunk.days * 100}% + ${$$props.chunk.days - 1}px)`;
    } else {
      style["width"] = `calc(${$$props.chunk.days * 100}% + ${($$props.chunk.days - 1) * 7}px)`;
      style["margin-top"] = `${$.get(event)._margin ?? $.get(margin)}px`;
    }
    return style;
  });
  function reposition() {
    if (!$.get(el)) {
      return;
    }
    $.set(margin, repositionEvent$1($$props.chunk, longChunks(), height($.get(el))), true);
  }
  InteractableEvent($$anchor, {
    get chunk() {
      return $$props.chunk;
    },
    get styles() {
      return $.get(styles);
    },
    axis: "x",
    forceMargin: () => rect($.get(el)).top - rect(ancestor($.get(el), 1)).top,
    get el() {
      return $.get(el);
    },
    set el($$value) {
      $.set(el, $$value, true);
    }
  });
  return $.pop({ reposition });
}
var root_3$3 = $.from_html(`<div><!></div>`);
var root$b = $.from_html(`<div role="cell"><div><!></div> <!> <div><!></div></div>`);
function Day$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $highlightedDates = () => $.store_get(highlightedDates, "$highlightedDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  let iChunks = $.prop($$props, "iChunks", 19, () => []), resource = $.prop($$props, "resource", 3, void 0);
  let {
    highlightedDates,
    theme,
    validRange,
    _interaction,
    _today
  } = getContext("state");
  let el = $.state(void 0);
  let refs = [];
  let dayChunks = $.derived(() => $$props.chunks.filter((chunk) => datesEqual(chunk.date, $$props.date)));
  let dayBgChunks = $.derived(() => $$props.bgChunks.filter((bgChunk) => datesEqual(bgChunk.date, $$props.date)));
  let isToday = $.derived(() => datesEqual($$props.date, $_today()));
  let highlight = $.derived(() => $highlightedDates().some((d) => datesEqual(d, $$props.date)));
  let disabled = $.derived(() => outsideRange($$props.date, $validRange()));
  onMount(() => {
    setPayload($.get(el), () => ({
      allDay: true,
      date: $$props.date,
      resource: resource(),
      dayEl: $.get(el),
      disabled: $.get(disabled)
    }));
  });
  function reposition() {
    if (!$.get(disabled)) {
      runReposition(refs, $.get(dayChunks));
    }
  }
  var div = root$b();
  div.__pointerdown = function(...$$args) {
    var _a, _b;
    (_b = !$.get(disabled) ? (_a = $_interaction().action) == null ? void 0 : _a.select : void 0) == null ? void 0 : _b.apply(this, $$args);
  };
  var div_1 = $.child(div);
  var node = $.child(div_1);
  {
    var consequent = ($$anchor2) => {
      var fragment = $.comment();
      var node_1 = $.first_child(fragment);
      $.each(node_1, 17, () => $.get(dayBgChunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event$1($$anchor3, {
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      $.append($$anchor2, fragment);
    };
    $.if(node, ($$render) => {
      if (!$.get(disabled)) $$render(consequent);
    });
  }
  $.reset(div_1);
  var node_2 = $.sibling(div_1, 2);
  {
    var consequent_1 = ($$anchor2) => {
      var div_2 = root_3$3();
      var node_3 = $.child(div_2);
      Event$1(node_3, {
        get chunk() {
          return iChunks()[0];
        }
      });
      $.reset(div_2);
      $.template_effect(() => $.set_class(div_2, 1, `${$theme().events ?? ""} ${$theme().preview ?? ""}`));
      $.append($$anchor2, div_2);
    };
    $.if(node_2, ($$render) => {
      if (iChunks()[0] && datesEqual(iChunks()[0].date, $$props.date) && !$.get(disabled)) $$render(consequent_1);
    });
  }
  var div_3 = $.sibling(node_2, 2);
  var node_4 = $.child(div_3);
  {
    var consequent_2 = ($$anchor2) => {
      var fragment_2 = $.comment();
      var node_5 = $.first_child(fragment_2);
      $.each(node_5, 19, () => $.get(dayChunks), (chunk) => chunk.event, ($$anchor3, chunk, i) => {
        $.bind_this(
          Event$1($$anchor3, {
            get chunk() {
              return $.get(chunk);
            },
            get longChunks() {
              return $$props.longChunks;
            }
          }),
          ($$value, i2) => refs[i2] = $$value,
          (i2) => refs == null ? void 0 : refs[i2],
          () => [$.get(i)]
        );
      });
      $.append($$anchor2, fragment_2);
    };
    $.if(node_4, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_2);
    });
  }
  $.reset(div_3);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.template_effect(
    ($0) => {
      $.set_class(div, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$.get(isToday) ? " " + $theme().today : ""}${$.get(highlight) ? " " + $theme().highlight : ""}${$.get(disabled) ? " " + $theme().disabled : ""}`);
      $.set_class(div_1, 1, $theme().bgEvents);
      $.set_class(div_3, 1, $theme().events);
    },
    [
      () => {
        var _a;
        return (_a = $theme().weekdays) == null ? void 0 : _a[$$props.date.getUTCDay()];
      }
    ]
  );
  $.append($$anchor, div);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
$.delegate(["pointerdown"]);
function Week($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $filterEventsWithResources = () => $.store_get(filterEventsWithResources, "$filterEventsWithResources", $$stores);
  const $resources = () => $.store_get(resources, "$resources", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $hiddenDays = () => $.store_get(hiddenDays, "$hiddenDays", $$stores);
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  let resource = $.prop($$props, "resource", 3, void 0);
  let {
    _filteredEvents,
    _iEvents,
    hiddenDays,
    resources,
    filterEventsWithResources,
    validRange
  } = getContext("state");
  let refs = [];
  let start = $.derived(() => limitToRange($$props.dates[0], $validRange()));
  let end = $.derived(() => addDay(cloneDate(limitToRange($$props.dates.at(-1), $validRange()))));
  let resourceFilter = $.derived(() => resource() ?? ($filterEventsWithResources() ? $resources() : void 0));
  let $$d = $.derived(() => {
    let chunks2 = [];
    let bgChunks2 = [];
    for (let event of $_filteredEvents()) {
      if (event.allDay && eventIntersects(event, $.get(start), $.get(end), $.get(resourceFilter))) {
        let chunk = createEventChunk(event, $.get(start), $.get(end));
        if (bgEvent(event.display)) {
          bgChunks2.push(chunk);
        } else {
          chunks2.push(chunk);
        }
      }
    }
    prepareEventChunks$1(bgChunks2, $hiddenDays());
    let longChunks2 = prepareEventChunks$1(chunks2, $hiddenDays());
    return [chunks2, bgChunks2, longChunks2];
  }), $$array = $.derived(() => $.to_array($.get($$d), 3)), chunks = $.derived(() => $.get($$array)[0]), bgChunks = $.derived(() => $.get($$array)[1]), longChunks = $.derived(() => $.get($$array)[2]);
  function reposition() {
    runReposition(refs, $$props.dates);
  }
  $.user_effect(() => {
    $.get(chunks);
    untrack(reposition);
  });
  let iChunks = $.derived(() => $_iEvents().map((event) => {
    let chunk;
    if (event && event.allDay && eventIntersects(event, $.get(start), $.get(end), resource())) {
      chunk = createEventChunk(event, $.get(start), $.get(end));
      prepareEventChunks$1([chunk], $hiddenDays());
    } else {
      chunk = null;
    }
    return chunk;
  }));
  var fragment = $.comment();
  $.event("resize", $.window, reposition);
  var node = $.first_child(fragment);
  $.each(node, 17, () => $$props.dates, $.index, ($$anchor2, date, i) => {
    $.bind_this(
      Day$1($$anchor2, {
        get date() {
          return $.get(date);
        },
        get chunks() {
          return $.get(chunks);
        },
        get bgChunks() {
          return $.get(bgChunks);
        },
        get longChunks() {
          return $.get(longChunks);
        },
        get iChunks() {
          return $.get(iChunks);
        },
        get resource() {
          return resource();
        }
      }),
      ($$value, i2) => refs[i2] = $$value,
      (i2) => refs == null ? void 0 : refs[i2],
      () => [i]
    );
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
var root_2$4 = $.from_html(`<div role="columnheader"><time></time></div>`);
var root_3$2 = $.from_html(`<div><div><!> <div></div></div></div>`);
var root$a = $.from_html(`<div><!> <div></div></div> <!> <!>`, 1);
function View$2($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $_intlDayHeaderAL = () => $.store_get(_intlDayHeaderAL, "$_intlDayHeaderAL", $$stores);
  const $_intlDayHeader = () => $.store_get(_intlDayHeader, "$_intlDayHeader", $$stores);
  const $allDaySlot = () => $.store_get(allDaySlot, "$allDaySlot", $$stores);
  let {
    _viewDates,
    _intlDayHeader,
    _intlDayHeaderAL,
    _today,
    allDaySlot,
    theme
  } = getContext("state");
  $.init();
  var fragment = root$a();
  var div = $.first_child(fragment);
  var node = $.child(div);
  Section(node, {
    children: ($$anchor2, $$slotProps) => {
      var fragment_1 = $.comment();
      var node_1 = $.first_child(fragment_1);
      $.each(node_1, 1, $_viewDates, $.index, ($$anchor3, date) => {
        var div_1 = root_2$4();
        var time = $.child(div_1);
        $.action(time, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(date)));
        $.reset(div_1);
        $.template_effect(
          ($0, $1, $2, $3) => {
            $.set_class(div_1, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$1 ?? ""}`);
            $.set_attribute(time, "datetime", $2);
            $.set_attribute(time, "aria-label", $3);
          },
          [
            () => {
              var _a;
              return (_a = $theme().weekdays) == null ? void 0 : _a[$.get(date).getUTCDay()];
            },
            () => datesEqual($.get(date), $_today()) ? " " + $theme().today : "",
            () => toISOString($.get(date), 10),
            () => $_intlDayHeaderAL().format($.get(date))
          ],
          $.derived_safe_equal
        );
        $.append($$anchor3, div_1);
      });
      $.append($$anchor2, fragment_1);
    },
    $$slots: { default: true }
  });
  var div_2 = $.sibling(node, 2);
  $.reset(div);
  var node_2 = $.sibling(div, 2);
  {
    var consequent = ($$anchor2) => {
      var div_3 = root_3$2();
      var div_4 = $.child(div_3);
      var node_3 = $.child(div_4);
      Section(node_3, {
        children: ($$anchor3, $$slotProps) => {
          Week($$anchor3, {
            get dates() {
              return $_viewDates();
            }
          });
        },
        $$slots: { default: true }
      });
      var div_5 = $.sibling(node_3, 2);
      $.reset(div_4);
      $.reset(div_3);
      $.template_effect(() => {
        $.set_class(div_3, 1, $theme().allDay);
        $.set_class(div_4, 1, $theme().content);
        $.set_class(div_5, 1, $theme().hiddenScroll);
      });
      $.append($$anchor2, div_3);
    };
    $.if(node_2, ($$render) => {
      if ($allDaySlot()) $$render(consequent);
    });
  }
  var node_4 = $.sibling(node_2, 2);
  Body$1(node_4, {
    children: ($$anchor2, $$slotProps) => {
      var fragment_3 = $.comment();
      var node_5 = $.first_child(fragment_3);
      $.each(node_5, 1, $_viewDates, $.index, ($$anchor3, date) => {
        Day$2($$anchor3, {
          get date() {
            return $.get(date);
          }
        });
      });
      $.append($$anchor2, fragment_3);
    },
    $$slots: { default: true }
  });
  $.template_effect(() => {
    $.set_class(div, 1, $theme().header);
    $.set_class(div_2, 1, $theme().hiddenScroll);
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
const TimeGrid = {
  createOptions(options) {
    options.buttonText.timeGridDay = "day";
    options.buttonText.timeGridWeek = "week";
    options.view = "timeGridWeek";
    options.views.timeGridDay = {
      buttonText: btnTextDay,
      component: View$2,
      dayHeaderFormat: { weekday: "long" },
      duration: { days: 1 },
      theme: themeView("ec-time-grid ec-day-view"),
      titleFormat: { year: "numeric", month: "long", day: "numeric" }
    };
    options.views.timeGridWeek = {
      buttonText: btnTextWeek,
      component: View$2,
      duration: { weeks: 1 },
      theme: themeView("ec-time-grid ec-week-view")
    };
  },
  createStores(state) {
    state._slotTimeLimits = slotTimeLimits(state);
    state._times = times(state);
  }
};
var root$9 = $.from_html(`<span></span>`);
function Label$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $resourceLabelContent = () => $.store_get(resourceLabelContent, "$resourceLabelContent", $$stores);
  const $_intlDayHeaderAL = () => $.store_get(_intlDayHeaderAL, "$_intlDayHeaderAL", $$stores);
  const $resourceLabelDidMount = () => $.store_get(resourceLabelDidMount, "$resourceLabelDidMount", $$stores);
  let date = $.prop($$props, "date", 3, void 0), setLabel = $.prop($$props, "setLabel", 3, void 0);
  let {
    resourceLabelContent,
    resourceLabelDidMount,
    _intlDayHeaderAL
  } = getContext("state");
  let el = $.state(void 0);
  let content = $.derived(() => {
    if ($resourceLabelContent()) {
      return isFunction($resourceLabelContent()) ? $resourceLabelContent()({
        resource: $$props.resource,
        date: date() ? toLocalDate(date()) : void 0
      }) : $resourceLabelContent();
    } else {
      return $$props.resource.title;
    }
  });
  let ariaLabel = $.state(void 0);
  $.user_effect(() => {
    $.get(content);
    untrack(() => {
      if (date()) {
        $.set(ariaLabel, $_intlDayHeaderAL().format(date()) + ", " + $.get(el).innerText);
      } else if (setLabel()) {
        $.set(ariaLabel, void 0);
        setLabel()($.get(el).innerText);
      }
    });
  });
  onMount(() => {
    if (isFunction($resourceLabelDidMount())) {
      $resourceLabelDidMount()({
        resource: $$props.resource,
        date: date() ? toLocalDate(date()) : void 0,
        el: $.get(el)
      });
    }
  });
  var span = root$9();
  $.bind_this(span, ($$value) => $.set(el, $$value), () => $.get(el));
  $.action(span, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(content));
  $.template_effect(() => $.set_attribute(span, "aria-label", $.get(ariaLabel)));
  $.append($$anchor, span);
  $.pop();
  $$cleanup();
}
var root_3$1 = $.from_html(`<div><time></time></div>`);
var root_4$1 = $.from_html(`<div><!></div>`);
var root_7 = $.from_html(`<div role="columnheader"><!></div>`);
var root_8 = $.from_html(`<div role="columnheader"><time></time></div>`);
var root_5 = $.from_html(`<div></div>`);
var root_2$3 = $.from_html(`<div><!> <!></div>`);
var root_12 = $.from_html(`<div></div>`);
var root_15 = $.from_html(`<div><!></div>`);
var root_9 = $.from_html(`<div><div><!> <div></div></div></div>`);
var root_17 = $.from_html(`<div></div>`);
var root$8 = $.from_html(`<div><!> <div></div></div> <!> <!>`, 1);
function View$1($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $datesAboveResources = () => $.store_get(datesAboveResources, "$datesAboveResources", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_viewResources = () => $.store_get(_viewResources, "$_viewResources", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $_intlDayHeaderAL = () => $.store_get(_intlDayHeaderAL, "$_intlDayHeaderAL", $$stores);
  const $_intlDayHeader = () => $.store_get(_intlDayHeader, "$_intlDayHeader", $$stores);
  const $allDaySlot = () => $.store_get(allDaySlot, "$allDaySlot", $$stores);
  let {
    datesAboveResources,
    _today,
    _viewDates,
    _viewResources,
    _intlDayHeader,
    _intlDayHeaderAL,
    allDaySlot,
    theme
  } = getContext("state");
  let loops = $.derived(() => $datesAboveResources() ? [$_viewDates(), $_viewResources()] : [$_viewResources(), $_viewDates()]);
  let resourceLabels = $.proxy([]);
  var fragment = root$8();
  var div = $.first_child(fragment);
  var node = $.child(div);
  Section(node, {
    children: ($$anchor2, $$slotProps) => {
      var fragment_1 = $.comment();
      var node_1 = $.first_child(fragment_1);
      $.each(node_1, 17, () => $.get(loops)[0], $.index, ($$anchor3, item0, i) => {
        var div_1 = root_2$3();
        var node_2 = $.child(div_1);
        {
          var consequent = ($$anchor4) => {
            var div_2 = root_3$1();
            var time = $.child(div_2);
            $.action(time, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(item0)));
            $.reset(div_2);
            $.template_effect(
              ($0, $1, $2, $3) => {
                $.set_class(div_2, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$1 ?? ""}`);
                $.set_attribute(time, "datetime", $2);
                $.set_attribute(time, "aria-label", $3);
              },
              [
                () => {
                  var _a;
                  return (_a = $theme().weekdays) == null ? void 0 : _a[$.get(item0).getUTCDay()];
                },
                () => datesEqual($.get(item0), $_today()) ? " " + $theme().today : "",
                () => toISOString($.get(item0), 10),
                () => $_intlDayHeaderAL().format($.get(item0))
              ]
            );
            $.append($$anchor4, div_2);
          };
          var alternate = ($$anchor4) => {
            var div_3 = root_4$1();
            var node_3 = $.child(div_3);
            Label$1(node_3, {
              get resource() {
                return $.get(item0);
              },
              setLabel: (e) => resourceLabels[i] = e.detail + ", "
            });
            $.reset(div_3);
            $.template_effect(() => $.set_class(div_3, 1, $theme().day));
            $.append($$anchor4, div_3);
          };
          $.if(node_2, ($$render) => {
            if ($datesAboveResources()) $$render(consequent);
            else $$render(alternate, false);
          });
        }
        var node_4 = $.sibling(node_2, 2);
        {
          var consequent_2 = ($$anchor4) => {
            var div_4 = root_5();
            $.each(div_4, 21, () => $.get(loops)[1], $.index, ($$anchor5, item1) => {
              var fragment_2 = $.comment();
              var node_5 = $.first_child(fragment_2);
              {
                var consequent_1 = ($$anchor6) => {
                  var div_5 = root_7();
                  var node_6 = $.child(div_5);
                  Label$1(node_6, {
                    get resource() {
                      return $.get(item1);
                    },
                    get date() {
                      return $.get(item0);
                    }
                  });
                  $.reset(div_5);
                  $.template_effect(() => $.set_class(div_5, 1, $theme().day));
                  $.append($$anchor6, div_5);
                };
                var alternate_1 = ($$anchor6) => {
                  var div_6 = root_8();
                  var time_1 = $.child(div_6);
                  $.action(time_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(item1)));
                  $.reset(div_6);
                  $.template_effect(
                    ($0, $1, $2, $3) => {
                      $.set_class(div_6, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$1 ?? ""}`);
                      $.set_attribute(time_1, "datetime", $2);
                      $.set_attribute(time_1, "aria-label", `${resourceLabels[i] ?? ""}${$3 ?? ""}`);
                    },
                    [
                      () => {
                        var _a;
                        return (_a = $theme().weekdays) == null ? void 0 : _a[$.get(item1).getUTCDay()];
                      },
                      () => datesEqual($.get(item1), $_today()) ? " " + $theme().today : "",
                      () => toISOString($.get(item1), 10),
                      () => $_intlDayHeaderAL().format($.get(item1))
                    ]
                  );
                  $.append($$anchor6, div_6);
                };
                $.if(node_5, ($$render) => {
                  if ($datesAboveResources()) $$render(consequent_1);
                  else $$render(alternate_1, false);
                });
              }
              $.append($$anchor5, fragment_2);
            });
            $.reset(div_4);
            $.template_effect(() => $.set_class(div_4, 1, $theme().days));
            $.append($$anchor4, div_4);
          };
          $.if(node_4, ($$render) => {
            if ($.get(loops)[1].length > 1) $$render(consequent_2);
          });
        }
        $.reset(div_1);
        $.template_effect(() => $.set_class(div_1, 1, $theme().resource));
        $.append($$anchor3, div_1);
      });
      $.append($$anchor2, fragment_1);
    },
    $$slots: { default: true }
  });
  var div_7 = $.sibling(node, 2);
  $.reset(div);
  var node_7 = $.sibling(div, 2);
  {
    var consequent_4 = ($$anchor2) => {
      var div_8 = root_9();
      var div_9 = $.child(div_8);
      var node_8 = $.child(div_9);
      Section(node_8, {
        children: ($$anchor3, $$slotProps) => {
          var fragment_3 = $.comment();
          var node_9 = $.first_child(fragment_3);
          {
            var consequent_3 = ($$anchor4) => {
              var fragment_4 = $.comment();
              var node_10 = $.first_child(fragment_4);
              $.each(node_10, 1, $_viewDates, $.index, ($$anchor5, date) => {
                var div_10 = root_12();
                $.each(div_10, 5, $_viewResources, $.index, ($$anchor6, resource) => {
                  const expression = $.derived(() => [$.get(date)]);
                  Week($$anchor6, {
                    get dates() {
                      return $.get(expression);
                    },
                    get resource() {
                      return $.get(resource);
                    }
                  });
                });
                $.reset(div_10);
                $.template_effect(() => $.set_class(div_10, 1, $theme().resource));
                $.append($$anchor5, div_10);
              });
              $.append($$anchor4, fragment_4);
            };
            var alternate_2 = ($$anchor4) => {
              var fragment_6 = $.comment();
              var node_11 = $.first_child(fragment_6);
              $.each(node_11, 1, $_viewResources, $.index, ($$anchor5, resource) => {
                var div_11 = root_15();
                var node_12 = $.child(div_11);
                Week(node_12, {
                  get dates() {
                    return $_viewDates();
                  },
                  get resource() {
                    return $.get(resource);
                  }
                });
                $.reset(div_11);
                $.template_effect(() => $.set_class(div_11, 1, $theme().resource));
                $.append($$anchor5, div_11);
              });
              $.append($$anchor4, fragment_6);
            };
            $.if(node_9, ($$render) => {
              if ($datesAboveResources()) $$render(consequent_3);
              else $$render(alternate_2, false);
            });
          }
          $.append($$anchor3, fragment_3);
        },
        $$slots: { default: true }
      });
      var div_12 = $.sibling(node_8, 2);
      $.reset(div_9);
      $.reset(div_8);
      $.template_effect(() => {
        $.set_class(div_8, 1, $theme().allDay);
        $.set_class(div_9, 1, $theme().content);
        $.set_class(div_12, 1, $theme().hiddenScroll);
      });
      $.append($$anchor2, div_8);
    };
    $.if(node_7, ($$render) => {
      if ($allDaySlot()) $$render(consequent_4);
    });
  }
  var node_13 = $.sibling(node_7, 2);
  Body$1(node_13, {
    children: ($$anchor2, $$slotProps) => {
      var fragment_7 = $.comment();
      var node_14 = $.first_child(fragment_7);
      $.each(node_14, 17, () => $.get(loops)[0], $.index, ($$anchor3, item0) => {
        var div_13 = root_17();
        $.each(div_13, 21, () => $.get(loops)[1], $.index, ($$anchor4, item1) => {
          const expression_1 = $.derived(() => $datesAboveResources() ? $.get(item0) : $.get(item1));
          const expression_2 = $.derived(() => $datesAboveResources() ? $.get(item1) : $.get(item0));
          Day$2($$anchor4, {
            get date() {
              return $.get(expression_1);
            },
            get resource() {
              return $.get(expression_2);
            }
          });
        });
        $.reset(div_13);
        $.template_effect(() => $.set_class(div_13, 1, $theme().resource));
        $.append($$anchor3, div_13);
      });
      $.append($$anchor2, fragment_7);
    },
    $$slots: { default: true }
  });
  $.template_effect(() => {
    $.set_class(div, 1, $theme().header);
    $.set_class(div_7, 1, $theme().hiddenScroll);
  });
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
const index$1 = {
  createOptions(options) {
    options.datesAboveResources = false;
    options.buttonText.resourceTimeGridDay = "resources";
    options.buttonText.resourceTimeGridWeek = "resources";
    options.view = "resourceTimeGridWeek";
    options.views.resourceTimeGridDay = {
      buttonText: btnTextDay,
      component: View$1,
      duration: { days: 1 },
      theme: themeView("ec-time-grid ec-resource-day-view")
    };
    options.views.resourceTimeGridWeek = {
      buttonText: btnTextWeek,
      component: View$1,
      duration: { weeks: 1 },
      theme: themeView("ec-time-grid ec-resource-week-view")
    };
  },
  createStores(state) {
    if (!("_times" in state)) {
      TimeGrid.createStores(state);
    }
    if (!("_viewResources" in state)) {
      state._viewResources = viewResources(state);
    }
  }
};
function dayTimeLimits(state) {
  return derived(
    [state.slotMinTime, state.slotMaxTime, state.flexibleSlotTimeLimits, state._viewDates, state._filteredEvents],
    ([$slotMinTime, $slotMaxTime, $flexibleSlotTimeLimits, $_viewDates, $_filteredEvents]) => {
      let dayTimeLimits2 = {};
      for (let date of $_viewDates) {
        dayTimeLimits2[date.getTime()] = createSlotTimeLimits(
          $slotMinTime,
          $slotMaxTime,
          $flexibleSlotTimeLimits,
          [date],
          $_filteredEvents
        );
      }
      return dayTimeLimits2;
    }
  );
}
function dayTimes(state) {
  return derived(
    [state._viewDates, state.slotDuration, state.slotLabelInterval, state._dayTimeLimits, state._intlSlotLabel],
    ([$_viewDates, $slotDuration, $slotLabelInterval, $_dayTimeLimits, $_intlSlotLabel]) => {
      let dayTimes2 = {};
      for (let date of $_viewDates) {
        let time = date.getTime();
        dayTimes2[time] = time in $_dayTimeLimits ? createTimes(date, $slotDuration, $slotLabelInterval, $_dayTimeLimits[time], $_intlSlotLabel) : [];
      }
      return dayTimes2;
    }
  );
}
function nestedResources(state) {
  return derived(state.resources, ($resources) => $resources.some((resource) => getPayload(resource).children.length));
}
var root$7 = $.from_html(`<span></span>`);
function Label($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $resourceLabelContent = () => $.store_get(resourceLabelContent, "$resourceLabelContent", $$stores);
  const $resourceLabelDidMount = () => $.store_get(resourceLabelDidMount, "$resourceLabelDidMount", $$stores);
  let date = $.prop($$props, "date", 3, void 0);
  let { resourceLabelContent, resourceLabelDidMount } = getContext("state");
  let el = $.state(void 0);
  let content = $.derived(() => {
    if ($resourceLabelContent()) {
      return isFunction($resourceLabelContent()) ? $resourceLabelContent()({
        resource: $$props.resource,
        date: date() ? toLocalDate(date()) : void 0
      }) : $resourceLabelContent();
    } else {
      return $$props.resource.title;
    }
  });
  onMount(() => {
    if (isFunction($resourceLabelDidMount())) {
      $resourceLabelDidMount()({
        resource: $$props.resource,
        date: date() ? toLocalDate(date()) : void 0,
        el: $.get(el)
      });
    }
  });
  var span = root$7();
  $.bind_this(span, ($$value) => $.set(el, $$value), () => $.get(el));
  $.action(span, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(content));
  $.append($$anchor, span);
  $.pop();
  $$cleanup();
}
function onclick(_, expanded, payload, toggle, resources) {
  $.set(expanded, !$.get(expanded));
  $.get(payload).expanded = $.get(expanded);
  toggle($.get(payload).children, $.get(expanded));
  resources.update(identity);
}
var root_1$4 = $.from_html(`<span></span>`);
var root_2$2 = $.from_html(`<button><!></button>`);
var root$6 = $.from_html(`<!> <span><!></span>`, 1);
function Expander($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let { resources, theme } = getContext("state");
  let payload = $.state({});
  let expanded = $.state(true);
  $.user_pre_effect(() => {
    $.set(payload, getPayload($$props.resource));
    $.set(expanded, $.get(payload).expanded, true);
  });
  function toggle(children, expand) {
    for (let child of children) {
      let payload2 = getPayload(child);
      payload2.hidden = !expand;
      if (payload2.expanded) {
        toggle(payload2.children, expand);
      }
    }
  }
  var fragment = root$6();
  var node = $.first_child(fragment);
  $.each(node, 17, () => Array($.get(payload).level), $.index, ($$anchor2, level) => {
    var span = root_1$4();
    $.template_effect(() => $.set_class(span, 1, $theme().expander));
    $.append($$anchor2, span);
  });
  var span_1 = $.sibling(node, 2);
  var node_1 = $.child(span_1);
  {
    var consequent_1 = ($$anchor2) => {
      var button = root_2$2();
      button.__click = [
        onclick,
        expanded,
        payload,
        toggle,
        resources
      ];
      var node_2 = $.child(button);
      {
        var consequent = ($$anchor3) => {
          var text = $.text("−");
          $.append($$anchor3, text);
        };
        var alternate = ($$anchor3) => {
          var text_1 = $.text("+");
          $.append($$anchor3, text_1);
        };
        $.if(node_2, ($$render) => {
          if ($.get(expanded)) $$render(consequent);
          else $$render(alternate, false);
        });
      }
      $.reset(button);
      $.template_effect(() => $.set_class(button, 1, $theme().button));
      $.append($$anchor2, button);
    };
    $.if(node_1, ($$render) => {
      var _a;
      if ((_a = $.get(payload).children) == null ? void 0 : _a.length) $$render(consequent_1);
    });
  }
  $.reset(span_1);
  $.template_effect(() => $.set_class(span_1, 1, $theme().expander));
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
$.delegate(["click"]);
var root_1$3 = $.from_html(`<div role="rowheader"><!> <!></div>`);
var root$5 = $.from_html(`<div><div></div> <div></div></div>`);
function Sidebar($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_headerHeight = () => $.store_get(_headerHeight, "$_headerHeight", $$stores);
  const $_sidebarEl = () => $.store_get(_sidebarEl, "$_sidebarEl", $$stores);
  const $_viewResources = () => $.store_get(_viewResources, "$_viewResources", $$stores);
  const $_resHs = () => $.store_get(_resHs, "$_resHs", $$stores);
  const $_nestedResources = () => $.store_get(_nestedResources, "$_nestedResources", $$stores);
  let {
    _viewResources,
    _headerHeight,
    _bodyEl,
    _resHs,
    _sidebarEl,
    _nestedResources,
    theme
  } = getContext("state");
  function onwheel(jsEvent) {
    $_bodyEl().scrollBy({ top: jsEvent.deltaY < 0 ? -30 : 30 });
  }
  $.init();
  var div = root$5();
  var div_1 = $.child(div);
  var div_2 = $.sibling(div_1, 2);
  $.each(div_2, 5, $_viewResources, $.index, ($$anchor2, resource) => {
    var div_3 = root_1$3();
    var node = $.child(div_3);
    {
      var consequent = ($$anchor3) => {
        Expander($$anchor3, {
          get resource() {
            return $.get(resource);
          }
        });
      };
      $.if(node, ($$render) => {
        if ($_nestedResources()) $$render(consequent);
      });
    }
    var node_1 = $.sibling(node, 2);
    Label(node_1, {
      get resource() {
        return $.get(resource);
      }
    });
    $.reset(div_3);
    $.template_effect(
      ($0) => {
        $.set_class(div_3, 1, $theme().resource);
        $.set_style(div_3, `flex-basis: ${$0 ?? ""}px`);
      },
      [
        () => max($_resHs().get($.get(resource)) ?? 0, 34)
      ],
      $.derived_safe_equal
    );
    $.append($$anchor2, div_3);
  });
  $.reset(div_2);
  $.bind_this(div_2, ($$value) => $.store_set(_sidebarEl, $$value), () => $_sidebarEl());
  $.reset(div);
  $.template_effect(() => {
    $.set_class(div, 1, $theme().sidebar);
    $.set_class(div_1, 1, $theme().sidebarTitle);
    $.set_style(div_1, `flex-basis: ${$_headerHeight() ?? ""}px`);
    $.set_class(div_2, 1, $theme().content);
  });
  $.event("wheel", div_2, onwheel);
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
var root_3 = $.from_html(`<div role="columnheader"><time></time></div>`);
var root_2$1 = $.from_html(`<div><time></time></div> <div></div>`, 1);
var root_4 = $.from_html(`<div role="columnheader"><time></time></div>`);
var root_1$2 = $.from_html(`<div><!></div>`);
var root$4 = $.from_html(`<div><div role="row"></div> <div></div></div>`);
function Header($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_headerEl = () => $.store_get(_headerEl, "$_headerEl", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $_intlDayHeaderAL = () => $.store_get(_intlDayHeaderAL, "$_intlDayHeaderAL", $$stores);
  const $_intlDayHeader = () => $.store_get(_intlDayHeader, "$_intlDayHeader", $$stores);
  const $_dayTimes = () => $.store_get(_dayTimes, "$_dayTimes", $$stores);
  let {
    _headerEl,
    _headerHeight,
    _intlDayHeader,
    _intlDayHeaderAL,
    _dayTimes,
    _today,
    _viewDates,
    slotDuration,
    theme
  } = getContext("state");
  $.init();
  var div = root$4();
  var div_1 = $.child(div);
  $.each(div_1, 5, $_viewDates, $.index, ($$anchor2, date) => {
    var div_2 = root_1$2();
    var node = $.child(div_2);
    {
      var consequent = ($$anchor3) => {
        var fragment = root_2$1();
        var div_3 = $.first_child(fragment);
        var time_1 = $.child(div_3);
        $.action(time_1, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(date)));
        $.reset(div_3);
        var div_4 = $.sibling(div_3, 2);
        $.each(div_4, 5, () => $_dayTimes()[$.get(date).getTime()], $.index, ($$anchor4, time) => {
          var div_5 = root_3();
          var time_2 = $.child(div_5);
          $.action(time_2, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $.get(time)[1]);
          $.reset(div_5);
          $.template_effect(() => {
            $.set_class(div_5, 1, `${$theme().time ?? ""}${$.get(time)[2] ? "" : " " + $theme().minor}`);
            $.set_attribute(time_2, "datetime", $.get(time)[0]);
          });
          $.append($$anchor4, div_5);
        });
        $.reset(div_4);
        $.template_effect(
          ($0, $1) => {
            $.set_class(div_3, 1, $theme().dayHead);
            $.set_attribute(time_1, "datetime", $0);
            $.set_attribute(time_1, "aria-label", $1);
            $.set_class(div_4, 1, $theme().times);
          },
          [
            () => toISOString($.get(date), 10),
            () => $_intlDayHeaderAL().format($.get(date))
          ],
          $.derived_safe_equal
        );
        $.append($$anchor3, fragment);
      };
      var alternate = ($$anchor3) => {
        var div_6 = root_4();
        var time_3 = $.child(div_6);
        $.action(time_3, ($$node, $$action_arg) => setContent == null ? void 0 : setContent($$node, $$action_arg), () => $_intlDayHeader().format($.get(date)));
        $.reset(div_6);
        $.template_effect(
          ($0, $1) => {
            $.set_class(div_6, 1, $theme().time);
            $.set_attribute(time_3, "datetime", $0);
            $.set_attribute(time_3, "aria-label", $1);
          },
          [
            () => toISOString($.get(date), 10),
            () => $_intlDayHeaderAL().format($.get(date))
          ],
          $.derived_safe_equal
        );
        $.append($$anchor3, div_6);
      };
      $.if(node, ($$render) => {
        if (toSeconds($slotDuration())) $$render(consequent);
        else $$render(alternate, false);
      });
    }
    $.reset(div_2);
    $.template_effect(
      ($0, $1) => $.set_class(div_2, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$1 ?? ""}`),
      [
        () => {
          var _a;
          return (_a = $theme().weekdays) == null ? void 0 : _a[$.get(date).getUTCDay()];
        },
        () => datesEqual($.get(date), $_today()) ? " " + $theme().today : ""
      ],
      $.derived_safe_equal
    );
    $.append($$anchor2, div_2);
  });
  $.reset(div_1);
  var div_7 = $.sibling(div_1, 2);
  $.reset(div);
  $.bind_this(div, ($$value) => $.store_set(_headerEl, $$value), () => $_headerEl());
  $.action(div, ($$node, $$action_arg) => observeResize == null ? void 0 : observeResize($$node, $$action_arg), () => () => $.store_set(_headerHeight, $_headerEl().clientHeight));
  $.template_effect(() => {
    $.set_class(div, 1, $theme().header);
    $.set_class(div_1, 1, $theme().days);
    $.set_class(div_7, 1, $theme().hiddenScroll);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
function prepareEventChunks(chunks, $_viewDates, $_dayTimeLimits, $slotDuration) {
  let longChunks = {};
  let filteredChunks = [];
  if (chunks.length) {
    sortEventChunks(chunks);
    let step = toSeconds($slotDuration);
    let prevChunk;
    for (let chunk of chunks) {
      let prevDayEnd;
      if (step) {
        let slots = 0;
        for (let i = 0; i < $_viewDates.length; ++i) {
          let slotTimeLimits2 = getSlotTimeLimits($_dayTimeLimits, $_viewDates[i]);
          let dayStart = addDuration(cloneDate($_viewDates[i]), slotTimeLimits2.min);
          let dayEnd = addDuration(cloneDate($_viewDates[i]), slotTimeLimits2.max);
          if (!chunk.date) {
            if (chunk.start < dayEnd && chunk.end > dayStart) {
              chunk.date = $_viewDates[i];
              if (chunk.start < dayStart) {
                chunk.start = dayStart;
              }
              chunk.offset = (chunk.start - dayStart) / 1e3 / step;
              if (chunk.end > dayEnd) {
                slots += dayEnd - chunk.start;
              } else {
                slots += chunk.end - chunk.start || step * 1e3;
                break;
              }
            }
          } else {
            if (chunk.end <= dayStart) {
              chunk.end = prevDayEnd;
              break;
            }
            let key = $_viewDates[i].getTime();
            if (longChunks[key]) {
              longChunks[key].push(chunk);
            } else {
              longChunks[key] = [chunk];
            }
            if (chunk.end > dayEnd) {
              slots += dayEnd - dayStart;
            } else {
              slots += chunk.end - dayStart;
              break;
            }
          }
          prevDayEnd = dayEnd;
        }
        chunk.slots = slots / 1e3 / step;
      } else {
        let days2 = 0;
        for (let i = 0; i < $_viewDates.length; ++i) {
          let dayStart = $_viewDates[i];
          let dayEnd = addDay(cloneDate(dayStart));
          if (!chunk.date) {
            if (chunk.start < dayEnd) {
              chunk.date = dayStart;
              if (chunk.start < dayStart) {
                chunk.start = dayStart;
              }
              ++days2;
            }
          } else {
            if (chunk.end <= dayStart) {
              chunk.end = prevDayEnd;
              break;
            }
            let key = dayStart.getTime();
            if (longChunks[key]) {
              longChunks[key].push(chunk);
            } else {
              longChunks[key] = [chunk];
            }
            ++days2;
          }
          prevDayEnd = dayEnd;
        }
        chunk.days = days2;
      }
      if (!chunk.date) {
        continue;
      }
      if (prevChunk && datesEqual(prevChunk.date, chunk.date)) {
        chunk.prev = prevChunk;
      }
      prevChunk = chunk;
      filteredChunks.push(chunk);
    }
  }
  return [filteredChunks, longChunks];
}
function repositionEvent(chunk, dayChunks, longChunks, height2, allDay) {
  chunk.top = 0;
  chunk.bottom = height2;
  let margin = 1;
  let key = chunk.date.getTime();
  longChunks = (longChunks == null ? void 0 : longChunks[key]) ?? [];
  let chunks = [...dayChunks, ...longChunks];
  chunks.sort((a, b) => (a.top ?? 0) - (b.top ?? 0) || a.start - b.start || b.event.allDay - a.event.allDay);
  for (let dayChunk of chunks) {
    if (dayChunk === chunk) {
      continue;
    }
    if ((allDay || chunk.start < dayChunk.end && chunk.end > dayChunk.start) && chunk.top < dayChunk.bottom && chunk.bottom > dayChunk.top) {
      let offset = dayChunk.bottom - chunk.top + 1;
      margin += offset;
      chunk.top += offset;
      chunk.bottom += offset;
    }
  }
  return margin;
}
function getSlotTimeLimits($_dayTimeLimits, date) {
  return $_dayTimeLimits[date.getTime()] ?? { min: createDuration(0), max: createDuration(0) };
}
function Event($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $slotWidth = () => $.store_get(slotWidth, "$slotWidth", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  let dayChunks = $.prop($$props, "dayChunks", 19, () => []), longChunks = $.prop($$props, "longChunks", 19, () => ({})), resource = $.prop($$props, "resource", 3, void 0);
  let { slotDuration, slotWidth } = getContext("state");
  let el = $.state(void 0);
  let margin = $.state($.proxy(helperEvent($$props.chunk.event.display) ? 1 : 0));
  let event = $.derived(() => $$props.chunk.event);
  let width = $.derived(() => "slots" in $$props.chunk ? $$props.chunk.slots * $slotWidth() : $$props.chunk.days * 100);
  let styles = $.derived(() => (style) => {
    if ("slots" in $$props.chunk) {
      let left = $$props.chunk.offset * $slotWidth();
      style["left"] = `${left}px`;
      style["width"] = `${$.get(width)}px`;
    } else {
      style["width"] = `${$.get(width)}%`;
    }
    let marginTop = $.get(margin);
    if ($.get(event)._margin) {
      let [_margin, _resource] = $.get(event)._margin;
      if (resource() === _resource) {
        marginTop = _margin;
      }
    }
    style["margin-top"] = `${marginTop}px`;
    return style;
  });
  function reposition() {
    if (!$.get(el)) {
      return 0;
    }
    let h = height($.get(el));
    $.set(margin, repositionEvent($$props.chunk, dayChunks(), longChunks(), h, !toSeconds($slotDuration())), true);
    return $.get(margin) + h;
  }
  var fragment = $.comment();
  var node = $.first_child(fragment);
  {
    var consequent = ($$anchor2) => {
      InteractableEvent($$anchor2, {
        get chunk() {
          return $$props.chunk;
        },
        get styles() {
          return $.get(styles);
        },
        axis: "x",
        forceMargin: () => [$.get(margin), resource()],
        get el() {
          return $.get(el);
        },
        set el($$value) {
          $.set(el, $$value, true);
        }
      });
    };
    $.if(node, ($$render) => {
      if ($.get(width) > 0) $$render(consequent);
    });
  }
  $.append($$anchor, fragment);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
var root_1$1 = $.from_html(`<!> <!> <!> <!>`, 1);
var root$3 = $.from_html(`<div role="cell"><div><!></div></div>`);
function Day($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $highlightedDates = () => $.store_get(highlightedDates, "$highlightedDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_dayTimeLimits = () => $.store_get(_dayTimeLimits, "$_dayTimeLimits", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $slotWidth = () => $.store_get(slotWidth, "$slotWidth", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_interaction = () => $.store_get(_interaction, "$_interaction", $$stores);
  let iChunks = $.prop($$props, "iChunks", 19, () => []);
  let {
    highlightedDates,
    slotDuration,
    slotWidth,
    theme,
    validRange,
    _interaction,
    _today,
    _dayTimeLimits
  } = getContext("state");
  let el = $.state(void 0);
  let refs = [];
  let isToday = $.derived(() => datesEqual($$props.date, $_today()));
  let highlight = $.derived(() => $highlightedDates().some((d) => datesEqual(d, $$props.date)));
  let disabled = $.derived(() => outsideRange($$props.date, $validRange()));
  let slotTimeLimits2 = $.derived(() => getSlotTimeLimits($_dayTimeLimits(), $$props.date));
  let allDay = $.derived(() => !toSeconds($slotDuration()));
  let pointerIdx = $.derived(() => $.get(allDay) ? 2 : 1);
  let dayChunks = $.derived(() => $$props.chunks.filter(chunkIntersects));
  let dayBgChunks = $.derived(() => $$props.bgChunks.filter((bgChunk) => (!$.get(allDay) || bgChunk.event.allDay) && chunkIntersects(bgChunk)));
  function chunkIntersects(chunk) {
    return datesEqual(chunk.date, $$props.date);
  }
  function dateFromPoint(x, y) {
    x -= rect($.get(el)).left;
    return {
      allDay: $.get(allDay),
      date: $.get(allDay) ? cloneDate($$props.date) : addDuration(addDuration(cloneDate($$props.date), $.get(slotTimeLimits2).min), $slotDuration(), floor(x / $slotWidth())),
      resource: $$props.resource,
      dayEl: $.get(el),
      disabled: $.get(disabled)
    };
  }
  $.user_effect(() => {
    setPayload($.get(el), dateFromPoint);
  });
  function reposition() {
    return max(...runReposition(refs, $.get(dayChunks)));
  }
  var div = root$3();
  div.__pointerdown = function(...$$args) {
    var _a, _b;
    (_b = (_a = $_interaction().action) == null ? void 0 : _a.select) == null ? void 0 : _b.apply(this, $$args);
  };
  var div_1 = $.child(div);
  var node = $.child(div_1);
  {
    var consequent_2 = ($$anchor2) => {
      var fragment = root_1$1();
      var node_1 = $.first_child(fragment);
      $.each(node_1, 17, () => $.get(dayBgChunks), (chunk) => chunk.event, ($$anchor3, chunk) => {
        Event($$anchor3, {
          get chunk() {
            return $.get(chunk);
          }
        });
      });
      var node_2 = $.sibling(node_1, 2);
      {
        var consequent = ($$anchor3) => {
          Event($$anchor3, {
            get chunk() {
              return iChunks()[$.get(pointerIdx)];
            }
          });
        };
        $.if(node_2, ($$render) => {
          if (iChunks()[$.get(pointerIdx)] && chunkIntersects(iChunks()[$.get(pointerIdx)])) $$render(consequent);
        });
      }
      var node_3 = $.sibling(node_2, 2);
      $.each(node_3, 19, () => $.get(dayChunks), (chunk) => chunk.event, ($$anchor3, chunk, i) => {
        $.bind_this(
          Event($$anchor3, {
            get chunk() {
              return $.get(chunk);
            },
            get dayChunks() {
              return $.get(dayChunks);
            },
            get longChunks() {
              return $$props.longChunks;
            },
            get resource() {
              return $$props.resource;
            }
          }),
          ($$value, i2) => refs[i2] = $$value,
          (i2) => refs == null ? void 0 : refs[i2],
          () => [$.get(i)]
        );
      });
      var node_4 = $.sibling(node_3, 2);
      {
        var consequent_1 = ($$anchor3) => {
          Event($$anchor3, {
            get chunk() {
              return iChunks()[0];
            },
            get resource() {
              return $$props.resource;
            }
          });
        };
        $.if(node_4, ($$render) => {
          if (iChunks()[0] && chunkIntersects(iChunks()[0])) $$render(consequent_1);
        });
      }
      $.append($$anchor2, fragment);
    };
    $.if(node, ($$render) => {
      if (!$.get(disabled)) $$render(consequent_2);
    });
  }
  $.reset(div_1);
  $.reset(div);
  $.bind_this(div, ($$value) => $.set(el, $$value), () => $.get(el));
  $.template_effect(
    ($0, $1) => {
      $.set_class(div, 1, `${$theme().day ?? ""} ${$0 ?? ""}${$.get(isToday) ? " " + $theme().today : ""}${$.get(highlight) ? " " + $theme().highlight : ""}${$.get(disabled) ? " " + $theme().disabled : ""}`);
      $.set_style(div, `flex-grow: ${$1 ?? ""}`);
      $.set_class(div_1, 1, $theme().events);
    },
    [
      () => {
        var _a;
        return (_a = $theme().weekdays) == null ? void 0 : _a[$$props.date.getUTCDay()];
      },
      () => $.get(allDay) ? null : ceil((toSeconds($.get(slotTimeLimits2).max) - toSeconds($.get(slotTimeLimits2).min)) / toSeconds($slotDuration()))
    ]
  );
  $.append($$anchor, div);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
$.delegate(["pointerdown"]);
var root$2 = $.from_html(`<div role="row"></div>`);
function Days($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $validRange = () => $.store_get(validRange, "$validRange", $$stores);
  const $_dayTimeLimits = () => $.store_get(_dayTimeLimits, "$_dayTimeLimits", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $_iEvents = () => $.store_get(_iEvents, "$_iEvents", $$stores);
  const $_resHs = () => $.store_get(_resHs, "$_resHs", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  let {
    _viewDates,
    _filteredEvents,
    _iEvents,
    _resHs,
    _dayTimeLimits,
    slotDuration,
    theme,
    validRange
  } = getContext("state");
  let refs = [];
  let height2 = $.state(0);
  let $$d = $.derived(() => {
    let start2 = cloneDate(limitToRange($_viewDates()[0], $validRange()));
    let end2 = cloneDate(limitToRange($_viewDates().at(-1), $validRange()));
    let slotTimeLimits2 = getSlotTimeLimits($_dayTimeLimits(), start2);
    addDuration(start2, slotTimeLimits2.min);
    slotTimeLimits2 = getSlotTimeLimits($_dayTimeLimits(), end2);
    if (slotTimeLimits2.max.seconds > DAY_IN_SECONDS) {
      addDuration(end2, slotTimeLimits2.max);
    } else {
      addDay(end2);
    }
    return [start2, end2];
  }), $$array = $.derived(() => $.to_array($.get($$d), 2)), start = $.derived(() => $.get($$array)[0]), end = $.derived(() => $.get($$array)[1]);
  let $$d_1 = $.derived(() => {
    let chunks2 = [];
    let bgChunks2 = [];
    let longChunks2;
    for (let event of $_filteredEvents()) {
      if (eventIntersects(event, $.get(start), $.get(end), $$props.resource)) {
        let chunk = createEventChunk(event, $.get(start), $.get(end));
        if (bgEvent(event.display)) {
          bgChunks2.push(chunk);
        } else {
          chunks2.push(chunk);
        }
      }
    }
    [bgChunks2] = prepareEventChunks(bgChunks2, $_viewDates(), $_dayTimeLimits(), $slotDuration());
    [chunks2, longChunks2] = prepareEventChunks(chunks2, $_viewDates(), $_dayTimeLimits(), $slotDuration());
    return [chunks2, bgChunks2, longChunks2];
  }), $$array_3 = $.derived(() => $.to_array($.get($$d_1), 3)), chunks = $.derived(() => $.get($$array_3)[0]), bgChunks = $.derived(() => $.get($$array_3)[1]), longChunks = $.derived(() => $.get($$array_3)[2]);
  let iChunks = $.derived(() => $_iEvents().map((event) => {
    let chunk;
    if (event && eventIntersects(event, $.get(start), $.get(end), $$props.resource)) {
      chunk = createEventChunk(event, $.get(start), $.get(end));
      [[chunk]] = prepareEventChunks([chunk], $_viewDates(), $_dayTimeLimits(), $slotDuration());
    } else {
      chunk = null;
    }
    return chunk;
  }));
  function reposition() {
    $.set(height2, ceil(max(...runReposition(refs, $_viewDates()))) + 10);
    $_resHs().set($$props.resource, $.get(height2));
    $.store_set(_resHs, $_resHs());
  }
  var div = root$2();
  $.each(div, 5, $_viewDates, $.index, ($$anchor2, date, i) => {
    $.bind_this(
      Day($$anchor2, {
        get date() {
          return $.get(date);
        },
        get resource() {
          return $$props.resource;
        },
        get chunks() {
          return $.get(chunks);
        },
        get bgChunks() {
          return $.get(bgChunks);
        },
        get longChunks() {
          return $.get(longChunks);
        },
        get iChunks() {
          return $.get(iChunks);
        }
      }),
      ($$value, i2) => refs[i2] = $$value,
      (i2) => refs == null ? void 0 : refs[i2],
      () => [i]
    );
  });
  $.reset(div);
  $.template_effect(
    ($0) => {
      $.set_class(div, 1, $theme().days);
      $.set_style(div, `flex-basis: ${$0 ?? ""}px`);
    },
    [() => max($.get(height2), 34)]
  );
  $.append($$anchor, div);
  var $$pop = $.pop({ reposition });
  $$cleanup();
  return $$pop;
}
var root_2 = $.from_html(`<div></div>`);
var root$1 = $.from_html(`<div><div><div></div> <!></div></div>`);
function Body($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_dayTimeLimits = () => $.store_get(_dayTimeLimits, "$_dayTimeLimits", $$stores);
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_bodyEl = () => $.store_get(_bodyEl, "$_bodyEl", $$stores);
  const $scrollTime = () => $.store_get(scrollTime, "$scrollTime", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $slotWidth = () => $.store_get(slotWidth, "$slotWidth", $$stores);
  const $_resHs = () => $.store_get(_resHs, "$_resHs", $$stores);
  const $_viewResources = () => $.store_get(_viewResources, "$_viewResources", $$stores);
  const $_filteredEvents = () => $.store_get(_filteredEvents, "$_filteredEvents", $$stores);
  const $_headerEl = () => $.store_get(_headerEl, "$_headerEl", $$stores);
  const $_sidebarEl = () => $.store_get(_sidebarEl, "$_sidebarEl", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_dayTimes = () => $.store_get(_dayTimes, "$_dayTimes", $$stores);
  let {
    _bodyEl,
    _bodyHeight,
    _bodyWidth,
    _bodyScrollLeft,
    _headerEl,
    _filteredEvents,
    _sidebarEl,
    _dayTimes,
    _dayTimeLimits,
    _recheckScrollable,
    _resHs,
    _viewResources,
    _viewDates,
    scrollTime,
    slotDuration,
    slotWidth,
    theme
  } = getContext("state");
  let refs = [];
  function scrollToTime() {
    let slotTimeLimits2 = getSlotTimeLimits($_dayTimeLimits(), $_viewDates()[0]);
    $.store_mutate(_bodyEl, $.untrack($_bodyEl).scrollLeft = (toSeconds($scrollTime()) - toSeconds(slotTimeLimits2.min)) / toSeconds($slotDuration()) * $slotWidth(), $.untrack($_bodyEl));
    $.store_set(_bodyScrollLeft, $_bodyEl().scrollLeft);
  }
  $.user_effect(() => {
    $_viewDates();
    $scrollTime();
    untrack(scrollToTime);
  });
  function reposition() {
    $_resHs().clear();
    runReposition(refs, $_viewResources());
  }
  $.user_effect(() => {
    $_filteredEvents();
    $_viewResources();
    untrack(reposition);
  });
  function onscroll() {
    $.store_mutate(_headerEl, $.untrack($_headerEl).scrollLeft = $_bodyEl().scrollLeft, $.untrack($_headerEl));
    $.store_mutate(_sidebarEl, $.untrack($_sidebarEl).scrollTop = $_bodyEl().scrollTop, $.untrack($_sidebarEl));
    $.store_set(_bodyScrollLeft, $_bodyEl().scrollLeft);
  }
  function onresize() {
    $.store_set(_bodyHeight, $_bodyEl().clientHeight);
    $.store_set(_bodyWidth, $_bodyEl().clientWidth);
    $.store_set(_recheckScrollable, true);
  }
  var div = root$1();
  $.event("resize", $.window, reposition);
  var div_1 = $.child(div);
  var div_2 = $.child(div_1);
  $.each(div_2, 5, $_viewDates, $.index, ($$anchor2, date) => {
    var fragment = $.comment();
    var node = $.first_child(fragment);
    $.each(node, 1, () => $_dayTimes()[$.get(date).getTime()], $.index, ($$anchor3, time) => {
      var div_3 = root_2();
      $.template_effect(() => $.set_class(div_3, 1, `${$theme().line ?? ""}${$.get(time)[2] ? "" : " " + $theme().minor}`));
      $.append($$anchor3, div_3);
    });
    $.append($$anchor2, fragment);
  });
  $.reset(div_2);
  var node_1 = $.sibling(div_2, 2);
  $.each(node_1, 1, $_viewResources, $.index, ($$anchor2, resource, i) => {
    $.bind_this(
      Days($$anchor2, {
        get resource() {
          return $.get(resource);
        }
      }),
      ($$value, i2) => refs[i2] = $$value,
      (i2) => refs == null ? void 0 : refs[i2],
      () => [i]
    );
  });
  $.reset(div_1);
  $.reset(div);
  $.bind_this(div, ($$value) => $.store_set(_bodyEl, $$value), () => $_bodyEl());
  $.action(div, ($$node, $$action_arg) => observeResize == null ? void 0 : observeResize($$node, $$action_arg), () => onresize);
  $.template_effect(() => {
    $.set_class(div, 1, $theme().body);
    $.set_class(div_1, 1, $theme().content);
    $.set_class(div_2, 1, $theme().lines);
  });
  $.event("scroll", div, onscroll);
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
var root_1 = $.from_html(`<div></div>`);
function NowIndicator($$anchor, $$props) {
  $.push($$props, true);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $_viewDates = () => $.store_get(_viewDates, "$_viewDates", $$stores);
  const $_dayTimeLimits = () => $.store_get(_dayTimeLimits, "$_dayTimeLimits", $$stores);
  const $_today = () => $.store_get(_today, "$_today", $$stores);
  const $_now = () => $.store_get(_now, "$_now", $$stores);
  const $slotDuration = () => $.store_get(slotDuration, "$slotDuration", $$stores);
  const $slotWidth = () => $.store_get(slotWidth, "$slotWidth", $$stores);
  const $_bodyScrollLeft = () => $.store_get(_bodyScrollLeft, "$_bodyScrollLeft", $$stores);
  const $_bodyWidth = () => $.store_get(_bodyWidth, "$_bodyWidth", $$stores);
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $_headerHeight = () => $.store_get(_headerHeight, "$_headerHeight", $$stores);
  const $_bodyHeight = () => $.store_get(_bodyHeight, "$_bodyHeight", $$stores);
  let {
    slotDuration,
    slotWidth,
    theme,
    _bodyHeight,
    _bodyWidth,
    _bodyScrollLeft,
    _headerHeight,
    _dayTimeLimits,
    _now,
    _today,
    _viewDates
  } = getContext("state");
  let left = $.derived(() => {
    let offset = 0;
    for (let i = 0; i < $_viewDates().length; ++i) {
      let slotTimeLimits2 = getSlotTimeLimits($_dayTimeLimits(), $_viewDates()[i]);
      if (datesEqual($_viewDates()[i], $_today())) {
        let dayStart = addDuration(cloneDate($_viewDates()[i]), slotTimeLimits2.min);
        let dayEnd = addDuration(cloneDate($_viewDates()[i]), slotTimeLimits2.max);
        if ($_now() >= dayStart && $_now() <= dayEnd) {
          offset += ($_now() - dayStart) / 1e3;
          break;
        } else {
          return null;
        }
      } else {
        offset += slotTimeLimits2.max.seconds - slotTimeLimits2.min.seconds;
      }
    }
    let step = $slotDuration().seconds;
    return offset / step * $slotWidth() - $_bodyScrollLeft();
  });
  var fragment = $.comment();
  var node = $.first_child(fragment);
  {
    var consequent = ($$anchor2) => {
      var div = root_1();
      let styles;
      $.template_effect(() => {
        $.set_class(div, 1, $theme().nowIndicator);
        styles = $.set_style(div, "", styles, {
          top: `${$_headerHeight() + 2}px`,
          left: `${$.get(left) ?? ""}px`,
          height: `${$_bodyHeight() - 1}px`
        });
      });
      $.append($$anchor2, div);
    };
    $.if(node, ($$render) => {
      if ($.get(left) !== null && $.get(left) >= 3 && $.get(left) <= $_bodyWidth() - 3) $$render(consequent);
    });
  }
  $.append($$anchor, fragment);
  $.pop();
  $$cleanup();
}
var root = $.from_html(`<div><!> <div><!> <!> <!></div></div>`);
function View($$anchor, $$props) {
  $.push($$props, false);
  const [$$stores, $$cleanup] = $.setup_stores();
  const $theme = () => $.store_get(theme, "$theme", $$stores);
  const $nowIndicator = () => $.store_get(nowIndicator, "$nowIndicator", $$stores);
  let { nowIndicator, theme } = getContext("state");
  $.init();
  var div = root();
  var node = $.child(div);
  Sidebar(node, {});
  var div_1 = $.sibling(node, 2);
  var node_1 = $.child(div_1);
  Header(node_1, {});
  var node_2 = $.sibling(node_1, 2);
  Body(node_2, {});
  var node_3 = $.sibling(node_2, 2);
  {
    var consequent = ($$anchor2) => {
      NowIndicator($$anchor2, {});
    };
    $.if(node_3, ($$render) => {
      if ($nowIndicator()) $$render(consequent);
    });
  }
  $.reset(div_1);
  $.reset(div);
  $.template_effect(() => {
    $.set_class(div, 1, $theme().container);
    $.set_class(div_1, 1, $theme().main);
  });
  $.append($$anchor, div);
  $.pop();
  $$cleanup();
}
const index = {
  createOptions(options) {
    options.buttonText.resourceTimelineDay = "timeline";
    options.buttonText.resourceTimelineWeek = "timeline";
    options.buttonText.resourceTimelineMonth = "timeline";
    options.theme.expander = "ec-expander";
    options.theme.main = "ec-main";
    options.theme.times = "ec-times";
    options.theme.container = "ec-container";
    options.view = "resourceTimelineWeek";
    options.views.resourceTimelineDay = {
      buttonText: btnTextDay,
      component: View,
      displayEventEnd: false,
      dayHeaderFormat: { weekday: "long" },
      duration: { days: 1 },
      slotDuration: "01:00",
      theme: themeView("ec-timeline ec-resource-day-view"),
      titleFormat: { year: "numeric", month: "long", day: "numeric" }
    };
    options.views.resourceTimelineWeek = {
      buttonText: btnTextWeek,
      component: View,
      displayEventEnd: false,
      duration: { weeks: 1 },
      slotDuration: "01:00",
      theme: themeView("ec-timeline ec-resource-week-view")
    };
    options.views.resourceTimelineMonth = {
      buttonText: btnTextMonth,
      component: View,
      displayEventEnd: false,
      dayHeaderFormat: {
        weekday: "short",
        day: "numeric"
      },
      duration: { months: 1 },
      slotDuration: { days: 1 },
      theme: themeView("ec-timeline ec-resource-month-view"),
      titleFormat: { year: "numeric", month: "long" }
    };
  },
  createStores(state) {
    if (!("_viewResources" in state)) {
      state._viewResources = viewResources(state);
    }
    state._bodyHeight = writable(0);
    state._bodyWidth = writable(0);
    state._bodyScrollLeft = writable(0);
    state._headerEl = writable(void 0);
    state._headerHeight = writable(0);
    state._dayTimeLimits = dayTimeLimits(state);
    state._dayTimes = dayTimes(state);
    state._nestedResources = nestedResources(state);
    state._resHs = writable(/* @__PURE__ */ new Map());
    state._sidebarEl = writable(void 0);
  }
};
function createCalendar(target, plugins, options) {
  return mount(Calendar, {
    target,
    props: {
      plugins,
      options
    }
  });
}
function destroyCalendar(calendar) {
  return unmount(calendar);
}
export {
  index$4 as DayGrid,
  index$3 as Interaction,
  index$2 as List,
  index$1 as ResourceTimeGrid,
  index as ResourceTimeline,
  TimeGrid,
  createCalendar,
  destroyCalendar
};
